import { useCallback, useEffect, useMemo, useRef, useState, Suspense, lazy, Fragment } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { listenToOrderStatus } from '@/lib/orderStatusChannel';
import { ClubSettings, SurveyWithQuestions, EventLineup, Prize, MenuItem, ScanSource, CtaButton, CollectionPoint, OrderingMode, SectionOrderingMode, PaymentMode, Poll, tagStadium } from '@/lib/supabase-types';
import { getDeviceId } from '@/lib/deviceId';
import { isScreenJoinError } from '@/lib/screenJoin';
import { useScreenJoin } from '@/hooks/useScreenJoin';
import { lockCopyKeys, type LockReason } from '@/lib/lockScreenCopy';
import { AddonsSheet } from '@/components/fan/AddonsSheet';
import { itemInCategory } from '@/lib/menuCategoryMatch';
import { buildOrderLines, cartKeysForItem } from '@/lib/orderLines';
import { resolvePendingOrder, isTerminalOrder, shouldLandOnOrdering, readFinishedAt, writeFinishedAt, clearFinishedAt } from '@/lib/pendingOrderPointer';
import { buildStripeReturnUrl, parseStripeReturn, stripStripeReturnParams } from '@/lib/stripeReturn';
import { orderPillState } from '@/lib/orderPill';
import { OrderStatusPill } from '@/components/fan/OrderStatusPill';
import { canFanChoose, resolveEffectiveOrderingMode, buildOrderModeFields, menuTitleKey, isPickupOnly, pickupOnlyNames } from '@/lib/orderingMode';
import { mergeClubSettingsPatch, mergeOperatorSettingsPatch } from '@/lib/operatorSettingsOverlay';
import { onlineReadinessCall } from '@/lib/onlineReadiness';
import { hasRealLineupData } from '@/lib/lineups';
import { resolveBlockOrder, type BlockKey } from '@/lib/fanPageBlocks';
import { PaymentSheet } from '@/components/fan/PaymentSheet';
import { OrderReceivedBadge } from '@/components/fan/OrderReceivedBadge';
import { Loader2, Swords, Gift, MessageSquare, Check, ChevronRight, ChevronLeft, ShoppingBag, Plus, Minus, ShoppingCart, Lock, X, MapPin, Phone, Truck, RotateCcw, XCircle, ExternalLink, Activity, BarChart3, Globe, LifeBuoy, Clock } from 'lucide-react';
import { TeamLogo } from '@/components/TeamLogo';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { buildSafeFontFaceStyle } from '@/lib/sanitize';
import { FanPageSkeleton } from '@/components/fan/FanPageSkeleton';
import { MenuItemRow } from '@/components/fan/MenuItemRow';
import { applyMenuItemChange } from '@/lib/fanMenuRealtime';
import { OrderLineRow } from '@/components/fan/OrderLineRow';
import { buildFanPalette, legibleOn, inkOn } from '@/lib/fanTheme';
import { resolveSponsorLogos } from '@/lib/sponsorLogo';
import { SponsorLogo } from '@/components/fan/SponsorLogo';
import {
  DIETARY_TAG_LABELS,
  DIETARY_TAG_COLORS,
  ALLERGEN_LABELS,
  dietaryTagKey,
  allergenKey,
  knownDietaryTags,
  knownAllergens,
  resolveItemIngredients,
} from '@/lib/dietary';
import { resolveOrderReceivedNotice } from '@/lib/orderNotice';
import { resolveGiveawayPrize, normalizeGiveawayEmail } from '@/lib/giveaway';
import { ConfettiBurst } from '@/components/fan/ConfettiBurst';
import { setSentryFanContext } from '@/lib/sentry';
import { useTranslation } from '@/hooks/useTranslation';
import { useFanPageData } from '@/hooks/useFanPageData';
import { useRefetchOnResume } from '@/hooks/useRefetchOnResume';
import {
  useHalfTimePreorder,
  isStaleCancel,
  readConfirmedPaidAt,
  writeConfirmedPaidAt,
  clearConfirmedPaidAt,
} from '@/hooks/useHalfTimePreorder';
import {
  canOfferPreorder,
  isPreorderWindowClosed,
  preorderClosesAtMs,
  preorderHardCapAtMs,
  preorderStorageKey,
  DEFAULT_PREORDER_CUTOFF_MINUTE,
  PREORDER_SLOT,
  isPickupNowEnabled,
  type PickupSlot,
} from '@/lib/preorder';
import { shouldAutoOpenReadyPreorder, readReadyPreorderShown, markReadyPreorderShown } from '@/lib/preorderAutoOpen';
import { orderCodeLabel } from '@/lib/orderCode';
import { visibleCtaButtons } from '@/lib/ctaButtons';
import { isSurveyAnswerMissing, toggleSurveyOption, type SurveyAnswer } from '@/lib/surveyAnswers';
import { ctaIcon } from '@/lib/ctaIcons';
import { visibleHomeBlocks, resolveInitialView, orderingWindowOpen } from '@/lib/fanPageLanding';
import { openPollsFor } from '@/lib/polls';
import { usePollAutoOpen } from '@/hooks/usePollAutoOpen';
import { useIsMobile } from '@/hooks/use-mobile';
import {
  isWithinMatchWindow as checkIsWithinMatchWindow,
  isSurveyClosed as checkIsSurveyClosed,
  setPresenceCookie,
  hasPresenceCookie
} from '@/lib/presence';
import { usePostHog } from '@posthog/react';
import { fanEventProps } from '@/lib/posthog';
import { refusalReason, paymentReturnOutcome } from '@/lib/funnelEvents';
import { useOrderingPause } from '@/hooks/useOrderingPause';
import { readOrderingPaused } from '@/lib/orderingPause';

// Lazy load heavy interactive components
const PrizeWheel = lazy(() => import('@/components/fan/PrizeWheel').then(module => ({ default: module.PrizeWheel })));
const LiveScoreDisplay = lazy(() => import('@/components/fan/LiveScoreDisplay').then(module => ({ default: module.LiveScoreDisplay })));
const LanguageSettings = lazy(() => import('@/components/fan/LanguageSettings').then(module => ({ default: module.LanguageSettings })));
const PollSheet = lazy(() => import('@/components/fan/PollSheet').then(module => ({ default: module.PollSheet })));
const ReportSheet = lazy(() => import('@/components/fan/ReportSheet').then(module => ({ default: module.ReportSheet })));
const MatchSection = lazy(() => import('@/components/fan/MatchSection').then(module => ({ default: module.MatchSection })));
const ConcertHeader = lazy(() => import('@/components/fan/ConcertHeader').then(module => ({ default: module.ConcertHeader })));
// Stripe receipt handled via receipt_url
// Available Google Fonts for clubs
const fontFamilyMap: Record<string, string> = {
  'Inter': "'Inter', sans-serif",
  'Roboto': "'Roboto', sans-serif",
  'Open Sans': "'Open Sans', sans-serif",
  'Montserrat': "'Montserrat', sans-serif",
  'Oswald': "'Oswald', sans-serif",
  'Poppins': "'Poppins', sans-serif",
  'Lato': "'Lato', sans-serif",
  'Custom': 'CustomFont, sans-serif',
};

type ViewType = 'home' | 'predictor' | 'survey' | 'lineups' | 'ordering' | 'match';

// Survey gate: explicit state machine to guarantee we never show the form/intro
// until we have confirmed whether the survey is open or closed.
type SurveyGateState = 'unknown' | 'checking' | 'open' | 'closed';

// Pure utility functions — defined outside the component to avoid recreation on every render
const isSurveyClosedFromFinishedAt = (finishedAt: Date) => {
  const thirtyMinutesAfterFinish = finishedAt.getTime() + 30 * 60 * 1000;
  return Date.now() > thirtyMinutesAfterFinish;
};

const isValidEmail = (email: string) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return email.trim() !== '' && emailRegex.test(email.trim());
};

export default function TagPage() {
  // Support both old (/t/:tagPublicId) and new (/:stadiumSlug/:tagPublicId) URL formats
  const { tagPublicId, stadiumSlug } = useParams<{ tagPublicId: string; stadiumSlug?: string }>();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const posthog = usePostHog();
  const [showLanguageSettings, setShowLanguageSettings] = useState(false);
  const [showReportSheet, setShowReportSheet] = useState(false);
  const isMobile = useIsMobile();

  // Use the new cached data hook with parallel fetching
  const overriddenClubId = searchParams.get('club_id') || undefined;
  // Support both old (/t/:tagPublicId) and new (/:stadiumSlug/:tagPublicId) URL formats

  const fanPageData = useFanPageData(tagPublicId, stadiumSlug, overriddenClubId);
  const {
    tag,
    clubId: resolvedClubId,
    clubName,
    event,
    settings,
    ctaButtons,
    survey,
    polls,
    pollResults,
    lineups,
    prizes,
    isLoading,
    isError,
    error,
    stadiumSlugMismatch,
    menuItems: initialMenuItems,
    menuCategories: initialMenuCategories,
    orderingMode,
    collectionPoint,
    paymentMode,
    operator,
  } = fanPageData || {
    tag: null, clubId: null, clubName: null, event: null, settings: null,
    ctaButtons: [], survey: null, polls: [], pollResults: [], lineups: [], prizes: [],
    isLoading: true, isError: false, error: null,
    stadiumSlugMismatch: false, menuItems: [], menuCategories: [],
    orderingMode: 'delivery' as SectionOrderingMode, collectionPoint: null as CollectionPoint | null,
    paymentMode: 'in_person_only' as PaymentMode,
    operator: null,
  };

  const finalClubId = overriddenClubId || resolvedClubId;
  // Read by the menu realtime handler, whose closure outlives tenant changes.
  const finalClubIdRef = useRef<string | null>(finalClubId ?? null);
  finalClubIdRef.current = finalClubId ?? null;
  // The kiosk serving this seat, readable from long-lived realtime handlers
  // without resubscribing them when the payload refreshes.
  const operatorRef = useRef(operator);
  operatorRef.current = operator;

  // A seat flagged `is_test` is a PUBLIC DEMO seat: it bypasses the anti-fraud
  // presence check (fresh-scan + 5-scan limit) AND the match-window feature
  // lock, so a club can share the link and try predictor / survey / ordering
  // off-matchday to evaluate the app. Real seats keep full anti-fraud.
  const isDemoSeat = !!(tag?.seat as { is_test?: boolean } | null)?.is_test;
  // Screen tag: no seat. The venue hangs off the tag and the fan's identity is
  // the device; writes need a join claimed from the big-screen QR.
  const isScreenTag = tag?.kind === 'screen';
  const tagVenue = tagStadium(tag);
  const screenJoin = useScreenJoin(tag);

  // Concert / non-sport events suppress sports-only UI (live score, predictor,
  // lineups, match analytics). Legacy rows have event_kind null; treat null as
  // sports_match so nothing changes for them.
  const isSportsMatch = !event?.event_kind || event.event_kind === 'sports_match';

  // Live score / match status tracking for feature closing
  const [matchStatus, setMatchStatus] = useState<string>('not_started');
  // Live match minute — needed to keep the predictor closed through the second
  // half in 'half_time' mode (status flips back to 'live' after the interval).
  const [liveMinute, setLiveMinute] = useState<number | null>(null);
  const [matchFinishedAt, setMatchFinishedAt] = useState<Date | null>(null);

  // Force re-render trigger for survey deadline timer
  const [, forceUpdate] = useState(0);

  // Set when the DB trigger rejects a prediction as closed — shows the closed
  // screen even when the client's own clock/status view believed it was open.
  const [predictorServerClosed, setPredictorServerClosed] = useState(false);
  // A new event (next match day on the same seat) gets a fresh predictor.
  useEffect(() => {
    setPredictorServerClosed(false);
  }, [event?.id]);

  // Survey gate state - see SurveyGateState type above
  const [surveyGate, setSurveyGate] = useState<SurveyGateState>('unknown');


  // View navigation
  const [activeView, setActiveView] = useState<ViewType>('home');
  const [surveyStarted, setSurveyStarted] = useState(false);
  const [surveyStep, setSurveyStep] = useState(0);
  const [predictorStarted, setPredictorStarted] = useState(false);
  const [predictorInteractionId, setPredictorInteractionId] = useState<string | null>(null);
  const [surveyInteractionId, setSurveyInteractionId] = useState<string | null>(null);

  const checkSurveyClosedAuthoritatively = async () => {
    if (overriddenClubId) return { closed: false, finishedAt: null as Date | null };
    if (!event?.id) return { closed: false, finishedAt: null as Date | null };

    const { data, error } = await supabase
      .from('event_live_scores')
      .select('match_status, finished_at')
      .eq('event_id', event.id)
      .maybeSingle();

    if (error || !data) {
      // If we cannot determine, don't block access here (submit handler will still enforce).
      return { closed: false, finishedAt: null as Date | null };
    }

    if (data.match_status === 'finished' && data.finished_at) {
      const finishedAt = new Date(data.finished_at);
      return { closed: isSurveyClosedFromFinishedAt(finishedAt), finishedAt };
    }

    return { closed: false, finishedAt: null as Date | null };
  };

  // Form states
  const [homeScore, setHomeScore] = useState('');
  const [awayScore, setAwayScore] = useState('');
  const [predictorSubmitted, setPredictorSubmitted] = useState(false);
  const [predictorAlreadyCompleted, setPredictorAlreadyCompleted] = useState(false);
  const [predictorEmail, setPredictorEmail] = useState('');
  const [predictorMarketingConsent, setPredictorMarketingConsent] = useState(false);
  const [predictorTermsConsent, setPredictorTermsConsent] = useState(false);
  const [surveyResponses, setSurveyResponses] = useState<Record<string, SurveyAnswer>>({});
  const [surveySubmitted, setSurveySubmitted] = useState(false);
  const [surveyAlreadyCompleted, setSurveyAlreadyCompleted] = useState(false);
  // Fan tapped "I want it back" on the fan-page takeover screen.
  const [wantsItBack, setWantsItBack] = useState(false);
  const [sendingWantsItBack, setSendingWantsItBack] = useState(false);
  const [surveyEmail, setSurveyEmail] = useState('');
  const [surveyMarketingConsent, setSurveyMarketingConsent] = useState(false);
  const [surveyTermsConsent, setSurveyTermsConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Prize wheel states - SEPARATE for each flow
  const [showPredictorWheel, setShowPredictorWheel] = useState(false);
  const [showSurveyWheel, setShowSurveyWheel] = useState(false);
  const [predictorWonPrize, setPredictorWonPrize] = useState<Prize | null>(null);
  const [predictorPrizeRevealed, setPredictorPrizeRevealed] = useState(false);
  const [surveyWonPrize, setSurveyWonPrize] = useState<Prize | null>(null);
  const [surveyPrizeRevealed, setSurveyPrizeRevealed] = useState(false);

  // Ordering state
  const [cart, setCart] = useState<Map<string, { qty: number; addons: any[] }>>(new Map());
  const [fanComments, setFanComments] = useState('');
  // Optional giveaway entry (only offered when the club enabled a giveaway).
  const [giveawayEmail, setGiveawayEmail] = useState('');
  // Whether the order being placed carried a giveaway entry — drives the
  // confetti + "You're in!" banner on the received screens. Not restored on
  // reload (the celebration is a moment; giveaway_email is deliberately
  // absent from get_order_status).
  const [pendingGiveawayEntered, setPendingGiveawayEntered] = useState(false);
  const [itemToAddAddons, setItemToAddAddons] = useState<MenuItem | null>(null);
  // The picks the add-on sheet opens with: a cart line's, for its "+" button; empty from the menu.
  const [addonsPrefill, setAddonsPrefill] = useState<any[]>([]);
  // Dietary/allergen info sheet, opened from the ⓘ on a menu card.
  const [itemInfo, setItemInfo] = useState<MenuItem | null>(null);
  const [orderSubmitted, setOrderSubmitted] = useState(false);
  const [showCart, setShowCart] = useState(false);
  const [pendingOrderId, setPendingOrderId] = useState<string | null>(null);
  const [pendingOrderStatus, setPendingOrderStatus] = useState<string | null>(null);
  const [pendingPaymentStatus, setPendingPaymentStatus] = useState<string | null>(null);
  const [pendingOrderItems, setPendingOrderItems] = useState<Array<{
    menu_item_id?: string;
    item_name: string;
    item_size: string;
    item_price: number;
    quantity: number;
    selected_addons?: any[];
  }>>([]);
  const [pendingOrderTotal, setPendingOrderTotal] = useState(0);
  // Service fee shown on the confirmation screens = the placed order's total
  // minus its itemised lines (base × qty + add-ons). Surfacing it as its own
  // line makes the total reconcile with the items instead of looking wrong
  // (e.g. a €2 drink showing a €12 total). Mirrors the cart / receipt.
  const pendingServiceFee = useMemo(() => {
    const subtotal = pendingOrderItems.reduce((sum, item) => {
      const addons = (item.selected_addons || []).reduce((s: number, a: { price?: number }) => s + (a.price || 0), 0);
      return sum + item.item_price * item.quantity + addons;
    }, 0);
    return Math.max(0, pendingOrderTotal - subtotal);
  }, [pendingOrderItems, pendingOrderTotal]);
  const [checkingPendingOrder, setCheckingPendingOrder] = useState(false);
  // Pickup-mode state
  const [fanPhone, setFanPhone] = useState('');
  // "Both" sections: the fan's delivery-vs-pickup choice (delivery preselected
  // — one less tap for the common case). Only consulted when the section mode
  // is 'both'; resolveEffectiveOrderingMode collapses everything else.
  const [fanFulfillmentChoice, setFanFulfillmentChoice] = useState<OrderingMode>('delivery');
  // The mode/collection point of the PLACED order — set on submit and restored
  // from get_order_status on reload, because a 'both' section can't infer them
  // from config. Post-order screens key off these, never the section mode.
  const [pendingOrderingMode, setPendingOrderingMode] = useState<OrderingMode | null>(null);
  const [pendingCollectionPoint, setPendingCollectionPoint] = useState<CollectionPoint | null>(null);
  // The placed order's 3-character pick-up code (pick-up orders only; null
  // for delivery, or before the code migration). Same lifecycle as the two
  // above: set on submit, restored from get_order_status.
  const [pendingPickupCode, setPendingPickupCode] = useState<string | null>(null);
  // Payment state
  const [paymentClientSecret, setPaymentClientSecret] = useState<string | null>(null);
  const [paymentOrderId, setPaymentOrderId] = useState<string | null>(null);
  // The in-flight payment's pick-up code, moved to pendingPickupCode (now) or
  // the pre-order snapshot (half-time) once Stripe confirms.
  const [paymentPickupCode, setPaymentPickupCode] = useState<string | null>(null);
  const [paymentStripeAccount, setPaymentStripeAccount] = useState<string | null>(null);
  const [showPaymentSheet, setShowPaymentSheet] = useState(false);
  // Half-time pre-order: the fan's choice for the cart being built, and the
  // slot of the payment currently in flight — success/abandon must land on
  // the pre-order pointer, not the now-order one.
  const [pickupSlot, setPickupSlot] = useState<PickupSlot | null>(null);
  const [paymentPickupSlot, setPaymentPickupSlot] = useState<PickupSlot | null>(null);
  // The pre-order detail screen (Task 9 renders it) — declared here because
  // the payment-success path opens it.
  const [showPreorderDetail, setShowPreorderDetail] = useState(false);
  // Set when the fan returns from a redirect-based payment (MobilePay, iDEAL…)
  // that paid a pre-order: the reload lost paymentPickupSlot, so
  // handlePaymentSuccess never ran for it (see the effect after the
  // redirect-return effect).
  const preorderRedirectPendingRef = useRef(false);
  // The now-order Stripe confirmed client-side (confirmPayment → succeeded),
  // and when. A card declined once then retried in the same PaymentSheet
  // leaves the row cancelled/failed (onError's abandon-order, Stripe's
  // payment_failed) until the webhook's `succeeded` revives it; within
  // CANCEL_GRACE_MS of this stamp such a cancel is stale (isStaleCancel) and
  // must not tear down the order screen. Reset when another now-order starts.
  const nowOrderConfirmedPaidRef = useRef<{ id: string; confirmedPaidAt: number; logged?: boolean } | null>(null);
  // Receipt state
  const [receiptUrl, setReceiptUrl] = useState<string | null>(null);

  // Reactive live states for real-time updates
  const [liveSettings, setLiveSettings] = useState<ClubSettings | null>(null);
  const [liveCtaButtons, setLiveCtaButtons] = useState<CtaButton[]>([]);
  const [liveSurvey, setLiveSurvey] = useState<SurveyWithQuestions | null>(null);
  const [livePolls, setLivePolls] = useState<Poll[]>([]);
  const [livePrizes, setLivePrizes] = useState<Prize[]>([]);
  const [liveLineups, setLiveLineups] = useState<EventLineup[]>([]);
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  // The current list for closures that outlive renders (realtime merges, error recovery).
  const menuItemsRef = useRef<MenuItem[]>([]);
  menuItemsRef.current = menuItems;
  // Every menu item this page has ever seen, never pruned. A carted item that
  // is switched off mid-match leaves `menuItems`, but its display line after
  // payment still needs a name and price (see buildOrderLines).
  const knownMenuItemsRef = useRef<Map<string, MenuItem>>(new Map());
  for (const item of menuItems) knownMenuItemsRef.current.set(item.id, item);
  const [menuCategories, setMenuCategories] = useState<any[]>([]);

  // Online-payment readiness — null while loading, true/false once known.
  // If false, we silently hide the online button and (if club was set to
  // online_only) fall back to in_person so fans can still order.
  const [onlinePaymentReady, setOnlinePaymentReady] = useState<boolean | null>(null);

  // Presence Validation states (Option 1: Soft Risk)
  const [isFanInMatchWindow, setIsFanInMatchWindow] = useState<boolean>(true);
  const [isSeatLocked, setIsSeatLocked] = useState<boolean>(false);
  const [lockReason, setLockReason] = useState<LockReason>('limit');
  const [isFreshScan, setIsFreshScan] = useState(false);
  const [checkingPresence, setCheckingPresence] = useState<boolean>(true);

  useEffect(() => {
    setLiveSettings(settings);
    setLiveCtaButtons(ctaButtons || []);
    setLiveSurvey(survey || null);
    setLivePolls(polls || []);
    setLivePrizes(prizes || []);
    setLiveLineups(lineups || []);
    setMenuItems(initialMenuItems || []);
    setMenuCategories(initialMenuCategories || []);
  }, [settings, ctaButtons, survey, polls, prizes, lineups, initialMenuItems, initialMenuCategories]);


  // Use live versions for rendering
  const effectiveSettings = liveSettings || settings;
  // The serving kiosk has temporarily paused NEW orders (ordering block stays
  // visible). The settings copy carries the kiosk's switch (RPC overlay, then
  // the operator_settings realtime merge), but that push can miss a page
  // already open on stadium wifi (KTP vs MP, 25 Sep 2026: 23 taps on an
  // enabled Pay Now against a 409). useOrderingPause also re-reads the switch
  // whenever ordering is opened and locks on a refusal — from the KIOSK, not
  // the club's legacy flag (see readOrderingPaused).
  const settingsPaused = !!(effectiveSettings as any)?.ordering_paused;
  const servingOperatorId = operator?.id ?? null;
  const fetchOrderingPaused = useCallback(
    (): Promise<boolean | null> =>
      readOrderingPaused(supabase, { operatorId: servingOperatorId, clubId: finalClubId }),
    [servingOperatorId, finalClubId]
  );
  const applyFreshPause = useCallback((paused: boolean) => {
    setLiveSettings(prev => (prev ? ({ ...prev, ordering_paused: paused } as ClubSettings) : prev));
  }, []);
  const { paused: orderingPaused, markPausedByServer } = useOrderingPause({
    settingsPaused,
    orderingOpen: activeView === 'ordering',
    fetchPaused: fetchOrderingPaused,
    onFreshValue: applyFreshPause,
  });
  // Sponsor logos + size come from effectiveSettings (not raw settings) so
  // uploads, removals and slider changes propagate to open fan pages live.
  const sponsorLogos = resolveSponsorLogos(effectiveSettings);
  const sponsorLogoSize = (effectiveSettings as any)?.sponsor_logo_size as number | undefined;
  // What the NEXT order would be: section mode collapsed with the fan's
  // choice ('both' sections only). Drives the cart banners and submission.
  // A half-time pre-order is pickup-only, so a selected slot forces pickup.
  const effectiveOrderingMode = resolveEffectiveOrderingMode(orderingMode, pickupSlot ? 'pickup' : fanFulfillmentChoice);
  const effectiveIsPickup = effectiveOrderingMode === 'pickup';
  // Pick-up-only items (menu_items.seat_delivery_enabled = false). Cart keys
  // are `itemId` or `itemId:addonNames`. Any such item in the cart forces
  // pick-up in a 'both' section; in a section with no pick-up at all (mode
  // 'delivery', or legacy null/undefined — resolveEffectiveOrderingMode's
  // convention) the item cannot be ordered.
  const cartItemIds = Array.from(cart.keys(), (key) => key.split(':')[0]);
  const pickupOnlyInCart = pickupOnlyNames(cartItemIds, menuItems);
  const cartNeedsPickup = pickupOnlyInCart.length > 0;
  const sectionAllowsPickup = orderingMode === 'pickup' || orderingMode === 'both';
  // A 'both' (or pickup) section without a collection point can't fulfil a
  // pick-up either. One flag for the card state, addToCart, cart restore and
  // the 409 toast, so all four agree.
  const pickupImpossible = !sectionAllowsPickup || !collectionPoint;
  const effectiveCtaButtons = (liveCtaButtons.length > 0 ? liveCtaButtons : ctaButtons) || [];
  const effectiveSurvey = liveSurvey || survey;
  const effectivePolls = livePolls.length > 0 ? livePolls : (polls || []);
  // Open polls with a real ballot — each renders its own button + sheet.
  // Gated on the club's poll switch: when the club turns the feature off the
  // scheduler may still flip a poll to "open", but fans must never see it
  // (HJK vs Inter Turku, 9 Sep 2026). poll_enabled is not in the generated
  // ClubSettings type yet, hence the cast.
  const openPolls = openPollsFor(
    effectivePolls,
    (effectiveSettings as { poll_enabled?: boolean } | null)?.poll_enabled,
  );
  const effectiveLineups = (liveLineups.length > 0 ? liveLineups : lineups) || [];
  // No predictor prizes on a screen tag yet: draw_prediction_winner joins seats,
  // so a seatless fan could never win (Release C draws them).
  const predictorPrizes = isScreenTag ? [] : (livePrizes || []).filter(p => p.feature === 'predictor');
  const surveyPrizes = (livePrizes || []).filter(p => p.feature === 'survey');

  // Which poll sheet is open + the auto-open on a fresh scan. The sheet
  // only counts as seen once the fan closes it, so the reload the app does
  // on its first visit after a deploy no longer hides it (usePollAutoOpen).
  const { openPollId, openPoll, closePoll } = usePollAutoOpen({
    openPolls,
    isFreshScan,
    isDemoSeat,
    eventId: event?.id,
  });

  // Translation hook. `version` flows live via the club_settings realtime
  // merge above (effectiveSettings): when the club's translation ledger
  // changes, the DB bumps translations_updated_at, that reaches this page
  // as an ordinary club_settings UPDATE, and the hook refetches.
  // The serving kiosk's menu is translated under the kiosk; its rows merge
  // under the tenant's. Either ledger changing (the tenant's
  // translations_updated_at, the kiosk's menu_translations_updated_at — both
  // arrive through the live settings merges) refetches.
  const { t, locale, changeLanguage, languages } = useTranslation({
    clubId: finalClubId,
    operatorId: operator?.id ?? null,
    defaultLocale: settings?.default_language_code,
    version: `${(effectiveSettings as { translations_updated_at?: string | null } | null)?.translations_updated_at ?? ''}|${(effectiveSettings as { menu_translations_updated_at?: string | null } | null)?.menu_translations_updated_at ?? ''}`,
  });

  // The fan's half-time pre-order (if any), tracked beside the now-order.
  const preorder = useHalfTimePreorder({ tagId: tag?.id, menuItems, t });
  const rememberPreorder = preorder.remember; // stable (useCallback on tagId)

  // Optional club-authored line under the standard order-received copy, e.g.
  // KTP's "Keep an eye out for your order!". Derived once because both
  // order-received screens below render it, and flows live to open fan pages
  // via the club_settings realtime merge above.
  const orderReceivedNotice = useMemo(
    () => resolveOrderReceivedNotice((effectiveSettings as any)?.order_received_notice, t),
    [effectiveSettings, t],
  );

  // Real-time Event Selection Update
  useEffect(() => {
    const stadiumId = tagVenue?.id;
    if (!stadiumId) return;

    const channel = supabase
      .channel(`stadium-changes-${stadiumId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'stadiums',
          filter: `id=eq.${stadiumId}`,
        },
        async (payload) => {
          // If manual_event_id changed, refresh the whole data set
          const newManualEventId = (payload.new as any).manual_event_id;
          const oldEventId = event?.id;

          if (newManualEventId !== undefined && newManualEventId !== oldEventId) {
            console.log('Event selection changed via realtime:', { from: oldEventId, to: newManualEventId });

            // Give a small delay to ensure DB propagation and then refresh
            setTimeout(() => {
              queryClient.invalidateQueries({ queryKey: ['fan-page-data'] });
              toast.info(t('event.updated', { defaultValue: 'Match data updated' }));
            }, 500);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [tagVenue?.id, event?.id, queryClient, t]);


  // useEffect(() => {
  //   if (effectiveSettings?.default_language_code) {
  //     console.log('TagPage Locale Trace:', {
  //       clubId: finalClubId,
  //       defaultFromSettings: effectiveSettings.default_language_code,
  //       currentLocale: locale,
  //       localStorage: localStorage.getItem('fan_language'),
  //       isManual: localStorage.getItem('fan_language_is_manual')
  //     });
  //   }
  // }, [locale, effectiveSettings?.default_language_code, finalClubId]);

  // Helper to get survey title with proper fallback chain:
  // 1. Club-specific override for this survey (survey.title.{id})
  // 2. Generic survey title translation (survey.title) 
  // 3. Raw database title
  const getSurveyTitle = (surveyData: SurveyWithQuestions | null) => {
    if (!surveyData) return t('survey.title');
    const specificKey = `survey.title.${surveyData.id}`;
    const specificTranslation = t(specificKey);
    if (specificTranslation !== specificKey) {
      return specificTranslation;
    }
    // Fall back to generic translation or raw title
    const genericTranslation = t('survey.title');
    if (genericTranslation !== 'survey.title') {
      return genericTranslation;
    }
    return surveyData.title;
  };

  const getLocalizedMenuItemName = (item: { menu_item_id?: string; item_name: string; item_size: string }) => {
    if (item.menu_item_id) {
      return t(`menu.item.name.${item.menu_item_id}`, { defaultValue: item.item_name });
    }

    // Fallback for older orders where menu_item_id may not be returned
    const fallbackMenuItem = menuItems.find(mi => mi.name === item.item_name || mi.size === item.item_size);
    if (fallbackMenuItem) {
      return t(`menu.item.name.${fallbackMenuItem.id}`, { defaultValue: fallbackMenuItem.name });
    }

    return item.item_name;
  };

  const getLocalizedMenuItemSize = (item: { menu_item_id?: string; item_name: string; item_size: string }) => {
    if (item.menu_item_id && item.item_size) {
      return t(`menu.item.size.${item.menu_item_id}`, { defaultValue: item.item_size });
    }

    const fallbackMenuItem = menuItems.find(mi => mi.name === item.item_name || mi.size === item.item_size);
    if (fallbackMenuItem) {
      return t(`menu.item.size.${fallbackMenuItem.id}`, { defaultValue: fallbackMenuItem.size || item.item_size });
    }

    return item.item_size;
  };

  // Translated, price-annotated add-on list for an ordered line. Shared by the
  // three order-status screens so they can't drift apart again (the
  // awaiting-payment copy had silently lost its add-ons line).
  const formatAddonsLabel = (addons: any[] | null | undefined): string | null => {
    if (!addons || addons.length === 0) return null;
    return addons
      .map((a: any) => {
        const name = t(`menu.addon.${a.name}`, { defaultValue: a.name });
        return a.price > 0 ? `${name} (+€${a.price.toFixed(2)})` : name;
      })
      .join(', ');
  };

  const getPrizeLabel = (prize: Prize) => {
    return t(`prize.${prize.id}`, { defaultValue: prize.name });
  };

  const getPrizeResultText = (prize: Prize) => {
    const prizeTranslation = t(`prize.${prize.id}`);
    const finalPrizeName = prizeTranslation !== `prize.${prize.id}` ? prizeTranslation : prize.name;

    // If prize key is configured as full sentence (e.g. contains {{prizeName}}), 
    // replacing it is still supported for flexibility, but using original name.
    if (prizeTranslation !== `prize.${prize.id}` && prizeTranslation.includes('{{prizeName}}')) {
      return prizeTranslation.replace('{{prizeName}}', prize.name);
    }

    const baseMessage = t('predictor.chanceToWin', { prizeName: finalPrizeName });

    // If base message is just the key, just show prize name
    if (baseMessage === 'predictor.chanceToWin') {
      return finalPrizeName;
    }

    // Safety: If for some reason the translated baseMessage doesn't include the prize name
    // (e.g. user removed the placeholder in the DB), append it.
    if (!baseMessage.includes(finalPrizeName)) {
      return `${baseMessage.trim()} ${finalPrizeName}`;
    }

    return baseMessage;
  };

  // Check for pending order on mount / when entering ordering view
  // `silent` = background re-check (poll / foreground resume): update the
  // state but never flash the "checking" loader over the order screen.
  const checkPendingOrder = async ({ silent = false }: { silent?: boolean } = {}) => {
    if (!tag) return;
    const storageKey = `idseat_pending_order_${tag.id}`;
    const savedOrderId = localStorage.getItem(storageKey);
    if (!savedOrderId) {
      setPendingOrderId(null);
      setPendingOrderStatus(null);
      return;
    }
    if (!silent) setCheckingPendingOrder(true);
    // The pointer is the fan's only way back to their order after a reload.
    // It goes only for a definitive reason (see resolvePendingOrder).
    const forgetPointer = () => {
      localStorage.removeItem(storageKey);
      clearConfirmedPaidAt(storageKey);
      clearFinishedAt(storageKey);
    };
    const loadItems = async () => {
      const { data: itemsData } = await supabase.rpc('get_order_items', { _order_id: savedOrderId });
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const normalizedItems = (itemsData || []).map((it: any) => {
        const resolvedId = it.menu_item_id || menuItems.find(mi => mi.name === it.item_name)?.id;
        return { ...it, menu_item_id: resolvedId, item_vat_rate: Number(it.item_vat_rate) || 0 };
      });
      setPendingOrderItems(normalizedItems);
    };
    try {
      const { data: statusData, error: statusError } = await supabase.rpc('get_order_status', { _order_id: savedOrderId });
      if (statusError) {
        // A transient RPC failure (network, PostgREST schema reload) is not
        // "order not found": keep the pointer and the current state; the next
        // check reconciles it.
        console.warn('[checkPendingOrder] get_order_status failed:', statusError);
        return;
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const order = statusData?.[0] as any;
      // After a reload the in-memory stamp is gone: restore it from the one
      // handlePaymentSuccess persisted next to the pointer (id-matched).
      if (nowOrderConfirmedPaidRef.current?.id !== savedOrderId) {
        const paidAt = readConfirmedPaidAt(storageKey, savedOrderId);
        nowOrderConfirmedPaidRef.current = paidAt != null ? { id: savedOrderId, confirmedPaidAt: paidAt } : null;
      }
      // Restore the PLACED order's mode + collection point — on a 'both'
      // section they can't be inferred from config. get_order_status returns
      // them since 20260902120000; against an older DB they're undefined and
      // the section-mode fallback in the screens applies.
      if (order) {
        setPendingOrderingMode(
          order.ordering_mode === 'pickup' || order.ordering_mode === 'delivery'
            ? order.ordering_mode
            : null,
        );
        setPendingCollectionPoint(order.collection_point ?? null);
        setPendingPickupCode(order.pickup_code ?? null);
      } else {
        setPendingOrderingMode(null);
        setPendingCollectionPoint(null);
        setPendingPickupCode(null);
      }

      const staleCancel = !!order && isStaleCancel(order.id, order, nowOrderConfirmedPaidRef.current);
      const finishedAt = order ? readFinishedAt(storageKey, order.id) : null;
      const action = resolvePendingOrder({ order: order ?? null, now: Date.now(), finishedAt, staleCancel });

      if (action === 'remove') {
        // Not found, or a finished order past its grace window.
        forgetPointer();
        setPendingOrderId(null);
        setPendingOrderStatus(null);
        setPendingPaymentStatus(null);
        setPendingOrderItems([]);
        setPendingOrderTotal(0);
        return;
      }
      if (action === 'abandon') {
        // Old pending_payment — fan abandoned ages ago. Clean up server-
        // side so it doesn't pollute the club's admin totals.
        forgetPointer();
        setPendingOrderId(null);
        setPendingOrderStatus(null);
        setPendingPaymentStatus(null);
        setPendingOrderItems([]);
        setPendingOrderTotal(0);
        supabase.functions.invoke('abandon-order', {
          body: { order_id: savedOrderId, tag_id: tag.id },
        }).catch((e) => console.warn('[abandon-order] from checkPendingOrder failed:', e));
        return;
      }

      if (staleCancel) {
        // Stale abandon of an order the client just saw paid: keep the
        // optimistic pending/paid state AND the pointer — the order stays
        // "moving", so realtime + the resume poll pick up the webhook's
        // revive. A real cancel is accepted once the grace window is over.
        if (nowOrderConfirmedPaidRef.current && !nowOrderConfirmedPaidRef.current.logged) {
          nowOrderConfirmedPaidRef.current.logged = true;
          console.info('[checkPendingOrder] ignoring a stale cancel right after a confirmed payment:', order.id);
        }
        if (pendingOrderId !== order.id) {
          // Reload during the webhook lag: nothing held for this order yet.
          // Show it as the live pending order handlePaymentSuccess would have
          // shown (mode / collection point / pick-up code are set above).
          setPendingOrderId(order.id);
          setPendingOrderStatus('pending');
          setPendingPaymentStatus('paid');
          setPendingOrderTotal(Number(order.total_amount));
          await loadItems();
        }
        return;
      }

      // A finished order stays on screen — and survives a reload — for the
      // grace window, or until the fan taps "Order again". Stamp when first seen.
      if (isTerminalOrder(order) && finishedAt == null) writeFinishedAt(storageKey, order.id);
      // pending_payment: still waiting for the Stripe webhook. For redirect-
      // based methods (MobilePay, Klarna, iDEAL, Bancontact) it can lag 1-3s
      // behind the fan returning; realtime advances the UI from "Processing
      // payment…" to "Order Received".
      setPendingOrderId(order.id);
      setPendingOrderStatus(order.status);
      setPendingPaymentStatus(order.payment_status || null);
      setPendingOrderTotal(Number(order.total_amount));
      await loadItems();
    } catch (err) {
      // A transient network/RPC error must not drop the fan's only pointer to
      // their order: this runs on every focus/visibility/online event and every
      // 30s, and 'online' fires exactly when the stadium network is flaky.
      // Keep the pointer + whatever state is already held; the next successful
      // check reconciles it.
      console.error('Error checking pending order:', err);
    } finally {
      if (!silent) setCheckingPendingOrder(false);
    }
  };

  useEffect(() => {
    if (!tag) return;
    // Always in the ordering view; elsewhere only when a pointer exists, so
    // the status pill on the home view knows about the order after a reload.
    if (activeView === 'ordering') checkPendingOrder();
    else if (localStorage.getItem(`idseat_pending_order_${tag.id}`)) checkPendingOrder({ silent: true });
  }, [tag?.id, activeView]);

  // Self-healing for the order screen. The realtime UPDATE that flips the
  // order to delivered is a single event; a phone whose socket iOS closed in
  // the background never gets it and sits on "order received" for the rest of
  // the match (HJK vs Inter Turku, 9 Sep 2026). Re-read the status when the
  // page comes back, when the network returns, and every 30s while the order
  // is still moving.
  const orderStillMoving =
    !!pendingOrderId &&
    pendingOrderStatus !== 'delivered' &&
    pendingOrderStatus !== 'collected' &&
    pendingOrderStatus !== 'cancelled';
  useRefetchOnResume(() => checkPendingOrder({ silent: true }), {
    enabled: orderStillMoving,
    intervalMs: 30_000,
  });

  // Cart persistence — survives the browser navigating away to MobilePay /
  // Klarna / iDEAL / Bancontact and back. Without this, the fan's selection
  // disappears the moment they return from the bank's redirect flow.
  //
  // Two refs solve the chicken-and-egg with the save effect firing in the
  // same commit as restore (and seeing the still-empty default Map in its
  // closure):
  //   - cartRestoredForTagRef: have we read localStorage yet for this tag?
  //   - lastSavedCartSizeRef:  tracks the last cart size we actually
  //                            persisted. The save effect only wipes
  //                            localStorage when this ref says "we had a
  //                            non-empty cart that just became empty",
  //                            never when the cart is empty because React
  //                            hasn't committed the restore yet.
  const cartRestoredForTagRef = useRef<string | null>(null);
  const lastSavedCartSizeRef = useRef(0);

  useEffect(() => {
    if (!tag?.id) return;
    if (cartRestoredForTagRef.current === tag.id) return;
    try {
      const raw = localStorage.getItem(`idseat_cart_${tag.id}`);
      if (raw) {
        const parsed = JSON.parse(raw) as Array<[string, { qty: number; addons: any[] }]>;
        // A section without pick-up can't take pick-up-only items: drop them
        // (the menu no longer offers them here either). initialMenuItems, not
        // the menuItems state — it arrives with tag/orderingMode in one RPC,
        // while the state copy lags a render behind.
        const entries = Array.isArray(parsed) && pickupImpossible
          ? parsed.filter(([key]) => !(initialMenuItems ?? []).some((i) => i.id === key.split(':')[0] && isPickupOnly(i)))
          : parsed;
        if (Array.isArray(entries) && entries.length > 0) {
          setCart(new Map(entries));
          // Pretend the saved cart was just "saved" by us so the next save
          // run knows there's already legit data on disk for this tag.
          lastSavedCartSizeRef.current = entries.length;
        } else if (Array.isArray(parsed) && parsed.length > 0) {
          // Everything saved was dropped above — don't leave it on disk.
          localStorage.removeItem(`idseat_cart_${tag.id}`);
        }
      }
    } catch (e) {
      console.warn('[cart-persist] restore failed:', e);
    } finally {
      cartRestoredForTagRef.current = tag.id;
    }
  }, [tag?.id, pickupImpossible, initialMenuItems]);

  useEffect(() => {
    if (!tag?.id || cartRestoredForTagRef.current !== tag.id) return;
    const key = `idseat_cart_${tag.id}`;
    try {
      if (cart.size > 0) {
        localStorage.setItem(key, JSON.stringify(Array.from(cart.entries())));
        lastSavedCartSizeRef.current = cart.size;
      } else if (lastSavedCartSizeRef.current > 0) {
        // Cart went from non-empty to empty (fan emptied it, OR order
        // completed and our other effect cleared it). Safe to wipe.
        localStorage.removeItem(key);
        lastSavedCartSizeRef.current = 0;
      }
      // else: cart is empty AND we've never saved anything in this session.
      // That's the same-commit-as-restore case where the closure still has
      // the default empty Map; leaving localStorage alone protects the data
      // we just read from it. The next render fires this effect again with
      // the populated cart and the upper branch persists it.
    } catch (e) {
      console.warn('[cart-persist] save failed:', e);
    }
  }, [cart, tag?.id]);

  // If the fan returns to the tag URL with leftover state from an earlier
  // session — a pending order in flight, or items still sitting in the saved
  // cart — drop them straight into the ordering view. Otherwise they'd land
  // on the home menu with no visible hint that their cart / pending payment
  // is still there, and they'd think they need to start over.
  //
  // Covers the scenario where Stripe redirected them to iDEAL/MobilePay,
  // they hit the browser back button (so no redirect_status param fires the
  // existing redirect handler), and end up on the original TagPage URL.
  const autoSwitchToOrderingRef = useRef<string | null>(null);
  useEffect(() => {
    if (!tag?.id) return;
    if (autoSwitchToOrderingRef.current === tag.id) return;
    autoSwitchToOrderingRef.current = tag.id;
    const pointerKey = `idseat_pending_order_${tag.id}`;
    const pointerOrderId = localStorage.getItem(pointerKey);
    const hasSavedCart = !!localStorage.getItem(`idseat_cart_${tag.id}`);
    // A finished order's pointer is kept for a while (see pendingOrderPointer);
    // it must not drag every re-tap onto the "Order delivered" screen.
    const finishedAt = pointerOrderId ? readFinishedAt(pointerKey, pointerOrderId) : null;
    if (shouldLandOnOrdering({ pointerOrderId, finishedAt, hasSavedCart })) {
      setActiveView('ordering');
    }
  }, [tag?.id]);

  // Ordering-only landing: when the home list would hold nothing but the
  // ordering button AND ordering is actually usable, open the menu directly.
  // Additive and one-shot per tag (same shape as autoSwitchToOrderingRef
  // above): every early return leaves the page exactly as it is today, the
  // shot is consumed the first time this tag's data is in, and it only ever
  // moves 'home' → 'ordering' — realtime settings merges, resume refetches and
  // the fan's own navigation can never re-land them. The back-to-home chip in
  // the shared header is on every ordering screen, so a landed fan is always
  // one tap from the home list.
  const orderingOnlyLandingRef = useRef<string | null>(null);
  useEffect(() => {
    if (!tag?.id || isLoading || checkingPresence) return;
    if (orderingOnlyLandingRef.current === tag.id) return;
    orderingOnlyLandingRef.current = tag.id;
    if (activeView !== 'home') return; // pending-order / Stripe-return landing already won
    const blocks = visibleHomeBlocks({
      isSportsMatch,
      hasSportsmonksFixture: !!event?.id && !!event?.sportsmonks_fixture_id,
      visibleCtaCount: visibleCtaButtons(effectiveCtaButtons, effectiveSettings?.cta_enabled).length,
      openPollCount: openPolls.length,
      surveyQuestionCount: effectiveSurvey?.survey_questions?.length ?? 0,
      settings: effectiveSettings,
    });
    const view = resolveInitialView(blocks, {
      paused: orderingPaused,
      // Computed here, not read from isFanInMatchWindow: that state is still
      // its default (true) in the commit where the data arrives.
      inMatchWindow: orderingWindowOpen({
        kickoffIso: event?.kickoff_datetime,
        bypass: !!overriddenClubId || isDemoSeat,
      }),
      // Hook value on purpose — menuItems state is seeded one commit later.
      menuItemCount: (initialMenuItems ?? []).length,
    });
    if (view !== 'ordering') return;
    posthog.capture('fan_ordering_auto_landed', fanEventProps({ club_id: finalClubId }, isDemoSeat)); // additive; fan_view_changed untouched
    setActiveView('ordering'); // NOT the home-button handler: no setCart(new Map()), the restored cart survives
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tag?.id, isLoading, checkingPresence]);

  // Clear the cart once the order has actually been placed (status moves
  // past pending_payment to pending / ready / delivered / collected). Keeps
  // cancelled separate — fan may want to retry with the same items.
  useEffect(() => {
    if (!pendingOrderStatus) return;
    if (pendingOrderStatus === 'pending_payment') return;
    if (pendingOrderStatus === 'cancelled') return;
    if (cart.size > 0) setCart(new Map());
    // The giveaway entry belongs to the order that was just placed.
    setGiveawayEmail('');
  }, [pendingOrderStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  // Detect return from a redirect-based payment method (MobilePay, iDEAL, Bancontact, etc.)
  // Stripe appends payment_intent + redirect_status to the return_url. We drop
  // the fan into the ordering view so checkPendingOrder picks up the saved
  // orderId from localStorage and renders the "Order received / being prepared"
  // banner instead of the home landing.
  useEffect(() => {
    const { redirectStatus, orderId: returnedOrderId, preorder: returnedPreorder } = parseStripeReturn(window.location.search);
    if (!redirectStatus) return;
    // Cold load: the tag arrives after this effect's first run (async query
    // persister), and the params must survive until then — deps re-run it.
    if (!tag?.id) return;
    posthog.capture('fan_payment_returned', fanEventProps({ outcome: paymentReturnOutcome(redirectStatus), preorder: returnedPreorder, event_id: event?.id ?? null, club_id: finalClubId }, isDemoSeat));
    // The return URL carries the order id (buildStripeReturnUrl). When the
    // bank app hands the fan back to a different browser, localStorage is
    // empty and this is the only way to find the order again.
    if (returnedOrderId) {
      const key = returnedPreorder ? preorderStorageKey(tag.id) : `idseat_pending_order_${tag.id}`;
      if (localStorage.getItem(key) !== returnedOrderId) localStorage.setItem(key, returnedOrderId);
    }
    setActiveView('ordering');
    // Which order did this redirect pay? Only when a pre-order pointer is
    // present and no now-order pointer is can it only have been the
    // pre-order; with both present keep today's behaviour (the now-order
    // effect clears the cart). Shared by `succeeded` and `processing` — an
    // async method (Pay by Bank) returns `processing`, and the pre-order-return
    // effect below then waits for the paid status the webhook lands.
    const redirectPaidPreorder = () => {
      const tagId = tag?.id;
      return !!tagId
        && !!localStorage.getItem(preorderStorageKey(tagId))
        && !localStorage.getItem(`idseat_pending_order_${tagId}`);
    };
    if (redirectStatus === 'succeeded') {
      const paidPreorder = redirectPaidPreorder();
      // handlePaymentSuccess (the in-sheet success path) never runs after a
      // redirect reload, so the order-placed event is sent here. The cart may
      // be gone after the reload: no total / item_count.
      posthog.capture('fan_order_submitted', fanEventProps({ payment_mode: 'online', via: 'redirect', pickup_slot: paidPreorder ? 'half_time' : null, event_id: event?.id ?? null, club_id: finalClubId }, isDemoSeat));
      if (paidPreorder) {
        preorderRedirectPendingRef.current = true;
        toast.success(t('preorder.confirmedTitle'));
      } else {
        toast.success(t('payment.success', { defaultValue: 'Payment successful! Your order is on its way.' }));
      }
    } else if (redirectStatus === 'failed') {
      // Stripe told us the payment definitely failed. Abandon the order
      // server-side and clear local pointers so the fan goes back to the
      // ordering view with their cart intact (cart is persisted to
      // localStorage separately and restores on mount). Without this, the
      // 30-min age check below in checkPendingOrder would keep the order
      // alive in "Processing…" state for half an hour.
      const tagId = tag?.id;
      if (tagId) {
        const storageKey = `idseat_pending_order_${tagId}`;
        const orderId = localStorage.getItem(storageKey);
        if (orderId) {
          supabase.functions.invoke('abandon-order', {
            body: { order_id: orderId, tag_id: tagId },
          }).catch((e) => console.warn('[abandon-order] on redirect failed:', e));
          localStorage.removeItem(storageKey);
        }
        // The failed redirect may have been a half-time pre-order's payment.
        // abandon-order only touches pending_payment orders, so an already
        // scheduled (paid) pre-order under this pointer is safe. The key is
        // deliberately NOT removed: useHalfTimePreorder clears it once the
        // order comes back cancelled, which also resets its tracked state.
        const preorderId = localStorage.getItem(preorderStorageKey(tagId));
        if (preorderId) {
          supabase.functions.invoke('abandon-order', {
            body: { order_id: preorderId, tag_id: tagId },
          }).catch((e) => console.warn('[abandon-order] pre-order on redirect failed:', e));
        }
      }
      toast.error(t('payment.failed', { defaultValue: 'Payment failed. Please try another method.' }));
    } else if (redirectStatus === 'processing') {
      if (redirectPaidPreorder()) preorderRedirectPendingRef.current = true;
      toast.info(t('payment.processing', { defaultValue: 'Payment is processing. We will update your order shortly.' }));
    }
    window.history.replaceState({}, '', stripStripeReturnParams(window.location.href));
  }, [tag?.id]);

  // Redirect-paid pre-order: the reload lost the in-memory slot, so
  // handlePaymentSuccess never ran and the cart restored from
  // idseat_cart_<tagId>. Once useHalfTimePreorder reports the order as paid
  // (it may first see pending_payment while the webhook lands — wait), do
  // what the in-page success path does: clear the cart (the persist effect
  // then wipes the stored copy) and the giveaway entry, and open the detail.
  useEffect(() => {
    if (!preorderRedirectPendingRef.current) return;
    const status = preorder.preorder?.status;
    if (status !== 'scheduled' && status !== 'pending' && status !== 'ready_at_collection_point') return;
    preorderRedirectPendingRef.current = false;
    if (cart.size > 0) setCart(new Map());
    setGiveawayEmail('');
    setShowPreorderDetail(true);
  }, [preorder.preorder?.id, preorder.preorder?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pre-order ready at the collection point: bring the fan to its detail
  // screen, from wherever they are — once per order (remembered across
  // reloads), and never over a now-order they are following. See
  // shouldAutoOpenReadyPreorder for why.
  const trackedPreorderId = preorder.preorder?.id ?? null;
  const trackedPreorderStatus = preorder.preorder?.status ?? null;
  // Never while the fan is in the cart or paying for a now-order: the detail
  // branch would replace that screen and unmount the PaymentSheet (typed card
  // details lost). The id is only recorded once it actually opens, so it
  // opens as soon as they leave the cart/payment (the strip is green meanwhile).
  const checkoutInProgress = showCart || showPaymentSheet || !!paymentClientSecret;
  // Right after a reload (e.g. back from MobilePay) the now-order's status is
  // still loading: its pointer alone says one is in flight (it is removed
  // once the order is delivered, collected or cancelled).
  const nowOrderInProgress = pendingOrderId
    ? orderStillMoving
    : !!tag?.id && !!localStorage.getItem(`idseat_pending_order_${tag.id}`);
  useEffect(() => {
    if (!tag?.id) return;
    if (!shouldAutoOpenReadyPreorder({
      preorderId: trackedPreorderId,
      preorderStatus: trackedPreorderStatus,
      shownPreorderId: readReadyPreorderShown(tag.id),
      checkoutInProgress,
      nowOrderInProgress,
    })) return;
    markReadyPreorderShown(tag.id, trackedPreorderId!);
    setActiveView('ordering');
    setShowPreorderDetail(true);
  }, [tag?.id, trackedPreorderId, trackedPreorderStatus, checkoutInProgress, nowOrderInProgress]);

  // The hook can drop the pre-order on its own (row not found, 30-minute
  // abandon → clear()); close the detail with it so the flag can't go stale
  // and hide the strip / hijack the next menu visit.
  useEffect(() => {
    if (!preorder.preorder && showPreorderDetail) setShowPreorderDetail(false);
  }, [preorder.preorder, showPreorderDetail]);

  // Check whether this club can accept online payments (active Stripe + complete business info).
  // The server enforces this in create-payment-intent; this query lets us hide the button up-front
  // so the fan never sees an option that would fail.
  // Readiness is the SELLER's: the kiosk's organization when a kiosk serves
  // the seat, the club's own only when none does (see onlineReadinessCall).
  useEffect(() => {
    const call = onlineReadinessCall(operator?.id ?? null, finalClubId ?? null);
    if (!call) {
      setOnlinePaymentReady(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { data, error } = await supabase.rpc(call.fn as any, call.args as any);
        if (cancelled) return;
        if (error) {
          console.warn('[online-readiness] RPC error:', error);
          // Fail-safe: assume not ready, fans can still order in-person.
          setOnlinePaymentReady(false);
          return;
        }
        const row = (data as any[])?.[0];
        setOnlinePaymentReady(!!row?.online_ready);
      } catch (err) {
        console.warn('[online-readiness] errored:', err);
        setOnlinePaymentReady(false);
      }
    })();
    return () => { cancelled = true; };
  }, [finalClubId, operator?.id]);

  // Subscribe to real-time updates for the pending order
  useEffect(() => {
    if (!pendingOrderId) return;

    // The order's own broadcast topic (order:<id>): fans cannot read the
    // orders table, the database pushes each status change there.
    const channel = listenToOrderStatus(supabase, pendingOrderId,
        (updatedOrder) => {
          // Declined-then-retried abandon arriving after the client saw the
          // payment succeed: ignore it, the revive UPDATE follows.
          if (isStaleCancel(pendingOrderId, updatedOrder, nowOrderConfirmedPaidRef.current)) return;
          setPendingOrderStatus(updatedOrder.status);
          if (updatedOrder.payment_status) {
            setPendingPaymentStatus(updatedOrder.payment_status);
          }

          // The pointer stays: the final screen must survive a reload. The
          // grace window (TERMINAL_GRACE_MS) or "Order again" clears it.
          const markFinished = () => {
            if (!tag?.id) return;
            writeFinishedAt(`idseat_pending_order_${tag.id}`, pendingOrderId);
          };

          if (updatedOrder.payment_status === 'refunded') {
            markFinished();
            toast.info(t('payment.orderRefunded', { defaultValue: 'Your order has been refunded.' }));
          } else if (updatedOrder.status === 'cancelled') {
            markFinished();
            toast.error(t('payment.orderCancelled', { defaultValue: 'Your order has been cancelled.' }));
          } else if (updatedOrder.status === 'delivered') {
            markFinished();
            toast.success(t('ordering.orderDelivered'));
          } else if (updatedOrder.status === 'ready_at_collection_point') {
            toast.success(t('ordering.orderReadyAtCollection', { defaultValue: '📍 Your order is ready! Head to the collection point.' }));
          } else if (updatedOrder.status === 'collected') {
            markFinished();
            toast.success(t('ordering.orderCollected', { defaultValue: 'Order collected! Enjoy! 🎉' }));
          }
        },
      )
      .subscribe((status) => {
        // A (re)subscribe after a dropped socket replays nothing that fired in
        // the gap, so re-read the authoritative status.
        if (status === 'SUBSCRIBED') checkPendingOrder({ silent: true });
      });

    return () => {
      supabase.removeChannel(channel);
    };
  }, [pendingOrderId, tag?.id]);

  // Fetch (and poll for) the Stripe receipt URL whenever we have a pending order.
  // Covers both fresh card payments and orders recovered after a redirect-based
  // method (iDEAL, Bancontact, MobilePay, etc.) where the webhook may still be
  // catching up when the fan lands back on the page.
  useEffect(() => {
    if (!pendingOrderId) {
      setReceiptUrl(null);
      return;
    }
    if (receiptUrl) return;
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      if (cancelled) return;
      attempts++;
      try {
        const { data, error } = await supabase.rpc('get_receipt_url' as any, { _order_id: pendingOrderId });
        if (error) console.error('[receipt] RPC error:', error);
        if (data && !cancelled) {
          setReceiptUrl(data as string);
          return;
        }
      } catch (err) {
        console.error('[receipt] Failed to fetch receipt URL:', err);
      }
      if (attempts < 10 && !cancelled) {
        setTimeout(poll, 1500);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [pendingOrderId, receiptUrl]);

  // Combined Features Subscription (Settings, CTAs, Surveys, Prizes, Lineups)
  useEffect(() => {
    if (!finalClubId) return;

    const channel = supabase
      .channel(`feature_updates_${finalClubId}`)
      // Club Settings (Instant feature toggles)
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'club_settings', filter: `club_id=eq.${finalClubId}` },
        (payload) => {
          // Merge with existing settings instead of replacing — real-time payloads
          // can omit unchanged columns, which would wipe other feature flags.
          // With a kiosk serving the seat, its switches stand and ordering
          // visibility is recomputed (see operatorSettingsOverlay).
          setLiveSettings(prev =>
            mergeClubSettingsPatch<ClubSettings>(prev, payload.new as Partial<ClubSettings>, operatorRef.current)
          );
        }
      )
      // CTA Buttons
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'cta_buttons', filter: `club_id=eq.${finalClubId}` },
        (payload) => {
          if (payload.eventType === 'UPDATE' || payload.eventType === 'INSERT') {
            const btn = payload.new as CtaButton;
            setLiveCtaButtons(prev => {
              const filtered = prev.filter(b => b.id !== btn.id);
              if (!btn.enabled) return filtered.sort((a, b) => a.sort_order - b.sort_order);
              return [...filtered, btn].sort((a, b) => a.sort_order - b.sort_order);
            });
          } else if (payload.eventType === 'DELETE') {
            const oldBtn = payload.old as { id: string };
            setLiveCtaButtons(prev => prev.filter(b => b.id !== oldBtn.id));
          }
        }
      )
      // Surveys — real-time payloads are raw rows (no joined survey_questions),
      // so merge with existing liveSurvey to preserve the questions array.
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'surveys', filter: `club_id=eq.${finalClubId}` },
        (payload) => {
          if (payload.eventType === 'UPDATE' || payload.eventType === 'INSERT') {
            const s = payload.new as SurveyWithQuestions;
            if (s.active) {
              setLiveSurvey(prev => prev && prev.id === s.id
                ? { ...prev, ...s, survey_questions: prev.survey_questions }
                : prev || s
              );
            } else {
              setLiveSurvey(prev => prev?.id === s.id ? null : prev);
            }
          } else if (payload.eventType === 'DELETE') {
            const oldS = payload.old as { id: string };
            setLiveSurvey(prev => prev?.id === oldS.id ? null : prev);
          }
        }
      )
      // Live polls open/close — raw row (no joined options); preserve options
      // from the initial RPC load. A club can run several polls at once, so we
      // keep the full LIST (upsert/remove by id) instead of a single slot. A
      // poll opening mid-session without cached options stays hidden until the
      // next scan/refresh reloads its options.
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'polls', filter: `club_id=eq.${finalClubId}` },
        (payload) => {
          if (payload.eventType === 'UPDATE' || payload.eventType === 'INSERT') {
            const p = payload.new as Poll;
            setLivePolls(prev => {
              const existing = prev.find(x => x.id === p.id);
              // Merge the raw row while preserving that poll's cached options.
              const merged = existing ? { ...existing, ...p, options: existing.options } : p;
              return prev.some(x => x.id === p.id)
                ? prev.map(x => (x.id === p.id ? merged : x))
                : [...prev, merged];
            });
          } else if (payload.eventType === 'DELETE') {
            const oldP = payload.old as { id: string };
            setLivePolls(prev => prev.filter(x => x.id !== oldP.id));
          }
        }
      )
      // Prizes
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'prizes', filter: `club_id=eq.${finalClubId}` },
        (payload) => {
          if (payload.eventType === 'UPDATE' || payload.eventType === 'INSERT') {
            const p = payload.new as Prize;
            setLivePrizes(prev => {
              const filtered = prev.filter(item => item.id !== p.id);
              if (!p.enabled) return filtered;
              return [...filtered, p].sort((a, b) => a.sort_order - b.sort_order);
            });
          } else if (payload.eventType === 'DELETE') {
            const oldP = payload.old as { id: string };
            setLivePrizes(prev => prev.filter(item => item.id !== oldP.id));
          }
        }
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [finalClubId, liveSurvey?.id]);

  // Lineups Real-time - filtered by event_id
  useEffect(() => {
    if (!event?.id) return;

    const channel = supabase
      .channel(`lineup_updates_${event.id}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'event_lineups', filter: `event_id=eq.${event.id}` },
        (payload) => {
          if (payload.eventType === 'UPDATE' || payload.eventType === 'INSERT') {
            const l = payload.new as EventLineup;
            setLiveLineups(prev => {
              const filtered = prev.filter(item => item.id !== l.id);
              return [...filtered, l];
            });
          } else if (payload.eventType === 'DELETE') {
            const oldL = payload.old as { id: string };
            setLiveLineups(prev => prev.filter(item => item.id !== oldL.id));
          }
        }
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [event?.id]);

  // The serving kiosk's ordering switches (pause, pick-up now, half-time
  // pre-orders, payment mode…) reach open fan pages live. They live on
  // operator_settings now; club_settings only carries stale copies.
  const operatorId = operator?.id ?? null;
  useEffect(() => {
    if (!operatorId) return;

    const channel = supabase
      .channel(`operator_settings_${operatorId}`)
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'operator_settings', filter: `operator_id=eq.${operatorId}` },
        (payload) => {
          setLiveSettings(prev => mergeOperatorSettingsPatch<ClubSettings>(prev, payload.new as Record<string, unknown>));
        }
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [operatorId]);

  // Subscribe to real-time updates for menu items — the serving kiosk's menu,
  // which is not necessarily the tenant's own.
  useEffect(() => {
    if (!operatorId) return;

    const channel = supabase
      .channel(`menu_items_updates_${operatorId}`)
      .on(
        'postgres_changes',
        {
          event: '*', // Listen to INSERT, UPDATE, DELETE
          schema: 'public',
          table: 'menu_items',
          filter: `operator_id=eq.${operatorId}`,
        },
        (payload) => {
          if (payload.eventType === 'UPDATE' || payload.eventType === 'INSERT') {
            // Disabled, or no longer offered to this tenant (tenant_club_ids):
            // out of the list and the cart; otherwise merged in sort order.
            const changed = payload.new as MenuItem;
            const result = applyMenuItemChange(menuItemsRef.current, payload.eventType, changed, finalClubIdRef.current, tag?.seat?.section ?? null);
            setMenuItems(result.items);
            if (result.removeFromCart) {
              const gone = result.removeFromCart;
              setCart(prevCart => {
                // Lines with add-ons are keyed `<id>:<add-ons>` — drop those too.
                const keys = cartKeysForItem(prevCart, gone);
                if (keys.length === 0) return prevCart;
                const newCart = new Map(prevCart);
                for (const k of keys) newCart.delete(k);
                return newCart;
              });
            }
          } else if (payload.eventType === 'DELETE') {
            const oldItem = payload.old as { id: string };
            setMenuItems((prev) => prev.filter(item => item.id !== oldItem.id));
            setCart(prevCart => {
              const keys = cartKeysForItem(prevCart, oldItem.id);
              if (keys.length === 0) return prevCart;
              const newCart = new Map(prevCart);
              for (const k of keys) newCart.delete(k);
              return newCart;
            });
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [operatorId]);

  const getScanSource = (): ScanSource => {
    if (isScreenTag) return 'screen';
    const src = searchParams.get('src');
    if (src === 'qr') return 'qr';
    if (src === 'nfc') return 'nfc';
    return 'unknown';
  };

  /**
   * Presence Cookie and URL Sanitization
   * Detects a fresh scan from URL params, sets a short-lived presence cookie,
   * and subsequently cleans the URL to prevent bookmarking the scan proof.
   */
  useEffect(() => {
    const src = searchParams.get('src');
    const isScan = src === 'qr' || src === 'nfc';

    if (isScan) {
      // 1. Record that this is a fresh scan for the current lifecycle
      setIsFreshScan(true);
      // 2. Persist presence in a short-lived cookie (4 hours)
      setPresenceCookie();
      // 3. Clean the URL to remove the scan parameter (prevents bookmark sharing)
      const newUrl = window.location.pathname + window.location.search.replace(/[?&]src=[^&]*/, '').replace(/^&/, '?');
      window.history.replaceState({}, '', newUrl);
    } else {
      // If no src param, check if we have a valid cookie from a previous scan
      setIsFreshScan(hasPresenceCookie());
    }
  }, [searchParams]);

  // A valid big-screen join is this fan's fresh scan (poll pop-up, presence).
  useEffect(() => {
    if (isScreenTag && screenJoin.status === 'joined') setIsFreshScan(true);
  }, [isScreenTag, screenJoin.status]);

  // Check for already completed predictor/survey on mount
  useEffect(() => {
    if (!event?.id) return;

    const predictorKey = `predictor_completed_${event.id}`;
    if (localStorage.getItem(predictorKey)) {
      setPredictorAlreadyCompleted(true);
    }
  }, [event?.id]);

  useEffect(() => {
    if (!survey?.id || !event?.id) return;

    const surveyKey = event?.id
      ? `survey_completed_${survey.id}_${event.id}`
      : `survey_completed_${survey.id}`;
    if (localStorage.getItem(surveyKey)) {
      setSurveyAlreadyCompleted(true);
    }
  }, [survey?.id, event?.id]);

  // Restore the "I want it back" thanks state, so a fan who taps and then
  // reloads doesn't get shown the button again as if nothing happened.
  useEffect(() => {
    setWantsItBack(!!localStorage.getItem(`fan_page_disabled_clicked_${event?.id ?? 'none'}`));
  }, [event?.id]);

  // Identify the seat on any error thrown from here on. Fans have no account,
  // so the seat is the only identity we have — without it a fan-page error can
  // only be traced back to a person by matching timestamps by hand.
  useEffect(() => {
    if (!tag && !finalClubId) return;
    setSentryFanContext({
      tagId: tag?.id ?? null,
      seatId: tag?.seat_id ?? null,
      clubId: finalClubId ?? null,
      stadiumSlug: tagVenue?.slug ?? null,
    });
  }, [tag?.id, tag?.seat_id, finalClubId]);

  // Log page visit (only once per session per tag)
  useEffect(() => {
    if (!tag || !finalClubId) return;
    if (isScreenTag && screenJoin.status !== 'joined') return; // the guard would refuse it

    const sessionKey = `visit_logged_${tag.id}`;
    if (sessionStorage.getItem(sessionKey)) return;

    // device_id / scan_source 'screen' are newer than the generated types.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (supabase as any).from('interactions_page_visits').insert({
      club_id: finalClubId,
      stadium_id: tagVenue?.id,
      event_id: event?.id || null,
      seat_id: tag.seat_id,
      tag_id: tag.id,
      device_id: getDeviceId(),
      scan_source: getScanSource(),
      user_agent: navigator.userAgent,
    }).then(() => {
      sessionStorage.setItem(sessionKey, 'true');
    });

    // PostHog: identify fan session context
    posthog.capture('fan_page_view', fanEventProps({
      club_id: finalClubId,
      section: tag.seat?.section,
      seat: tag.seat?.seat_number,
      event_id: event?.id || null,
      scan_source: getScanSource(),
      ordering_mode: orderingMode,
    }, isDemoSeat));
  }, [tag?.id, finalClubId, event?.id, isScreenTag, screenJoin.status]);

  // PostHog: track view navigation
  useEffect(() => {
    if (activeView === 'home') return; // don't track the default/initial view
    posthog.capture('fan_view_changed', fanEventProps({ view: activeView, club_id: finalClubId }, isDemoSeat));
  }, [activeView]);

  // Presence Validation Logic (Option 1: Soft Risk)
  useEffect(() => {
    if (isScreenTag) return; // a screen tag's presence is its join (effect below)
    const runPresenceCheck = async () => {
      if (!event?.id || !tag?.seat_id) {
        setCheckingPresence(false);
        return;
      }

      setCheckingPresence(true);
      try {
        // Preview mode (overriddenClubId) and public demo seats (isDemoSeat)
        // bypass every presence gate so the app can be shown off-matchday.
        const bypassPresence = !!overriddenClubId || isDemoSeat;

        // 1. Time Window Check (3h before kickoff to 4h after)
        const inWindow = bypassPresence ? true : checkIsWithinMatchWindow(new Date(event.kickoff_datetime));
        setIsFanInMatchWindow(inWindow);

        // 2. Fresh Scan Check
        // If no fresh scan param was provided AND no valid presence cookie exists,
        // we lock the features and ask them to scan again.
        if (!isFreshScan && !bypassPresence) {
          setLockReason('scan_required');
          setIsSeatLocked(true);
          setCheckingPresence(false);
          return;
        }

        setLockReason('limit');

        // 3. Scan Limit Check (Anti-Fraud). Demo/preview seats skip it entirely.
        if (bypassPresence) {
          setIsSeatLocked(false);
          setCheckingPresence(false);
          return;
        }

        // We cast to any because the newly created RPC function may not be in the types yet
        const { data: isAllowed, error: rpcError } = await (supabase.rpc as any)('check_seat_scan_limit', {
          p_seat_id: tag.seat_id,
          p_event_id: event.id,
          p_max_scans: 5
        });

        if (rpcError) throw rpcError;
        setIsSeatLocked(!isAllowed);

      } catch (err) {
        console.error('Presence check failed:', err);
        // Fail open on transient errors to avoid blocking fans during a big match
        setIsFanInMatchWindow(true);
      } finally {
        setCheckingPresence(false);
      }
    };

    runPresenceCheck();
  }, [event?.id, tag?.seat_id, isDemoSeat]);

  // Screen tags: the join is the presence proof — no seat, no scan limit, no cookie.
  useEffect(() => {
    if (!isScreenTag) return;
    if (screenJoin.status === 'checking' || screenJoin.status === 'n/a') {
      setCheckingPresence(true);
      return;
    }
    setIsFanInMatchWindow(event?.kickoff_datetime ? checkIsWithinMatchWindow(new Date(event.kickoff_datetime)) : true);
    setLockReason('scan_screen');
    setIsSeatLocked(screenJoin.status !== 'joined');
    setCheckingPresence(false);
  }, [isScreenTag, screenJoin.status, event?.id, event?.kickoff_datetime]);

  // Authoritative read of the match status / minute. Used on mount, whenever
  // the realtime channel (re)subscribes, when the tab comes back to the
  // foreground, and on a slow poll while the match is in play. Realtime alone
  // left a fan on "4'" for an hour when iOS closed the socket.
  const fetchLiveScore = useCallback(async () => {
    if (!event?.id) return;
    const { data } = await supabase
      .from('event_live_scores')
      .select('match_status, finished_at, minute')
      .eq('event_id', event.id)
      .maybeSingle();

    if (data) {
      setMatchStatus(data.match_status);
      setLiveMinute((data as { minute?: number | null }).minute ?? null);
      // Use finished_at for accurate deadline calculation
      if (data.match_status === 'finished' && data.finished_at) {
        setMatchFinishedAt(new Date(data.finished_at));
      }
    }
  }, [event?.id]);

  const matchInPlay = matchStatus === 'live' || matchStatus === 'half_time';
  useRefetchOnResume(fetchLiveScore, {
    enabled: !!event?.id,
    intervalMs: matchInPlay ? 60_000 : undefined,
  });

  // Subscribe to live score updates to track match status
  useEffect(() => {
    if (!event?.id) return;

    fetchLiveScore();

    // Subscribe to realtime updates
    const channel = supabase
      .channel(`match-status-${event.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'event_live_scores',
          filter: `event_id=eq.${event.id}`,
        },
        (payload) => {
          if (payload.new && typeof payload.new === 'object') {
            const newData = payload.new as { match_status: string; finished_at: string | null; minute?: number | null };
            setMatchStatus(newData.match_status);
            setLiveMinute(newData.minute ?? null);

            // Track when match finished using finished_at
            if (newData.match_status === 'finished' && newData.finished_at && !matchFinishedAt) {
              setMatchFinishedAt(new Date(newData.finished_at));
            }
          }
        }
      )
      .subscribe((status) => {
        // A (re)subscribe after a dropped socket replays nothing, so re-read.
        if (status === 'SUBSCRIBED') fetchLiveScore();
      });

    return () => {
      supabase.removeChannel(channel);
    };
  }, [event?.id, matchFinishedAt, fetchLiveScore]);

  // When entering the survey view, immediately start the authoritative check.
  // The gate starts as 'checking' (set when tapping Survey button) so the UI never
  // renders intro/form until the server has confirmed open/closed.
  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      // Only run when we're in survey view AND gate is 'checking'
      if (activeView !== 'survey' || surveyGate !== 'checking') return;

      const { closed, finishedAt } = await checkSurveyClosedAuthoritatively();
      if (cancelled) return;

      // If we learned the match is finished, hydrate local state so timers/screens are consistent.
      if (finishedAt) {
        setMatchStatus('finished');
        setMatchFinishedAt(finishedAt);
      }

      setSurveyGate(closed ? 'closed' : 'open');
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [activeView, surveyGate, event?.id]);

  // Compute feature availability
  // Default close behavior is at kickoff. If the club opts into 'half_time',
  // the predictor stays open during the first half and closes once the match
  // REACHES half-time — one-way. The second half flips match_status back to
  // 'live', so status alone can't tell the halves apart: also close while
  // 'live' once the minute passes 45, and when the feed sends no minute fall
  // back to kickoff + 50 min.
  const predictorCloseAt = effectiveSettings?.predictor_close_at ?? 'kickoff';
  const pastFirstHalfWindow =
    liveMinute !== null
      ? liveMinute >= 46
      : !!event?.kickoff_datetime && Date.now() > new Date(event.kickoff_datetime).getTime() + 50 * 60 * 1000;
  // Kickoff mode must also close on wall-clock time: the live-score row (and
  // its realtime status flip) can lag kickoff by minutes, and a fan sitting on
  // the open form otherwise sees no change and rage-taps a submit the DB will
  // reject (observed at JäPS vs Haka: rage on "Lähetä veikkaus" 2 min after KO).
  const kickoffPassed =
    !!event?.kickoff_datetime && Date.now() > new Date(event.kickoff_datetime).getTime();
  const isPredictorClosed = predictorCloseAt === 'half_time'
    ? ['half_time', 'finished', 'postponed', 'cancelled'].includes(matchStatus)
      || (matchStatus === 'live' && pastFirstHalfWindow)
    : matchStatus !== 'not_started' || kickoffPassed;

  // Survey closes 30 minutes after match finished
  // Re-compute on every render (Date.now() changes) to ensure real-time accuracy
  const surveyClosed = checkIsSurveyClosed(matchFinishedAt) || surveyGate === 'closed';

  // Force re-render periodically to update isSurveyClosed after matchFinishedAt is set
  useEffect(() => {
    if (matchStatus !== 'finished' || !matchFinishedAt) return;

    const thirtyMinutesAfterFinish = matchFinishedAt.getTime() + 30 * 60 * 1000;
    const timeUntilClose = thirtyMinutesAfterFinish - Date.now();

    // If already closed, no need for timer
    if (timeUntilClose <= 0) return;

    // Set timer to force re-render when survey closes
    const timer = setTimeout(() => {
      forceUpdate(n => n + 1);
    }, timeUntilClose + 100); // +100ms buffer

    return () => clearTimeout(timer);
  }, [matchStatus, matchFinishedAt]);

  // Force a re-render right after the predictor deadline passes, so a fan
  // sitting on the open form sees it flip to the closed screen without any
  // realtime update (isPredictorClosed is wall-clock-based but only evaluated
  // on render). Deadline: kickoff (kickoff mode) or kickoff + 50 min
  // (half_time mode's no-minute fallback).
  useEffect(() => {
    if (!event?.kickoff_datetime || isPredictorClosed) return;
    const kickoffMs = new Date(event.kickoff_datetime).getTime();
    const deadline = predictorCloseAt === 'half_time' ? kickoffMs + 50 * 60 * 1000 : kickoffMs;
    const delay = deadline - Date.now();
    if (delay <= 0) return;
    const timer = setTimeout(() => forceUpdate(n => n + 1), delay + 1000);
    return () => clearTimeout(timer);
  }, [event?.kickoff_datetime, predictorCloseAt, isPredictorClosed]);

  // Half-time pre-orders. One rule (src/lib/preorder.ts) decides "closed",
  // sticky across the half-time → live flip; the offer adds the club/section/
  // payment gates. Server-side create-payment-intent re-derives the same rule.
  const preorderCutoffMinute: number =
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (effectiveSettings as any)?.preorder_cutoff_minute ?? DEFAULT_PREORDER_CUTOFF_MINUTE;
  const preorderWindow = {
    matchStatus, liveMinute,
    kickoffIso: event?.kickoff_datetime ?? null,
    cutoffMinute: preorderCutoffMinute,
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const preorderEnabled = !!(effectiveSettings as any)?.preorder_enabled;
  const preorderClosed = isPreorderWindowClosed(preorderWindow);
  // The pre-order the fan still holds. The hook keeps a terminal state
  // (cancelled / collected / refunded) so the detail screen can explain it,
  // but that must not block a new pre-order.
  const activePreorder =
    preorder.preorder
    && !['cancelled', 'collected'].includes(preorder.preorder.status)
    && preorder.preorder.paymentStatus !== 'refunded'
      ? preorder.preorder
      : null;
  const preorderOfferable =
    !activePreorder &&
    canOfferPreorder({
      ...preorderWindow,
      preorderEnabled,
      sectionMode: orderingMode,
      hasCollectionPoint: !!collectionPoint,
      clubPaymentMode: paymentMode,
      onlinePaymentReady,
      eventKind: event?.event_kind ?? null,
    });
  // Pre-orders would apply here but for the window: the non-window gates of
  // canOfferPreorder (supabase/functions/_shared/preorder.ts), mirrored so the
  // "closed" banner never shows where pre-orders don't apply at all (a
  // concert, an in-person-only club, online payment not ready).
  const preorderClosedApplies =
    preorderEnabled &&
    preorderClosed &&
    !activePreorder &&
    (orderingMode === 'pickup' || orderingMode === 'both') &&
    !!collectionPoint &&
    paymentMode !== 'in_person_only' &&
    onlinePaymentReady === true &&
    (event?.event_kind ?? 'sports_match') === 'sports_match';
  // "Pick-up now" club switch (missing = enabled). Off: pick-up exists only
  // as a half-time pre-order, so pick-up is blocked whenever no pre-order can
  // be offered. pickupImpossible stays the structural flag (the cart-restore
  // effect above reads it before these derivations exist, and a time-based
  // window must never delete cart lines); pickupBlocked adds the window.
  const pickupNowEnabled = isPickupNowEnabled(effectiveSettings?.pickup_now_enabled);
  const pickupBlocked = pickupImpossible || (!pickupNowEnabled && !preorderOfferable);
  // Why pick-up is unavailable with Pick-up now off: the fan already holds
  // this match's pre-order (a second one is not offered), or pre-orders are
  // closed. Shown by the cart toggle, the "When" block and the pay area.
  const pickupHalfTimeOnlyNote = activePreorder
    ? t('ordering.pickupHeldPreorder')
    : t('ordering.pickupHalfTimeOnlyClosed');
  // The cart's "When do you want it?" block (pick-up orders only). The pay
  // area's no-slot guard repeats the note only when this block is absent.
  const pickupWhenShown = effectiveIsPickup && (!pickupNowEnabled || preorderOfferable || preorderClosedApplies);
  const preorderPointName = collectionPoint
    ? t(`pickup.name.${collectionPoint.id}`, { defaultValue: collectionPoint.name })
    : '';
  // The TRACKED pre-order's point (as placed, else the section's) and its
  // one-line status — shared by the header strip and the detail screen.
  const trackedPreorderPoint = preorder.preorder?.collectionPoint ?? collectionPoint;
  const trackedPreorderPointName = trackedPreorderPoint
    ? t(`pickup.name.${trackedPreorderPoint.id}`, { defaultValue: trackedPreorderPoint.name })
    : '';
  const preorderStripLabel = ((): string => {
    const p = preorder.preorder;
    if (!p) return '';
    // Refunded first: a refunded pre-order is usually also cancelled.
    if (p.paymentStatus === 'refunded') return t('payment.orderRefunded', { defaultValue: 'Your order has been refunded.' });
    switch (p.status) {
      case 'scheduled': return t('preorder.stripScheduled');
      case 'pending': return t('preorder.stripPreparing');
      case 'ready_at_collection_point': return t('preorder.stripReady', { point: trackedPreorderPointName });
      case 'collected': return t('ordering.orderCollected', { defaultValue: 'Order Collected!' });
      case 'cancelled': return t('payment.orderCancelled', { defaultValue: 'Your order has been cancelled.' });
      default: return t('preorder.stripProcessing'); // pending_payment
    }
  })();

  // A half-time slot the fan picked can stop being valid while the cart is
  // open: the window closes (tell them — the order becomes a now-order) or
  // another gate drops (settings, payment readiness, a pre-order now held —
  // reset silently). Only ever sets the slot to null, so it cannot loop.
  useEffect(() => {
    // A payment in flight completes as a pre-order regardless (the server
    // accepted it; paymentPickupSlot routes success), so don't tell the fan
    // it switched to now.
    if (!pickupSlot || paymentPickupSlot) return;
    if (preorderClosed) {
      setPickupSlot(null);
      // With Pick-up now off the slot is auto-selected for every pick-up fan,
      // cart or not — only a fan with something in the cart gets told (and
      // moved); an empty cart just loses the slot quietly. (`cart`, not
      // `cartCount`: that is declared further down — TDZ in these deps.)
      if (cart.size === 0) return;
      if (pickupNowEnabled) {
        toast.info(t('preorder.switchedToNow'));
      } else {
        // No "now" pick-up to fall back to: say so, and in a 'both' section
        // move to seat delivery (unless a pick-up-only item pins pick-up —
        // the pay area then shows the closed note instead).
        toast.info(t('preorder.closedNoNow'));
        if (canFanChoose(orderingMode) && !cartNeedsPickup) setFanFulfillmentChoice('delivery');
      }
    } else if (!preorderOfferable) {
      setPickupSlot(null);
    }
  }, [pickupSlot, paymentPickupSlot, preorderClosed, preorderOfferable, t, pickupNowEnabled, orderingMode, cartNeedsPickup, cart]);

  // A pick-up-only item in the cart of a 'both' section: seat delivery is off
  // the table, so select Pick-up (the Seat delivery button is disabled while
  // this holds). Only ever sets 'pickup', and only while the cart needs it, so
  // it cannot fight the fan or loop. Never without a collection point — that
  // would submit a pick-up with collection_point_id null.
  useEffect(() => {
    if (canFanChoose(orderingMode) && collectionPoint && !pickupBlocked && cartNeedsPickup && fanFulfillmentChoice !== 'pickup') {
      setFanFulfillmentChoice('pickup');
    }
  }, [orderingMode, collectionPoint, pickupBlocked, cartNeedsPickup, fanFulfillmentChoice]);

  // The mirror image: pick-up is blocked (Pick-up now off and no pre-order
  // on offer) while a 'both' section has Pick-up selected — fall back to
  // seat delivery. Only ever sets 'delivery', only while pickupBlocked, and
  // the effect above only sets 'pickup' while !pickupBlocked, so the two
  // never alternate. A pick-up-only item keeps Pick-up (nothing else works).
  useEffect(() => {
    if (canFanChoose(orderingMode) && pickupBlocked && fanFulfillmentChoice === 'pickup' && !cartNeedsPickup) {
      setFanFulfillmentChoice('delivery');
    }
  }, [orderingMode, pickupBlocked, fanFulfillmentChoice, cartNeedsPickup]);

  // Pick-up now off: a pick-up cart can only be a half-time pre-order, so
  // select the slot for the fan. Only ever sets the slot, only while it is
  // null and a pre-order is offerable; the window effect above only clears
  // it once the offer is gone, so they cannot alternate.
  useEffect(() => {
    if (!pickupNowEnabled && effectiveIsPickup && preorderOfferable && !pickupSlot && !paymentPickupSlot) {
      setPickupSlot(PREORDER_SLOT);
    }
  }, [pickupNowEnabled, effectiveIsPickup, preorderOfferable, pickupSlot, paymentPickupSlot]);

  // Re-render right after the pre-order window's wall-clock deadlines (kickoff
  // + cutoff, kickoff + hard cap), so the chooser flips to "closed" without a
  // realtime update — same pattern as the predictor timer above.
  useEffect(() => {
    if (!preorderEnabled || !event?.kickoff_datetime || preorderClosed) return;
    const now = Date.now();
    const upcoming = [
      preorderClosesAtMs(event.kickoff_datetime, preorderCutoffMinute),
      preorderHardCapAtMs(event.kickoff_datetime),
    ].filter((ms): ms is number => ms !== null && ms > now);
    if (upcoming.length === 0) return;
    const delay = Math.min(...upcoming) - now;
    const timer = setTimeout(() => forceUpdate(n => n + 1), delay + 1000);
    return () => clearTimeout(timer);
  }, [preorderEnabled, event?.kickoff_datetime, preorderCutoffMinute, preorderClosed]);

  const handleCtaClick = (button: CtaButton) => async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    if (!tag || !button.button_url || !finalClubId) return;

    // Ensure URL has a protocol, otherwise browser treats it as relative path
    let url = button.button_url || '';
    if (url && !url.startsWith('http://') && !url.startsWith('https://')) {
      url = 'https://' + url;
    }

    posthog.capture('fan_cta_clicked', fanEventProps({ club_id: finalClubId, button_text: button.button_text, url }, isDemoSeat));

    // Log the click — await to ensure it completes before the page navigates away,
    // otherwise the browser aborts the in-flight fetch and the click is lost.
    try {
      // device_id is newer than the generated types.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      await (supabase as any).from('interactions_cta_clicks').insert({
        club_id: finalClubId,
        stadium_id: tagVenue?.id,
        event_id: event?.id || null,
        seat_id: tag.seat_id,
        tag_id: tag.id,
        device_id: getDeviceId(),
        user_agent: navigator.userAgent
      });
    } catch {
      // Don't block navigation if logging fails
    }

    window.location.href = url;
  };

  const handlePredictorSubmit = async () => {
    if (!tag || !finalClubId) return;

    // Strict deadline check — must match the club's chosen close behavior so
    // the client never sends a request the server-side trigger will reject.
    if (isPredictorClosed) {
      toast.error(t('predictor.closedMessage'));
      return;
    }
    if (predictorCloseAt === 'kickoff' && event?.kickoff_datetime && new Date() > new Date(event.kickoff_datetime)) {
      toast.error(t('predictor.closedMessage'));
      return;
    }

    // Validate email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!predictorEmail.trim() || !emailRegex.test(predictorEmail.trim())) {
      toast.error(t('error.invalidEmail'));
      return;
    }

    // Validate terms consent if links are configured
    if ((settings?.privacy_policy_url || settings?.terms_conditions_url) && !predictorTermsConsent) {
      toast.error(t('error.acceptTerms'));
      return;
    }

    setSubmitting(true);
    try {
      // device_id is newer than the generated types.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data: insertedData, error: insertedError } = await (supabase as any).from('interactions_score_predictions').insert({
        club_id: finalClubId,
        stadium_id: tagVenue?.id,
        event_id: event?.id || null,
        seat_id: tag.seat_id,
        tag_id: tag.id,
        device_id: getDeviceId(),
        home_score: homeScore === '' ? 0 : parseInt(homeScore),
        away_score: awayScore === '' ? 0 : parseInt(awayScore),
        email: predictorEmail.trim(),
        marketing_consent: predictorMarketingConsent
      }).select('id').single();

      if (isScreenJoinError(insertedError)) screenJoin.expire();
      if (insertedError) throw insertedError;
      if (insertedData) {
        setPredictorInteractionId(insertedData.id);
      }

      // Store completion in localStorage to prevent repeat submissions
      if (event?.id) {
        localStorage.setItem(`predictor_completed_${event.id}`, 'true');
      }

      setPredictorSubmitted(true);
      posthog.capture('fan_prediction_submitted', fanEventProps({ club_id: finalClubId, home_score: homeScore, away_score: awayScore, event_id: event?.id }, isDemoSeat));
      if (predictorEmail.trim()) posthog.identify(predictorEmail.trim(), { club_id: finalClubId });
      // Show prize wheel if prizes are available
      if (predictorPrizes.length >= 2) {
        setShowPredictorWheel(true);
      }
      toast.success(t('predictor.submitted'));
    } catch (err) {
      console.error('Error submitting prediction:', err);
      if (isScreenJoinError(err)) return; // the lock screen takes over; finally still resets submitting
      // The DB deadline trigger raises "Predictions are closed for this match."
      // / "Match has already started. Predictions are closed." — surface that
      // as the closed state instead of a generic error.
      const msg = String((err as { message?: string })?.message ?? '');
      if (/predictions are closed|already started/i.test(msg)) {
        toast.error(t('predictor.closedMessage'));
        setPredictorServerClosed(true);
      } else {
        toast.error(t('error.genericSubmit'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleSurveySubmit = async () => {
    if (!tag || !finalClubId || !effectiveSurvey) return;

    // Fast local guard using the surveyGate state (prevents submits if UI state is stale)
    if (surveyGate === 'closed' || surveyClosed) {
      toast.error(t('survey.closed') || 'Survey is no longer open for this fixture');
      return;
    }

    // Validate email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!surveyEmail.trim() || !emailRegex.test(surveyEmail.trim())) {
      toast.error(t('error.invalidEmail'));
      return;
    }

    const missingRequired = effectiveSurvey.survey_questions
      .filter(q => q.required)
      .some(q => isSurveyAnswerMissing(surveyResponses[q.id]));

    if (missingRequired) {
      toast.error(t('error.requiredQuestions'));
      return;
    }

    // Validate terms consent if links are configured
    if ((settings?.privacy_policy_url || settings?.terms_conditions_url) && !surveyTermsConsent) {
      toast.error(t('error.acceptTerms'));
      return;
    }

    setSubmitting(true);
    try {
      // Server-side deadline validation: re-fetch authoritative match status
      if (event?.id) {
        const { data: liveScore, error: liveScoreError } = await supabase
          .from('event_live_scores')
          .select('match_status, finished_at')
          .eq('event_id', event.id)
          .maybeSingle();

        if (liveScoreError) {
          // If we cannot validate, fall back to local guard only.
          console.warn('Failed to fetch live score for survey validation:', liveScoreError);
        }

        if (liveScore?.match_status === 'finished' && liveScore.finished_at) {
          const finishedAt = new Date(liveScore.finished_at);
          const minutesSinceFinish = (Date.now() - finishedAt.getTime()) / 60000;
          if (minutesSinceFinish > 30) {
            toast.error(t('survey.closed') || 'Survey is no longer open for this fixture');
            setSubmitting(false);
            return;
          }
        }
      }

      // device_id is newer than the generated types.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data: insertedData, error: insertError } = await (supabase as any).from('interactions_survey_responses').insert({
        club_id: finalClubId,
        stadium_id: tagVenue?.id,
        event_id: event?.id || null,
        seat_id: tag.seat_id,
        tag_id: tag.id,
        device_id: getDeviceId(),
        responses_json: surveyResponses,
        email: surveyEmail.trim(),
        marketing_consent: surveyMarketingConsent
      }).select('id').single();

      if (isScreenJoinError(insertError)) screenJoin.expire();
      if (insertError) throw insertError;
      if (insertedData) {
        setSurveyInteractionId(insertedData.id);
      }

      // Store completion in localStorage to prevent repeat submissions (scoped to event)
      const surveyStorageKey = event?.id
        ? `survey_completed_${effectiveSurvey.id}_${event.id}`
        : `survey_completed_${effectiveSurvey.id}`;
      localStorage.setItem(surveyStorageKey, 'true');

      setSurveySubmitted(true);
      posthog.capture('fan_survey_submitted', fanEventProps({ club_id: finalClubId, survey_id: effectiveSurvey.id, event_id: event?.id }, isDemoSeat));
      if (surveyEmail.trim()) posthog.identify(surveyEmail.trim(), { club_id: finalClubId });
      // Show prize wheel if prizes are available
      if (surveyPrizes.length >= 2) {
        setShowSurveyWheel(true);
      }
      toast.success(t('survey.thankYou'));
    } catch (err) {
      console.error('Error submitting survey:', err);
      if (isScreenJoinError(err)) return; // the lock screen takes over; finally still resets submitting
      toast.error(t('error.genericSubmit'));
    } finally {
      setSubmitting(false);
    }
  };

  // Dynamic theming
  const primaryColor = effectiveSettings?.primary_color || '#1e3a5f';
  // Card, text, accent and icon colours derived from the club's brand colour.
  // The brand colour stays the page background; everything on top is computed
  // from it so it separates and stays readable on any club's palette.
  const palette = useMemo(() => buildFanPalette(primaryColor), [primaryColor]);
  const stadiumImageUrl = effectiveSettings?.stadium_image_url || null;
  const customFontUrl = effectiveSettings?.custom_font_url || null;
  const fontFamily = fontFamilyMap[effectiveSettings?.font_family || 'Inter'] || "'Inter', sans-serif";

  // Everything a fan report needs to be actionable, captured once so the sheet
  // never has to reach back into this component's state. Seat labels come
  // straight off the tag rather than the display* versions, which are computed
  // further down and dress up synthetic preview seats — a report from the
  // PREVIEW seat should say TEST, not "Section A3".
  const reportContext = useMemo(() => ({
    tagId: tag?.id ?? null,
    tagPublicId: tag?.tag_public_id ?? null,
    clubId: finalClubId ?? null,
    stadiumId: tagVenue?.id ?? null,
    stadiumSlug: tagVenue?.slug ?? null,
    seatId: tag?.seat_id ?? null,
    seatLabel: tag?.seat
      ? `${tag.seat.section} · ${tag.seat.row} · ${tag.seat.seat_number}`
      : (tag?.label ?? null),
    eventId: event?.id ?? null,
    view: activeView,
    locale,
    pendingOrderId: pendingOrderId ?? null,
    isDemoSeat,
  }), [tag, finalClubId, event?.id, activeView, locale, pendingOrderId, isDemoSeat]);

  // Build custom font style if needed (sanitized to prevent XSS)
  const customFontStyle = customFontUrl && effectiveSettings?.font_family === 'Custom'
    ? buildSafeFontFaceStyle(customFontUrl)
    : '';

  // Dynamically load Google Font when a non-default font is selected
  useEffect(() => {
    const selectedFont = effectiveSettings?.font_family;
    if (!selectedFont || selectedFont === 'Inter' || selectedFont === 'Custom') return;

    const linkId = `google-font-${selectedFont.replace(/\s/g, '-')}`;
    if (document.getElementById(linkId)) return;

    const link = document.createElement('link');
    link.id = linkId;
    link.rel = 'stylesheet';
    link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(selectedFont)}:wght@400;500;600;700&display=swap`;
    document.head.appendChild(link);
  }, [effectiveSettings?.font_family]);

  // ORDERING — cart utilities hoisted out of the render function so they have stable identity.
  // Placed before early returns so hook order stays stable across renders (Rules of Hooks).
  // A pick-up-only item where no pick-up is possible (no pick-up in the
  // section, or Pick-up now off with no pre-order on offer) can't be ordered.
  const itemBlockedInSection = useCallback(
    (itemId: string) => pickupBlocked && menuItems.some((i) => i.id === itemId && isPickupOnly(i)),
    [pickupBlocked, menuItems],
  );
  // The cartCount memo is declared below addToCart (a deps array can't read
  // it there); the ref carries the last rendered count into the callback.
  const cartCountRef = useRef(0);
  const addToCart = useCallback((itemId: string, selectedAddons: any[] = []) => {
    if (itemBlockedInSection(itemId)) return;
    posthog.capture('fan_item_added', fanEventProps({ item_id: itemId, cart_count: cartCountRef.current + 1, event_id: event?.id ?? null, club_id: finalClubId }, isDemoSeat));
    const addonNames = [...selectedAddons].sort((a, b) => a.name.localeCompare(b.name)).map(a => a.name).join(',');
    const cartKey = addonNames ? `${itemId}:${addonNames}` : itemId;

    setCart(prev => {
      const newCart = new Map(prev);
      const existing = newCart.get(cartKey);
      if (existing) {
        newCart.set(cartKey, { ...existing, qty: existing.qty + 1 });
      } else {
        newCart.set(cartKey, { qty: 1, addons: selectedAddons });
      }
      return newCart;
    });
  }, [itemBlockedInSection, posthog, event?.id, finalClubId, isDemoSeat]);

  const removeFromCart = useCallback((cartKey: string) => {
    setCart(prev => {
      const newCart = new Map(prev);
      const existing = newCart.get(cartKey);
      if (!existing) return prev;

      if (existing.qty <= 1) newCart.delete(cartKey);
      else newCart.set(cartKey, { ...existing, qty: existing.qty - 1 });
      return newCart;
    });
  }, []);

  const cartTotal = useMemo(() => Array.from(cart.entries()).reduce((sum, [key, val]) => {
    const itemId = key.split(':')[0];
    const item = menuItems.find(i => i.id === itemId);
    const addonsTotal = (val.addons || []).reduce((s: number, a: any) => s + (a.price || 0), 0);
    return sum + (item ? (item.price + addonsTotal) * val.qty : 0);
  }, 0), [cart, menuItems]);

  // Service fee: only applied when the club has the feature enabled AND has a non-zero amount.
  // Cart must be non-empty — empty carts can't be checked out so we hide the line entirely.
  const serviceFee = useMemo(() => {
    if (cart.size === 0) return 0;
    if (!effectiveSettings?.service_fee_feature_enabled) return 0;
    const cents = effectiveSettings.service_fee_cents ?? 0;
    return cents > 0 ? cents / 100 : 0;
  }, [cart.size, effectiveSettings?.service_fee_feature_enabled, effectiveSettings?.service_fee_cents]);

  const cartGrandTotal = cartTotal + serviceFee;

  const getItemCartCount = useCallback((itemId: string) => {
    let count = 0;
    cart.forEach((val, key) => {
      if (key === itemId || key.startsWith(`${itemId}:`)) count += val.qty;
    });
    return count;
  }, [cart]);

  const getFirstCartKeyForItem = useCallback((itemId: string) => {
    return Array.from(cart.keys()).find(k => k === itemId || k.startsWith(`${itemId}:`));
  }, [cart]);

  const cartCount = useMemo(
    () => Array.from(cart.values()).reduce((sum, val) => sum + val.qty, 0),
    [cart]
  );
  cartCountRef.current = cartCount;

  // Synchronous double-submit lock. React state updates aren't immediate, so
  // two rapid clicks can both pass the `submitting === false` check before
  // setSubmitting(true) lands. A useRef flip is synchronous and reliably
  // dedupes.
  const initiatingPaymentRef = useRef(false);

  const handleInitiatePayment = useCallback(async (selectedPaymentMode: 'online' | 'in_person') => {
    // Half-time pre-order in the cart? It is online-only (the in-person button
    // never renders for it; this guards any other caller).
    const slot = pickupSlot;
    if (slot && selectedPaymentMode !== 'online') return;
    if (cartCount === 0) return;
    if (!tag || !tag.id || (!tag.seat_id && !isScreenTag)) {
      // get_fan_page_data should always return a (real or synthetic test) tag.
      // If we get here something upstream is broken — surface it loudly.
      toast.error("Couldn't load this seat. Please refresh and try again.");
      return;
    }
    if (!finalClubId) {
      toast.error("Couldn't identify the club for this seat. Please refresh and try again.");
      return;
    }
    // Optional giveaway entry: empty is fine, but a typed invalid address
    // blocks submit — silently dropping it would betray the fan's entry.
    if (giveawayEmail.trim() && !isValidEmail(giveawayEmail)) {
      toast.error(t('error.invalidEmail'));
      return;
    }
    if (initiatingPaymentRef.current) return;          // already in flight
    initiatingPaymentRef.current = true;
    setSubmitting(true);
    try {
      // Build cart items for the Edge Function (IDs + quantities + addons only, NOT prices)
      const cartItems = Array.from(cart.entries()).map(([key, val]) => ({
        item_id: key.split(':')[0],
        quantity: val.qty,
        selected_addons: (val.addons || []).map((a: any) => ({ name: a.name, price: a.price })),
      }));

      // Section mode collapsed with the fan's choice ('both' sections). The
      // edge function re-derives the same thing server-side against the
      // section config, so a tampered body can't escape the club's setup.
      const modeFields = buildOrderModeFields({
        sectionMode: orderingMode,
        fanChoice: slot ? 'pickup' : fanFulfillmentChoice,
        collectionPointId: collectionPoint?.id ?? null,
        fanPhone,
      });
      // Every Pay tap that passed the guards, before any server call.
      posthog.capture('fan_pay_tapped', fanEventProps({ payment_mode: selectedPaymentMode, ordering_mode: modeFields.ordering_mode, item_count: cartCount, total: cartGrandTotal, event_id: event?.id ?? null, club_id: finalClubId }, isDemoSeat));
      const giveawayEntry = normalizeGiveawayEmail(giveawayEmail, true);

      const { data, error: fnError } = await supabase.functions.invoke('create-payment-intent', {
        body: {
          tag_id: tag.id,
          club_id: finalClubId,
          stadium_id: tagVenue?.id,
          event_id: event?.id || null,
          seat_id: tag.seat_id,
          // A screen tag has no seat: the order is this device's, and the
          // server checks the device joined the screen (screen_joins).
          device_id: getDeviceId(),
          items: cartItems,
          fan_comments: fanComments.trim() || null,
          // Format-normalized here; the edge function re-checks the club's
          // giveaway_enabled and nulls it when the feature is off.
          giveaway_email: giveawayEntry,
          ...modeFields,
          payment_mode: selectedPaymentMode,
          // 'half_time' for a pre-order, null for a now-order. The server
          // refuses (409) rather than converting a pre-order into a now-order.
          pickup_slot: slot,
        },
      });

      if (fnError) throw fnError;
      if (data?.error) throw new Error(data.error);

      const { order_id, verified_total, client_secret, total_vat_amount, stripe_account } = data;
      // 3-character pick-up code (null for delivery, or before the migration).
      const pickupCode: string | null = data.pickup_code ?? null;

      // Display lines for the local state (the server holds the real ones).
      const orderItems = buildOrderLines(cart, (id) => knownMenuItemsRef.current.get(id));

      // Remember what THIS order is (delivery vs pickup + its point) — the
      // post-order screens key off the order, not the section config. A
      // pre-order is tracked by useHalfTimePreorder instead, so it must not
      // overwrite the now-order's post-order state.
      if (!slot) {
        setPendingOrderingMode(modeFields.ordering_mode);
        setPendingCollectionPoint(modeFields.ordering_mode === 'pickup' ? collectionPoint : null);
        setPendingGiveawayEntered(!!giveawayEntry);
      }

      if (selectedPaymentMode === 'online' && client_secret) {
        // Save order_id to localStorage NOW so we can recover the success view
        // if the buyer is redirected away (iDEAL, Bancontact, MobilePay, etc.)
        // and lands back on the tag page after authorizing at the bank.
        // A pre-order uses its own pointer; the in-flight slot tells success/
        // abandon which pointer this payment belongs to.
        setPaymentPickupSlot(slot);
        if (!slot) nowOrderConfirmedPaidRef.current = null;
        if (tag) localStorage.setItem(slot ? preorderStorageKey(tag.id) : `idseat_pending_order_${tag.id}`, order_id);
        setPaymentClientSecret(client_secret);
        setPaymentOrderId(order_id);
        setPaymentPickupCode(pickupCode);
        setPaymentStripeAccount(stripe_account || null);
        setShowPaymentSheet(true);
        setSubmitting(false);
      } else {
        // In-person payment: order is submitted directly (same as old behavior)
        nowOrderConfirmedPaidRef.current = null;
        if (tag) localStorage.setItem(`idseat_pending_order_${tag.id}`, order_id);
        setPendingOrderId(order_id);
        setPendingPickupCode(pickupCode);
        setPendingOrderStatus('pending');
        setPendingOrderItems(orderItems);
        setPendingOrderTotal(verified_total);
        // Receipt handled via Stripe
        setOrderSubmitted(true);
        setShowCart(false);
        posthog.capture('fan_order_submitted', fanEventProps({ club_id: finalClubId, total: verified_total, item_count: cartCount, ordering_mode: modeFields.ordering_mode, payment_mode: selectedPaymentMode, event_id: event?.id }, isDemoSeat));
        toast.success(t('ordering.orderReceived'));
      }
    } catch (err: any) {
      console.error('Error initiating payment:', err);
      const rawMsg = (err?.message || '').toLowerCase();
      // Refusals are a non-2xx {error, code}; supabase-js wraps them in a
      // FunctionsHttpError whose message is generic, so read the body for
      // every error — msg = rawMsg + code + error text, so every rawMsg match
      // below still matches, and the body-only gates (paused, not ready,
      // pick-up-only items) now reach their own toast.
      let msg = rawMsg;
      let pickupOnlyItems: string[] = [];
      let unavailableItems: string[] = [];
      try {
        const body = await err?.context?.json?.();
        msg = `${rawMsg} ${body?.code ?? ''} ${body?.error ?? ''}`.toLowerCase();
        if (Array.isArray(body?.pickup_only_items)) {
          pickupOnlyItems = body.pickup_only_items.filter((n: unknown): n is string => typeof n === 'string');
        }
        if (Array.isArray(body?.unavailable_items)) {
          unavailableItems = body.unavailable_items.filter((n: unknown): n is string => typeof n === 'string');
        }
      } catch { /* body missing or not JSON — keep rawMsg */ }

      // Screen tag: this device's join has lapsed (or was never claimed) —
      // back to the lock screen, same as a refused vote.
      if (msg.includes('screen_join_required')) {
        screenJoin.expire();
        return;
      }
      posthog.capture('fan_order_refused', fanEventProps({ reason: refusalReason(msg), item_count: cartCount, total: cartGrandTotal, payment_mode: selectedPaymentMode, event_id: event?.id ?? null, club_id: finalClubId }, isDemoSeat));
      // An item the kiosk no longer offers at this tenant's matches (a stale
      // page): name it, drop it from the list and the cart, let the fan retry.
      if (msg.includes('item_not_for_tenant')) {
        toast.error(unavailableItems.length > 0
          ? `${unavailableItems.join(', ')}: ${t('ordering.errorItemNotForTenant')}`
          : t('ordering.errorItemNotForTenant'));
        const goneIds = new Set(menuItemsRef.current.filter(i => unavailableItems.includes(i.name)).map(i => i.id));
        if (goneIds.size > 0) {
          setMenuItems(prev => prev.filter(i => !goneIds.has(i.id)));
          setCart(prevCart => {
            const newCart = new Map(prevCart);
            for (const id of goneIds) for (const k of cartKeysForItem(newCart, id)) newCart.delete(k);
            return newCart;
          });
        }
        return;
      }
      // A cart line skipped a required add-on choice (e.g. a basket restored
      // from before the kiosk made the kind of tea required): name the item.
      if (msg.includes('addon_choice_required')) {
        toast.error(unavailableItems.length > 0
          ? `${unavailableItems.join(', ')}: ${t('ordering.addonChoiceRequired')}`
          : t('ordering.addonChoiceRequired'));
        return;
      }
      // Friendly message for the pause gate from create-payment-intent.
      if (msg.includes('ordering_paused') || msg.includes('temporarily paused')) {
        // Lock the page now (the realtime push may never come) and keep the basket.
        markPausedByServer();
        toast.info(t('ordering.paused'));
      // Friendly message for the readiness gate from create-payment-intent.
      } else if (msg.includes('online payment is not available') || msg.includes('business info') || msg.includes('stripe_not_ready')) {
        toast.error("Online payment isn't available for this club yet. Please pay in person at the counter.");
      // Half-time pre-order gates. Closed → back to a now-order choice.
      } else if (msg.includes('preorder_closed') || msg.includes('closed for this match') || msg.includes('no match to pre-order')) {
        if (pickupNowEnabled) {
          toast.info(t('preorder.errorClosed'));
        } else {
          // No "now" pick-up to fall back to. In a 'both' section move to seat
          // delivery (as the window-close effect does) so the fan isn't left on
          // a Pick-up that can't be paid — unless a pick-up-only item pins it.
          toast.info(t('ordering.pickupHalfTimeOnlyClosed'));
          if (canFanChoose(orderingMode) && !cartNeedsPickup) setFanFulfillmentChoice('delivery');
        }
        setPickupSlot(null);
      } else if (msg.includes('preorder_disabled')) {
        toast.error(t('preorder.errorDisabled'));
      } else if (msg.includes('preorder_requires_pickup')) {
        toast.error(t('preorder.errorRequiresPickup'));
      } else if (msg.includes('preorder_requires_online')) {
        toast.error(t('preorder.errorRequiresOnline'));
      // Pick-up now was switched off after this page loaded (realtime missed
      // or not yet arrived): patch the settings so the cart re-shapes now.
      } else if (msg.includes('pickup_now_disabled')) {
        toast.error(t('ordering.errorPickupNowDisabled'));
        setLiveSettings(prev => prev ? { ...prev, pickup_now_enabled: false } : prev);
      // A pick-up-only item on a seat-delivery order. Where pick-up exists,
      // switch to it; where it can't (no pick-up / no collection point), name
      // the items instead of pointing at an option the fan doesn't have.
      } else if (msg.includes('delivery_item_not_allowed')) {
        if (pickupBlocked) {
          toast.error(pickupOnlyItems.length > 0
            ? `${pickupOnlyItems.join(', ')}: ${t('ordering.notAvailableInSection')}`
            : t('ordering.notAvailableInSection'));
        } else {
          toast.error(t('ordering.errorDeliveryItemNotAllowed'));
          setFanFulfillmentChoice('pickup');
        }
      } else {
        toast.error(err?.message || 'Failed to submit order');
      }
    } finally {
      initiatingPaymentRef.current = false;            // release double-submit lock
      if (!showPaymentSheet) setSubmitting(false);
    }
  }, [tag, finalClubId, cartCount, cart, cartTotal, cartGrandTotal, fanComments, fanPhone, giveawayEmail, menuItems, event, orderingMode, fanFulfillmentChoice, collectionPoint, t, showPaymentSheet, pickupSlot, pickupBlocked, pickupNowEnabled, cartNeedsPickup]);

  const handlePaymentSuccess = useCallback(() => {
    if (!tag || !paymentOrderId) return;
    // A paid half-time pre-order: hand it to useHalfTimePreorder (optimistic
    // 'scheduled'; it already owns the pre-order pointer written on initiate)
    // and open its detail screen. The now-order state is left untouched.
    if (paymentPickupSlot) {
      const orderItems = buildOrderLines(cart, (id) => knownMenuItemsRef.current.get(id));
      rememberPreorder(paymentOrderId, { items: orderItems, total: cartGrandTotal, collectionPoint, pickupCode: paymentPickupCode });
      setPickupSlot(null);
      setPaymentPickupSlot(null);
      setShowPreorderDetail(true);
      setShowCart(false);
      setShowPaymentSheet(false);
      setPaymentClientSecret(null);
      setPaymentOrderId(null);
      setPaymentPickupCode(null);
      setSubmitting(false);
      // Same as the now-order "placed" effect: the cart and the giveaway entry
      // belong to the order that was just paid.
      setCart(new Map());
      setGiveawayEmail('');
      posthog.capture('fan_order_submitted', fanEventProps({ club_id: finalClubId, total: cartGrandTotal, item_count: cartCount, ordering_mode: 'pickup', payment_mode: 'online', event_id: event?.id, pickup_slot: 'half_time' }, isDemoSeat));
      toast.success(t('preorder.confirmedTitle'));
      return;
    }
    nowOrderConfirmedPaidRef.current = { id: paymentOrderId, confirmedPaidAt: Date.now() };
    localStorage.setItem(`idseat_pending_order_${tag.id}`, paymentOrderId);
    // Persisted too, so a reload before the webhook's revive still treats a
    // stale cancel as provisional (checkPendingOrder restores the ref).
    writeConfirmedPaidAt(`idseat_pending_order_${tag.id}`, paymentOrderId, nowOrderConfirmedPaidRef.current.confirmedPaidAt);
    setPendingOrderId(paymentOrderId);
    setPendingPickupCode(paymentPickupCode);
    setPaymentPickupCode(null);
    setPendingOrderStatus('pending');
    // This callback only fires once Stripe reports the PaymentIntent as
    // 'succeeded', so the payment IS captured — mark it paid immediately so the
    // "View Receipt" button shows right away, instead of waiting for the webhook's
    // realtime update to land (which could otherwise be as late as delivery).
    setPendingPaymentStatus('paid');
    // Display lines for the local state (the server holds the real ones).
    const orderItems = buildOrderLines(cart, (id) => knownMenuItemsRef.current.get(id));
    setPendingOrderItems(orderItems);
    setPendingOrderTotal(cartGrandTotal);
    setOrderSubmitted(true);
    setShowCart(false);
    setShowPaymentSheet(false);
    setPaymentClientSecret(null);
    // Receipt URL is fetched centrally by the useEffect on pendingOrderId.
    setPaymentOrderId(null);
    setSubmitting(false);
    posthog.capture('fan_order_submitted', fanEventProps({ club_id: finalClubId, total: cartGrandTotal, item_count: cartCount, ordering_mode: pendingOrderingMode ?? resolveEffectiveOrderingMode(orderingMode, null), payment_mode: 'online', event_id: event?.id, pickup_slot: null }, isDemoSeat));
    toast.success(t('ordering.orderReceived'));
  }, [tag, paymentOrderId, cart, cartGrandTotal, menuItems, finalClubId, cartCount, orderingMode, event, t, paymentPickupSlot, rememberPreorder, collectionPoint, paymentPickupCode]);

  // Format event date/time
  // Show skeleton while loading (only if we don't have data yet)
  if ((isLoading && !tag) || checkingPresence || (isScreenTag && screenJoin.status === 'checking')) {
    // On mobile: fullscreen skeleton without phone frame
    if (isMobile) {
      return <FanPageSkeleton primaryColor={primaryColor} />;
    }
    // On desktop: skeleton inside phone frame mockup
    return (
      <div className="min-h-screen bg-neutral-900 flex items-center justify-center p-4 md:p-8">
        <div
          className="w-full max-w-[375px] mx-auto rounded-[2rem] overflow-hidden shadow-2xl border-[6px] border-neutral-700 bg-neutral-800"
          style={{ height: '85vh', minHeight: '667px', maxHeight: '844px' }}
        >
          <FanPageSkeleton primaryColor={primaryColor} />
        </div>
      </div>
    );
  }

  // Show error state for stadium slug mismatch (wrong stadium URL)
  if (stadiumSlugMismatch && !overriddenClubId) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: primaryColor }}>
        <div className="text-center text-white">
          <h1 className="text-xl font-bold mb-2">
            {t('error.tagNotFound')}
          </h1>
          <p className="text-white/70">
            {t('error.scanAgain')}
          </p>
        </div>
      </div>
    );
  }

  // Show "Scan Limit Exceeded" or "Re-scan Required" state
  if (isSeatLocked) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 text-center" style={{ backgroundColor: primaryColor }}>
        <div className="max-w-xs">
          <div className="w-20 h-20 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-6">
            <Lock className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-4">
            {t(lockCopyKeys(lockReason).title)}
          </h1>
          <p className="text-white/80 leading-relaxed mb-8">
            {t(lockCopyKeys(lockReason).message)}
          </p>
          <div className="text-white/50 text-xs">
            {lockReason === 'scan_screen' ? t('error.securityRef').replace('409', 'SCAN-SCR') : lockReason === 'scan_required' ? t('error.securityRef').replace('409', 'SCAN-REQ') : t('error.securityRef')}
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if ((isError || !tag) && !overriddenClubId) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: primaryColor }}>
        <div className="text-center text-white">
          <h1 className="text-xl font-bold mb-2">
            {error?.message || t('error.tagNotFound')}
          </h1>
          <p className="text-white/70">
            {t('error.scanAgain')}
          </p>
        </div>
      </div>
    );
  }

  // FAN-PAGE TAKEOVER
  // The club switched the whole web-app off for this match. Everything below —
  // home view, ordering, predictor, match analytics, survey, polls — is
  // suppressed, and fans get one screen with one button.
  //
  // Placed after the error guard so `tag` is guaranteed loaded: the button
  // records which seat asked, and that is the whole point of the screen.
  // Flipping the flag off propagates live via the club_settings realtime merge,
  // so the app comes back without fans needing to reload.
  if ((effectiveSettings as any)?.fan_page_disabled) {
    const takeoverMessage = (effectiveSettings as any)?.fan_page_disabled_message?.trim()
      || 'Sorry, unfortunately there is no in-seat ordering today! The web-app has been disabled for today\u2019s match by the club.';
    const takeoverCtaLabel = (effectiveSettings as any)?.fan_page_disabled_cta_label?.trim()
      || 'I want it back';

    const handleWantsItBack = async () => {
      if (wantsItBack || sendingWantsItBack) return;
      setSendingWantsItBack(true);
      const { error: clickError } = await (supabase as any)
        .from('interactions_fan_page_disabled_clicks')
        .insert({
          club_id: finalClubId,
          stadium_id: tagVenue?.id ?? null,
          event_id: event?.id ?? null,
          seat_id: tag?.seat_id ?? null,
          tag_id: tag?.id ?? null,
          device_id: getDeviceId(),
          user_agent: navigator.userAgent,
        });
      setSendingWantsItBack(false);

      // A screen-tag join that ran out: back to the scan-the-screen lock.
      if (isScreenJoinError(clickError)) { screenJoin.expire(); return; }
      // 23505 = unique violation, i.e. this seat already asked. That is not a
      // failure from the fan's point of view — thank them and move on.
      if (clickError && (clickError as { code?: string }).code !== '23505') {
        toast.error('Could not send that \u2014 please try again');
        return;
      }
      localStorage.setItem(`fan_page_disabled_clicked_${event?.id ?? 'none'}`, '1');
      setWantsItBack(true);
    };

    return (
      <div
        className="min-h-screen flex items-center justify-center p-6 text-center"
        style={{ backgroundColor: primaryColor, fontFamily }}
      >
        <div className="max-w-xs w-full">
          <p className="text-white text-lg leading-relaxed mb-6 whitespace-pre-line">
            {takeoverMessage}
          </p>
          {wantsItBack ? (
            <p className="text-white/80 text-base leading-relaxed">
              Thanks — we’ve passed that on to the club.
            </p>
          ) : (
            <>
              <div className="text-3xl mb-6" aria-hidden="true">👇</div>
              <button
                onClick={handleWantsItBack}
                disabled={sendingWantsItBack}
                className="w-full h-14 text-base font-semibold rounded-xl transition-opacity disabled:opacity-60"
                style={{ backgroundColor: 'white', color: primaryColor }}
              >
                {sendingWantsItBack ? '\u2026' : takeoverCtaLabel}
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  // Parse seat display label for structured display.
  // Defaults are used for the PREVIEW route (Open Live) — keep them looking
  // like a real seat ("Section A3 • Row 3 • Seat 12") so the demo presents well.
  const seatParts = tag?.seat || { section: 'A3', row: '3', seat_number: '12', stand: 'A', stadium: { name: 'Preview Stadium', slug: 'preview-stadium' } };
  // For synthetic preview seats (is_test=true), substitute friendly demo labels
  // at render time only — the DB keeps the literal TEST/T/0 values so they're
  // unambiguous in admin views, logs, and order tables.
  // Only the SYNTHETIC preview seat (literal TEST/T/0 labels) gets friendly demo
  // labels — a real seat flagged as a public demo keeps its actual Section/Row/Seat.
  const isSyntheticSeat = isDemoSeat && seatParts.section === 'TEST';
  const displaySection = isSyntheticSeat
    ? 'A3'
    : (seatParts.section || (seatParts as any).section_name || (seatParts as any).stand || 'A3');
  const displayRow = isSyntheticSeat
    ? '3'
    : (seatParts.row || (seatParts as any).row_name || '3');
  const displaySeatNumber = isSyntheticSeat ? '12' : seatParts.seat_number;

  // Back-to-home handler — shared by the overlay button inside the header and
  // anywhere else that needs to bail out of a sub-view.
  // "Order again" / leaving a finished order: forget it for good (pointer,
  // stamps and state), so neither a reload nor the pill brings it back.
  const dismissPendingOrder = () => {
    if (tag?.id) {
      const key = `idseat_pending_order_${tag.id}`;
      localStorage.removeItem(key);
      clearConfirmedPaidAt(key);
      clearFinishedAt(key);
    }
    setPendingOrderId(null);
    setPendingOrderStatus(null);
    setPendingPaymentStatus(null);
    setPendingOrderItems([]);
    setPendingOrderTotal(0);
    setOrderSubmitted(false);
    setShowCart(false);
    setPendingOrderingMode(null);
    setPendingCollectionPoint(null);
    setPendingGiveawayEntered(false);
  };

  const handleBackToHome = () => {
    setActiveView('home');
    setSurveyStarted(false);
    setSurveyStep(0);
    setPredictorStarted(false);
    setSurveyGate('unknown');
    setShowCart(false);
    setOrderSubmitted(false);
    // Leaving the pre-order detail via the home chip must close it too, or
    // the header strip stays hidden and the next menu visit reopens it.
    setShowPreorderDetail(false);
  };

  // Header with match info and live score — used as the page hero on the home
  // view AND on every sub-view (predictor, survey, ordering, match analytics)
  // so the chrome stays visually identical as fans navigate. Sub-views get a
  // small top-left overlay back button instead of a separate strip so the
  // header height never changes.
  const renderHeader = () => (
    <div
      className="relative px-6 pt-10 pb-8"
      style={{ backgroundColor: '#07080b' }}
    >
      {/* Stadium Background Image */}
      {stadiumImageUrl && (
        <div
          className="absolute inset-0 bg-cover bg-center opacity-65"
          style={{ backgroundImage: `url(${stadiumImageUrl})` }}
        />
      )}
      {/* Club-color radial glow + bottom darkening */}
      <div
        className="absolute inset-0"
        style={{
          background: `radial-gradient(110% 70% at 50% 0%, color-mix(in srgb, ${primaryColor} 55%, transparent), transparent 65%), radial-gradient(80% 60% at 50% 110%, rgba(0,0,0,0.85), transparent 70%), linear-gradient(180deg, rgba(11,13,19,0.4) 0%, rgba(5,6,8,0.9) 100%)`,
        }}
      />
      {/* Scanline texture */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: 'repeating-linear-gradient(0deg, rgba(255,255,255,0.025) 0 1px, transparent 1px 3px)',
          mixBlendMode: 'overlay',
        }}
      />
      {/* Floating back-to-home chip — overlay on the stadium photo so the
          header height never changes between home and sub-views. Renders only
          on sub-views; home shows the seat strip alone. */}
      {activeView !== 'home' && (
        <button
          onClick={handleBackToHome}
          className="absolute top-3 left-3 z-20 inline-flex items-center gap-1 text-white text-[11px] font-medium uppercase tracking-wide px-3 py-1.5 rounded-full bg-black/45 hover:bg-black/65 backdrop-blur-sm transition-colors"
          aria-label={t('common.backToHome')}
        >
          <ChevronLeft className="w-3.5 h-3.5" />
          {t('common.backToHome')}
        </button>
      )}
      {/* Language chip — mirrors the back-to-home chip on the opposite corner.
          Lives in the shared header so a fan can switch language from ANY
          screen (the old home-list button only existed on home — exactly
          where a fan who can't read the page had to find their way back to). */}
      {/* Right-hand chips share one absolutely positioned container so adding
          the report chip changes nothing about the header's height. */}
      <div className="absolute top-3 right-3 z-20 flex items-center gap-1.5">
        {/* Report a problem — on every screen, including every ordering step,
            because that's where things go wrong. Not on a screen tag yet:
            submit-fan-report resolves the venue through the seat, so it can
            only refuse a seatless fan (Release B wires it). */}
        {!isScreenTag && (
          <button
            onClick={() => setShowReportSheet(true)}
            className="inline-flex items-center gap-1.5 text-white text-[11px] font-bold tracking-wide px-3 py-1.5 rounded-full bg-black/45 hover:bg-black/65 backdrop-blur-sm transition-colors"
            aria-label={t('report.title')}
          >
            <LifeBuoy className="w-3.5 h-3.5" />
            {t('report.chip')}
          </button>
        )}
        <button
          onClick={() => setShowLanguageSettings(true)}
          className="inline-flex items-center gap-1.5 text-white text-[11px] font-bold tracking-wide px-3 py-1.5 rounded-full bg-black/45 hover:bg-black/65 backdrop-blur-sm transition-colors"
          aria-label={t('common.language')}
        >
          <Globe className="w-3.5 h-3.5" />
          {locale.toUpperCase()}
        </button>
      </div>
      <div className="relative max-w-md mx-auto min-h-[140px]">
        <div className="flex items-center justify-center gap-6 mb-6 min-h-[96px]">
          {event ? (
            isSportsMatch ? (
              <>
                <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center overflow-hidden">
                  {event.home_team_logo_url ? (
                    <img src={event.home_team_logo_url} alt={event.home_team_name || t('lineups.home')} className="w-12 h-12 object-contain" />
                  ) : (
                    <span className="text-white text-2xl font-bold">{event.home_team_name?.charAt(0).toUpperCase() || 'H'}</span>
                  )}
                </div>
                <Suspense fallback={<div className="w-20 h-[80px] bg-white/10 animate-pulse rounded-lg" />}>
                  <div className="min-w-[80px] min-h-[80px] flex items-center justify-center">
                    <LiveScoreDisplay
                      eventId={event.id}
                      homeTeamName={event.home_team_name}
                      homeTeamLogo={event.home_team_logo_url}
                      awayTeamName={event.away_team_name}
                      awayTeamLogo={event.away_team_logo_url}
                      kickoffDatetime={event.kickoff_datetime}
                      compact
                    />
                  </div>
                </Suspense>
                <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center overflow-hidden">
                  {event.away_team_logo_url ? (
                    <img src={event.away_team_logo_url} alt={event.away_team_name || t('lineups.away')} className="w-12 h-12 object-contain" />
                  ) : (
                    <span className="text-white text-2xl font-bold">{event.away_team_name?.charAt(0).toUpperCase() || 'A'}</span>
                  )}
                </div>
              </>
            ) : (
              <Suspense fallback={<div className="w-full max-w-sm h-20 bg-white/10 animate-pulse rounded-2xl" />}>
                <div className="w-full max-w-sm">
                  <ConcertHeader
                    eventName={event.name}
                    headlinerName={event.headliner_name}
                    supportActs={event.support_acts}
                    doorsOpenDatetime={event.doors_open_datetime}
                    showtimeDatetime={event.kickoff_datetime}
                    posterImageUrl={event.poster_image_url}
                    primaryColor={primaryColor}
                  />
                </div>
              </Suspense>
            )
          ) : (
            <div className="text-white/70 text-sm">{t('common.noActiveMatch')}</div>
          )}
        </div>
        {/* Ticket-stub seat strip — same on home view and every sub-view */}
        <div className="flex items-center justify-center gap-2 text-white/90 text-[11px] font-semibold uppercase tracking-[0.22em] tabular-nums">
          {isScreenTag ? (
            <span>{t('common.screenTag')} <b className="font-bold text-white normal-case tracking-normal">{tag?.label}</b></span>
          ) : (
            <>
              <span>{t('common.section')} <b className="font-bold text-white">{displaySection}</b></span>
              <span className="text-white/40">•</span>
              <span>{t('common.row')} <b className="font-bold text-white">{displayRow}</b></span>
              <span className="text-white/40">•</span>
              <span>{t('common.seat')} <b className="font-bold text-white">{displaySeatNumber}</b></span>
            </>
          )}
        </div>
      </div>
      {/* Half-time pre-order strip — on every screen, so the fan can always
          get back to their pre-order (hidden on its own detail screen). */}
      {preorder.preorder && !showPreorderDetail && (
        <button
          onClick={() => { setActiveView('ordering'); setShowPreorderDetail(true); }}
          className="relative z-10 mt-4 w-full max-w-md mx-auto flex items-center justify-between gap-3 px-4 py-2.5 rounded-xl text-left text-white text-sm font-semibold backdrop-blur-sm transition-colors"
          style={{ backgroundColor: preorder.preorder.status === 'ready_at_collection_point' ? 'rgba(16,185,129,0.85)' : 'rgba(0,0,0,0.45)' }}
          aria-label={t('preorder.halfTime')}
        >
          <span className="flex items-center gap-2 min-w-0">
            <Clock className="w-4 h-4 flex-shrink-0" />
            <span className="truncate">{preorderStripLabel}</span>
          </span>
          <ChevronRight className="w-4 h-4 flex-shrink-0" />
        </button>
      )}
      {/* The now-order's status, on every screen outside the ordering view —
          a fan who went back home (or reloaded onto it) can see and reopen
          their order. */}
      {pendingOrderId && pendingOrderStatus && activeView !== 'ordering' && (() => {
        const { key, tone } = orderPillState(pendingOrderStatus, pendingPaymentStatus);
        return (
          <OrderStatusPill
            label={t(`ordering.pill.${key}`)}
            tone={tone}
            code={pendingPickupCode}
            onOpen={() => { setShowCart(false); setShowPreorderDetail(false); setActiveView('ordering'); }}
          />
        );
      })()}
    </div>
  );

  // Back button is now a floating overlay inside renderHeader (see the chip
  // positioned absolute top-3 left-3). This stays as a no-op so the existing
  // {renderBackButton()} calls in every sub-view keep working without edits.
  const renderBackButton = () => null;

  // Sponsor footer — the shared component renders one logo statically and
  // several as a scrolling ticker, at the club's configured size. The caption
  // colour comes from whatever surface each variant sits on.
  const sponsorLabel = t('common.officialPartners', { defaultValue: 'Official Partners' });
  const renderSponsorFooter = () => (
    <SponsorLogo
      logos={sponsorLogos}
      size={sponsorLogoSize}
      variant="band"
      primaryColor={primaryColor}
      label={sponsorLabel}
      labelColor={inkOn(primaryColor)}
    />
  );

  // The add-ons sheet must render on EVERY screen that can open it — it used
  // to live only in the menu-browse JSX, so the cart's "+" (which asks about
  // add-ons since 8a5a327) set the state and nothing appeared.
  const renderAddonsSheet = () => (
    itemToAddAddons && (
      <AddonsSheet
        key={itemToAddAddons.id}
        item={itemToAddAddons}
        t={t}
        primaryColor={primaryColor}
        initialSelection={addonsPrefill}
        onClose={() => { setItemToAddAddons(null); setAddonsPrefill([]); }}
        onAdd={(selection) => {
          addToCart(itemToAddAddons.id, selection);
          setItemToAddAddons(null);
          setAddonsPrefill([]);
          toast.success(t('ordering.addedToCart'));
        }}
      />
    )
  );

  // Bare variant for the brand-coloured intro/closed/completed screens: a
  // full-width band read as a heavy slab there (client feedback), so the
  // logos sit directly on the page.
  const renderSponsorInline = () => (
    <SponsorLogo
      logos={sponsorLogos}
      size={sponsorLogoSize}
      variant="bare"
      className="py-6"
      label={sponsorLabel}
      labelColor={palette.pageInkSoft}
    />
  );

  // Club's own line on the order-received screens, under the standard message.
  // Rendered from one helper because both of those screens (the optimistic one
  // and the live-status one) must show it — otherwise it flashes away the
  // moment the realtime order row arrives and the page swaps between them.
  // Uses the same translucent-white surface as the total row and the receipt
  // button on those screens, so it reads as an aside on the club's brand colour.
  // Golden banner for a giveaway entry on the order screens; the confetti
  // fires only where `celebrate` is true — the DELIVERED/COLLECTED moment
  // (client decision: celebrate when the food arrives, not when the order is
  // received). Reduced-motion fans get the banner alone.
  const renderGiveawayEnteredBanner = (celebrate = false) => {
    if (!pendingGiveawayEntered) return null;
    return (
      <>
        {/* Plays once per order — the component itself remembers played ids
            (module-level), so parent re-renders can't cancel a burst
            mid-flight and screen swaps can't replay it. */}
        {celebrate && <ConfettiBurst playId={pendingOrderId ?? undefined} />}
        <div
          className="mb-4 p-3 rounded-xl max-w-xs animate-fade-in-up"
          style={{
            background: 'linear-gradient(135deg, rgba(255,215,90,0.16), rgba(255,255,255,0.06))',
            border: '1px solid rgba(255,215,90,0.4)',
          }}
        >
          <p className="text-white text-xs leading-relaxed">
            🎁 <span className="font-bold" style={{ color: '#ffd75a' }}>
              {t('ordering.giveawayEnteredTitle', { defaultValue: "You're in!" })}
            </span>{' '}
            {t('ordering.giveawayEnteredMessage', { defaultValue: "You've also been entered into the giveaway, you'll be contacted by email if you're the winner." })}
          </p>
        </div>
      </>
    );
  };

  const renderOrderReceivedNotice = () => (
    orderReceivedNotice ? (
      <div
        className="w-full max-w-xs px-4 py-3 mb-6 rounded-xl"
        style={{ backgroundColor: 'rgba(255,255,255,0.15)' }}
      >
        <p className="text-white text-sm font-medium leading-relaxed whitespace-pre-line break-words">
          {orderReceivedNotice}
        </p>
      </div>
    ) : null
  );

  // The "ready at the collection point" body — shared by the now-order ready
  // screen and the half-time pre-order detail screen, so the two can't drift.
  const renderCollectionPointReadyBody = (orderCp: CollectionPoint | null) => (
    <>
      <div className="w-20 h-20 rounded-full bg-orange-400/30 flex items-center justify-center mb-6">
        <MapPin className="w-10 h-10 text-white" />
      </div>
      <h1 className="text-2xl font-bold text-white mb-4">
        {t('ordering.orderReadyAtCollectionTitle', { defaultValue: 'Ready for Pick-up! 📍' })}
      </h1>
      {orderCp?.name && (
        <p className="text-white text-lg font-semibold mb-2">{t(`pickup.name.${orderCp.id}`, { defaultValue: orderCp.name })}</p>
      )}
      <p className="text-white/90 text-base leading-relaxed max-w-xs mb-4">
        {orderCp?.instructions_text ? t(`pickup.instructions.${orderCp.id}`, { defaultValue: orderCp.instructions_text }) : t('ordering.orderReadyAtCollectionMessage', { defaultValue: 'Your order is ready. Head to the collection point now.' })}
      </p>
      {orderCp?.phone_number && (
        <a
          href={`tel:${orderCp.phone_number.replace(/\s/g, '')}`}
          className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white/20 text-white font-medium text-sm hover:bg-white/30 active:scale-95 transition-all mb-4"
        >
          <Phone className="w-4 h-4" />
          {t('ordering.callStaff', { defaultValue: 'Call Staff' })}
        </a>
      )}
      {orderCp?.instructions_image_url && (
        <img src={orderCp.instructions_image_url} alt={t(`pickup.name.${orderCp.id}`, { defaultValue: orderCp.name })} className="w-full max-w-xs rounded-xl mb-4 object-cover max-h-40" />
      )}
    </>
  );

  // The order code shown to the fan (and read out at the collection point):
  // the pick-up code, or the #id fallback on the pre-order screens.
  const renderOrderCodeBlock = (code: string) => (
    <div className="w-full max-w-xs mt-6 px-4 py-4 rounded-xl text-center" style={{ backgroundColor: 'rgba(255,255,255,0.15)' }}>
      <p className="text-white/70 text-xs font-semibold uppercase tracking-wide mb-1">{t('preorder.orderCode')}</p>
      <p className="text-white text-3xl font-bold tracking-widest tabular-nums">{code}</p>
    </div>
  );

  // Half-time pre-order detail screen — the tracked pre-order's own status
  // screen, alongside (never replacing) the now-order screens. The back chip
  // only hides it; the terminal bodies' Back buttons drop the pre-order.
  const renderPreorderDetail = () => {
    const p = preorder.preorder;
    if (!p) return null;
    const cp = trackedPreorderPoint;
    const point = trackedPreorderPointName;
    const code = orderCodeLabel({ id: p.id, pickup_code: p.pickupCode });
    // Same reconciliation as pendingServiceFee: total minus itemised lines.
    const subtotal = p.items.reduce((sum, item) => {
      const addons = (item.selected_addons || []).reduce((s: number, a: { price?: number }) => s + (a.price || 0), 0);
      return sum + item.item_price * item.quantity + addons;
    }, 0);
    const serviceFee = Math.max(0, p.total - subtotal);
    const clearPreorder = () => { preorder.clear(); setShowPreorderDetail(false); };

    const itemRows = p.items.length > 0 && (
      <div className="w-full max-w-xs space-y-2 mb-6">
        {p.items.map((item, idx) => {
          const named = { ...item, item_size: item.item_size ?? '' };
          return (
            <OrderLineRow
              key={idx}
              name={getLocalizedMenuItemName(named)}
              size={item.item_size ? getLocalizedMenuItemSize(named) : null}
              addonsLabel={formatAddonsLabel(item.selected_addons)}
              quantity={item.quantity}
              lineTotal={item.item_price * item.quantity}
            />
          );
        })}
      </div>
    );
    const codeBlock = renderOrderCodeBlock(code);
    const receiptButton = p.paymentStatus === 'paid' && (
      <button
        onClick={() => window.open(`/receipt/${p.id}`, '_blank')}
        className="mt-4 px-6 py-2.5 rounded-full text-sm font-medium text-white flex items-center gap-2 transition-colors hover:bg-white/20"
        style={{ backgroundColor: 'rgba(255,255,255,0.15)' }}
      >
        <ExternalLink className="w-4 h-4" />
        {t('receipt.viewReceipt', { defaultValue: 'View Receipt' })}
      </button>
    );
    const backButton = (
      <button
        onClick={clearPreorder}
        className="mt-5 px-6 py-2 rounded-full text-sm font-medium text-white/80 hover:text-white transition-colors"
        style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}
      >
        {t('common.back')}
      </button>
    );

    let body: React.ReactNode;
    if (p.paymentStatus === 'refunded') {
      body = (
        <>
          <div className="w-20 h-20 rounded-full bg-white/20 flex items-center justify-center mb-6">
            <RotateCcw className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-4">
            {t('payment.orderRefundedTitle', { defaultValue: 'Order Refunded' })}
          </h1>
          <p className="text-white/90 text-base leading-relaxed max-w-xs mb-6">
            {t('payment.orderRefundedMessage', { defaultValue: 'Your order has been refunded. The amount will be returned to your payment method within a few business days.' })}
          </p>
          {backButton}
        </>
      );
    } else if (p.status === 'cancelled') {
      body = (
        <>
          <div className="w-20 h-20 rounded-full bg-red-400/30 flex items-center justify-center mb-6">
            <XCircle className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-4">
            {t('payment.orderCancelledTitle', { defaultValue: 'Order Cancelled' })}
          </h1>
          <p className="text-white/90 text-base leading-relaxed max-w-xs mb-6">
            {t('payment.orderCancelledMessage', { defaultValue: 'Your order has been cancelled. No payment was charged.' })}
          </p>
          {backButton}
        </>
      );
    } else if (p.status === 'scheduled') {
      body = (
        <>
          <div className="w-20 h-20 rounded-full bg-emerald-500 shadow-lg flex items-center justify-center mb-6 animate-pop-in">
            <Check className="w-10 h-10 text-white" strokeWidth={3} />
          </div>
          <h1 className="text-2xl font-bold text-white mb-4">{t('preorder.confirmedTitle')}</h1>
          <p className="text-white/90 text-base leading-relaxed max-w-xs mb-6">
            {t('preorder.confirmedMessage', { point })}
          </p>
          {itemRows}
          {serviceFee > 0.005 && (
            <div className="flex items-center justify-between w-full max-w-xs px-4 py-2 mb-2 text-white/80 text-sm">
              <span>{t('ordering.serviceFee', { defaultValue: 'Service fee' })}</span>
              <span className="tabular-nums">€{serviceFee.toFixed(2)}</span>
            </div>
          )}
          <div className="flex items-center justify-between w-full max-w-xs px-4 py-3 rounded-lg" style={{ backgroundColor: 'rgba(255,255,255,0.15)' }}>
            <span className="text-white font-bold">{t('ordering.total')}</span>
            <span className="text-white font-bold">€{p.total.toFixed(2)}</span>
          </div>
          {codeBlock}
          {cp?.instructions_text && (
            <p className="text-white/90 text-sm leading-relaxed max-w-xs mt-6">
              {t(`pickup.instructions.${cp.id}`, { defaultValue: cp.instructions_text })}
            </p>
          )}
          {cp?.phone_number && (
            <a
              href={`tel:${cp.phone_number.replace(/\s/g, '')}`}
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white/20 text-white font-medium text-sm hover:bg-white/30 active:scale-95 transition-all mt-4"
            >
              <Phone className="w-4 h-4" />
              {t('ordering.callStaff', { defaultValue: 'Call Staff' })}
            </a>
          )}
          {receiptButton}
        </>
      );
    } else if (p.status === 'pending') {
      body = (
        <>
          <div className="w-20 h-20 rounded-full bg-white/20 flex items-center justify-center mb-6">
            <Loader2 className="w-10 h-10 text-white animate-spin" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-4">{t('preorder.preparingTitle')}</h1>
          <p className="text-white/90 text-base leading-relaxed max-w-xs mb-6">
            {t('preorder.preparingMessage', { point })}
          </p>
          {itemRows}
          {codeBlock}
        </>
      );
    } else if (p.status === 'ready_at_collection_point') {
      body = (
        <>
          {renderCollectionPointReadyBody(cp)}
          {codeBlock}
        </>
      );
    } else if (p.status === 'collected') {
      body = (
        <>
          <div className="w-20 h-20 rounded-full bg-emerald-500 shadow-lg flex items-center justify-center mb-6 animate-pop-in">
            <Check className="w-10 h-10 text-white" strokeWidth={3} />
          </div>
          <h1 className="text-2xl font-bold text-white mb-4">
            {t('ordering.orderCollected', { defaultValue: 'Order Collected!' })}
          </h1>
          <p className="text-white/90 text-base leading-relaxed max-w-xs mb-6">
            {t('ordering.orderCollectedMessage', { defaultValue: 'Your order has been picked up. Enjoy!' })}
          </p>
          {receiptButton}
          {backButton}
        </>
      );
    } else {
      // pending_payment: the webhook hasn't confirmed the payment yet.
      body = (
        <>
          <div className="w-20 h-20 rounded-full bg-white/20 flex items-center justify-center mb-6">
            <Loader2 className="w-10 h-10 text-white animate-spin" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-4">
            {t('ordering.processingPayment', { defaultValue: 'Processing payment…' })}
          </h1>
          <p className="text-white/90 text-base leading-relaxed max-w-xs mb-6">
            {t('ordering.processingPaymentMessage', { defaultValue: 'We\'re confirming your payment with the provider. This usually takes a few seconds.' })}
          </p>
        </>
      );
    }

    return (
      <div className="min-h-screen flex flex-col w-full" style={{ fontFamily }}>
        {renderHeader()}
        <button onClick={() => setShowPreorderDetail(false)} className="flex items-center gap-1 text-white/90 text-sm py-3 px-4 hover:text-white transition-colors" style={{ backgroundColor: 'rgba(0,0,0,0.3)' }}><ChevronLeft className="w-4 h-4" /> {t('common.back')}</button>
        <div className="flex-1 flex flex-col" style={{ backgroundColor: primaryColor }}>
          <div className="flex-1 flex flex-col items-center justify-center px-8 py-12 text-center">
            {body}
          </div>
          {renderSponsorFooter()}
        </div>
      </div>
    );
  };

  // HOME VIEW
  const renderHomeView = () => (
    <div className="min-h-screen flex flex-col w-full" style={{ fontFamily, backgroundColor: '#07080b' }}>
      {/* Shared hero — same component used by every sub-view so navigating
          between Order / Predict / Match Analytics / Survey looks consistent. */}
      {renderHeader()}

      {/* Action Buttons */}
      <div
        className="flex-1 px-6 py-6"
        style={{
          backgroundColor: primaryColor,
          borderTopLeftRadius: '24px',
          borderTopRightRadius: '24px',
          marginTop: '-12px',
          position: 'relative',
          zIndex: 10,
          // Expose primary_color as CSS var so child ActionButtons can tint their icon chips
          ['--club-color' as any]: primaryColor,
          // …and a legible version of it for the glyph itself. The raw brand
          // colour on the near-white chip is unreadable for a light club colour
          // (Aris F.C.'s yellow measures 1.9:1); this keeps the hue and walks
          // the lightness until it clears 4.5:1.
          ['--club-icon' as any]: palette.iconInk,
        } as React.CSSProperties}
      >
        <div className="max-w-md mx-auto space-y-3">
          {/*
            Fan action blocks render in the club's saved order
            (club_settings.block_order). Each block keeps its exact markup,
            props, gating conditions, handlers and keys — ONLY the emit order
            changes. `header` is the hero above and is not part of this region.

            SAFETY: when block_order is null/undefined (current reality until the
            migration lands) we fall back to the exact current source order
            below, so the null path renders byte-for-byte identical to before.
            resolveBlockOrder is used only when a saved order actually exists.

            The visibility gates below are MIRRORED in src/lib/fanPageLanding.ts
            (homeBlockVisibility) for the ordering-only landing — keep both in sync.
          */}
          {(() => {
            const ctaBlock = (
              <>
                {/* CTA Buttons */}
                {/* Same visibility rule as the club portal preview (visibleCtaButtons),
                    including the club-level switch. */}
                {visibleCtaButtons(effectiveCtaButtons, effectiveSettings?.cta_enabled).map((button) => {
                  // Preset icon → uploaded image → default (same rule as FanPagePreview).
                  const PresetIcon = ctaIcon(button.icon);
                  return (
                  <ActionButton
                    key={button.id}
                    icon={PresetIcon ? (
                      <PresetIcon className="w-5 h-5" />
                    ) : button.icon_url ? (
                      <img src={button.icon_url} alt="" className="w-5 h-5 object-contain" />
                    ) : (
                      <Gift className="w-5 h-5" />
                    )}
                    label={t(`cta.${button.id}`) !== `cta.${button.id}` ? t(`cta.${button.id}`) : button.button_text}
                    onClick={handleCtaClick(button)}
                  />
                  );
                })}
              </>
            );

            const matchAnalyticsBlock = (
              /* Match analytics — only for sports-match events sourced from
                  SportMonks AND when the club admin has the Match Analytics toggle
                  on. Off → button (and the whole sub-view) is hidden. */
              isSportsMatch && event?.id && event.sportsmonks_fixture_id && effectiveSettings?.match_analytics_enabled && (
                <ActionButton
                  icon={<Activity className="w-5 h-5" />}
                  label={t('match.title', { defaultValue: 'Match Analytics' })}
                  onClick={() => setActiveView('match')}
                />
              )
            );

            const predictorBlock = (
              /* Score Predictor */
              isSportsMatch && effectiveSettings?.predictor_enabled && (
                <div className="relative">
                  <ActionButton
                    icon={effectiveSettings.predictor_icon_url ? (
                      <img src={effectiveSettings.predictor_icon_url} alt="" className="w-5 h-5 object-contain" />
                    ) : (
                      <Swords className="w-5 h-5" />
                    )}
                    label={t('predictor.title')}
                    onClick={() => {
                      if (!isFanInMatchWindow) {
                        toast.info(t('predictor.openingSoon'));
                        return;
                      }
                      setActiveView('predictor');
                    }}
                    className={cn(!isFanInMatchWindow && "opacity-60")}
                  />
                  {!isFanInMatchWindow && (
                    <div className="absolute top-1/2 -translate-y-1/2 right-4 flex items-center gap-1.5 px-2 py-1 bg-black/20 rounded text-[10px] font-bold text-white uppercase tracking-wider backdrop-blur-sm pointer-events-none">
                      <Lock className="w-2.5 h-2.5" />
                      {t('common.locked')}
                    </div>
                  )}
                </div>
              )
            );

            const surveyBlock = (
              /* Survey */
              !isLoading && effectiveSettings?.survey_enabled && effectiveSurvey && (effectiveSurvey.survey_questions?.length ?? 0) > 0 && (
                <div className="relative">
                  <ActionButton
                    icon={effectiveSettings.survey_icon_url ? (
                      <img src={effectiveSettings.survey_icon_url} alt="" className="w-5 h-5 object-contain" />
                    ) : (
                      <MessageSquare className="w-5 h-5" />
                    )}
                    label={getSurveyTitle(effectiveSurvey)}
                    onClick={() => {
                      if (!isFanInMatchWindow) {
                        toast.info(t('survey.openingSoon'));
                        return;
                      }
                      setSurveyGate('checking');
                      setActiveView('survey');
                    }}
                    className={cn(!isFanInMatchWindow && "opacity-60")}
                  />
                  {!isFanInMatchWindow && (
                    <div className="absolute top-1/2 -translate-y-1/2 right-4 flex items-center gap-1.5 px-2 py-1 bg-black/20 rounded text-[10px] font-bold text-white uppercase tracking-wider backdrop-blur-sm pointer-events-none">
                      <Lock className="w-2.5 h-2.5" />
                      {t('common.locked')}
                    </div>
                  )}
                </div>
              )
            );

            const pollBlock = (
              /* Live polls — one button per OPEN poll (a club can run several
                 at once). Hidden entirely when none is open, never greyed. Not
                 match-window-locked: purely club-controlled / auto open/close. */
              openPolls.length > 0 && (
                <Fragment>
                  {openPolls.map((p) => (
                    <ActionButton
                      key={p.id}
                      icon={<BarChart3 className="w-5 h-5" />}
                      label={t(`poll.title.${p.id}`, { defaultValue: p.title || t('poll.title') })}
                      onClick={() => openPoll(p.id)}
                    />
                  ))}
                </Fragment>
              )
            );

            // Lineups live inside Match Analytics → Lineup tab. The
            // `match_analytics_enabled` toggle on club_settings hides the whole
            // Match Analytics button when off.

            // Paused = temporarily not taking NEW orders (button stays visible,
            // mirroring the match-window lock treatment). Distinct from
            // ordering_enabled which hides the block entirely.
            const orderingBlocked = !isFanInMatchWindow || orderingPaused;
            const orderingBlock = (
              /* Ordering */
              effectiveSettings?.ordering_enabled && (
                <div className="relative">
                  <ActionButton
                    icon={<ShoppingBag className="w-5 h-5" />}
                    label={t(menuTitleKey(orderingMode))}
                    onClick={() => {
                      if (orderingPaused) {
                        toast.info(t('ordering.paused'));
                        return;
                      }
                      if (!isFanInMatchWindow) {
                        toast.info(t('ordering.openingSoon'));
                        return;
                      }
                      setCart(new Map());
                      setShowCart(false);
                      setActiveView('ordering');
                    }}
                    className={cn(orderingBlocked && "opacity-60")}
                  />
                  {orderingBlocked && (
                    <div className="absolute top-1/2 -translate-y-1/2 right-4 flex items-center gap-1.5 px-2 py-1 bg-black/20 rounded text-[10px] font-bold text-white uppercase tracking-wider backdrop-blur-sm pointer-events-none">
                      <Lock className="w-2.5 h-2.5" />
                      {orderingPaused ? t('ordering.pausedBadge') : t('common.locked')}
                    </div>
                  )}
                </div>
              )
            );

            const actionBlocks: Record<BlockKey, React.ReactNode> = {
              cta: ctaBlock,
              match_analytics: matchAnalyticsBlock,
              predictor: predictorBlock,
              survey: surveyBlock,
              poll: pollBlock,
              ordering: orderingBlock,
            };

            // Current hardcoded source order — used verbatim when no saved order
            // exists so the default render is byte-for-byte identical to before.
            const DEFAULT_ACTION_ORDER: BlockKey[] = [
              'cta', 'match_analytics', 'predictor', 'survey', 'poll', 'ordering',
            ];

            const savedOrder = effectiveSettings?.block_order;
            const actionOrder = savedOrder
              ? resolveBlockOrder(savedOrder)
              : DEFAULT_ACTION_ORDER;

            return actionOrder.map((key) => (
              <Fragment key={key}>{actionBlocks[key]}</Fragment>
            ));
          })()}

        </div>

        {/* Sponsor Footer — bare variant: home has no coloured band. Height
            now matches every other screen (40px base × the club's size
            setting); it used to be 20% larger here, unified deliberately. */}
        <SponsorLogo
          logos={sponsorLogos}
          size={sponsorLogoSize}
          variant="bare"
          className="mt-12 pb-4"
          label={sponsorLabel}
          labelColor={palette.pageInkSoft}
        />

        {/* Legal Footer — gets its own breathing room from the action stack.
            When the sponsor logo is rendered above it already supplies the
            margin; without a sponsor, mt-12 keeps "POWERED BY IDSEAT" from
            sitting right under the last action button. */}
        <div className={cn("pb-8 flex flex-col items-center gap-2 text-center", sponsorLogos.length === 0 && "mt-12")}>
          {/* Legal links — typeface inherits from club's font_family. */}
          {(effectiveSettings?.privacy_policy_url || effectiveSettings?.terms_conditions_url) && (
            <div
              className="flex items-center gap-6 text-[10px] font-semibold uppercase tracking-[0.24em]"
              style={{ color: effectiveSettings?.footer_text_color || '#ffffff' }}
            >
              {effectiveSettings?.privacy_policy_url && (
                <a
                  href={effectiveSettings.privacy_policy_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="border-b border-dashed border-current pb-px hover:opacity-90"
                >
                  {t('common.privacyPolicy')}
                </a>
              )}
              {effectiveSettings?.terms_conditions_url && (
                <a
                  href={effectiveSettings.terms_conditions_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="border-b border-dashed border-current pb-px hover:opacity-90"
                >
                  {t('common.termsConditions')}
                </a>
              )}
            </div>
          )}
          <div
            className="text-[10px] font-semibold uppercase tracking-[0.28em]"
            style={{ color: effectiveSettings?.footer_text_color || '#ffffff', opacity: 0.5 }}
          >
            {t('common.poweredBy')}
          </div>
        </div>
      </div>
    </div>
  );

  // PREDICTOR VIEW
  const renderPredictorView = () => {
    const hasPrizes = predictorPrizes.length >= 1;

    // Predictions closed screen (match has started, or the server rejected a
    // submit as closed while the client still believed it was open)
    if ((isPredictorClosed || predictorServerClosed) && !predictorSubmitted && !predictorAlreadyCompleted) {
      return (
        <div className="min-h-screen flex flex-col w-full" style={{ fontFamily }}>
          {renderHeader()}
          {renderBackButton()}

          <div className="flex-1 flex flex-col bg-neutral-100">
            <div className="flex-1 flex flex-col items-center justify-center px-8 py-12">
              <div className="w-20 h-20 rounded-full bg-orange-100 flex items-center justify-center mb-6">
                <Swords className="w-10 h-10 text-orange-600" />
              </div>
              <h1 className="text-2xl font-bold text-neutral-900 mb-4 text-center" style={{ fontFamily }}>{t('predictor.closedTitle')}</h1>
              <p className="text-neutral-600 text-center leading-relaxed text-lg">
                {t('predictor.closedMessage')}
              </p>
            </div>
            {renderSponsorInline()}
          </div>
        </div>
      );
    }

    // Already completed screen
    if (predictorAlreadyCompleted && !predictorSubmitted) {
      return (
        <div className="min-h-screen flex flex-col w-full" style={{ fontFamily }}>
          {renderHeader()}
          {renderBackButton()}

          <div className="flex-1 flex flex-col bg-neutral-100">
            <div className="flex-1 flex flex-col items-center justify-center px-8 py-12">
              <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mb-6">
                <Check className="w-10 h-10 text-green-600" />
              </div>
              <h1 className="text-2xl font-bold text-neutral-900 mb-4 text-center" style={{ fontFamily }}>{t('predictor.alreadySubmittedTitle')}</h1>
              <p className="text-neutral-600 text-center leading-relaxed text-lg">
                {t('predictor.alreadySubmittedMessage')}
              </p>
            </div>
            {renderSponsorInline()}
          </div>
        </div>
      );
    }

    // Predictor intro screen
    if (!predictorStarted && !predictorSubmitted && !showPredictorWheel) {
      return (
        <div className="min-h-screen flex flex-col w-full" style={{ fontFamily }}>
          {renderHeader()}
          {renderBackButton()}

          {/* Brand-coloured like the ordering view (client feedback) — text and
              chips take their colours from the computed fan palette. */}
          <div className="flex-1 flex flex-col" style={{ background: palette.bg }}>
            {/* Large icon and prize wheel preview */}
            <div className="flex flex-col items-center px-8 pt-10 pb-4">
              <Swords className="w-16 h-16 mb-4" style={{ color: palette.pageInk }} strokeWidth={1.5} />
              <h1 className="text-2xl font-bold mb-4 text-center" style={{ fontFamily, color: palette.pageInk }}>{t('predictor.title')}</h1>
              <p className="text-center leading-relaxed text-lg" style={{ color: palette.pageInkSoft }}>
                {hasPrizes ? t('predictor.introWithPrizes') : t('predictor.introNoPrizes')}
              </p>

              {/* Mini prize wheel preview */}
              {hasPrizes && (
                <div className="mt-8 flex flex-wrap justify-center gap-2 max-w-xs">
                  {predictorPrizes.slice(0, 6).map((prize) => (
                    <span
                      key={prize.id}
                      className="px-3 py-1.5 rounded-full text-sm font-medium shadow-sm"
                      style={{ background: palette.cardBg, color: palette.ink, border: `1px solid ${palette.cardBorder}` }}
                    >
                      {t(`prize.${prize.id}`) !== `prize.${prize.id}` ? t(`prize.${prize.id}`) : prize.name}
                    </span>
                  ))}
                  {predictorPrizes.length > 6 && (
                    <span
                      className="px-3 py-1.5 rounded-full text-sm font-medium"
                      style={{ background: palette.cardBg, color: palette.inkSoft, border: `1px solid ${palette.cardBorder}` }}
                    >
                      +{predictorPrizes.length - 6} {t('prizes.more')}
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Get Started button */}
            <div className="px-6 py-6">
              <Button
                onClick={() => setPredictorStarted(true)}
                className="w-full h-14 text-base font-semibold rounded-xl border-0 hover:opacity-90"
                style={{ backgroundColor: palette.accent, color: palette.accentInk }}
              >
                {t('common.getStarted')}
              </Button>
            </div>
            {renderSponsorInline()}
          </div>
        </div>
      );
    }

    const isPredictorEmailValid = isValidEmail(predictorEmail);

    return (
      <div className="min-h-screen flex flex-col w-full" style={{ fontFamily }}>
        {renderHeader()}
        {renderBackButton()}

        <div className="flex-1 flex flex-col" style={{ backgroundColor: primaryColor }}>
          <div className="px-6 pt-6 pb-4 text-center">
            <h1 className="text-white text-xl font-bold uppercase tracking-wide mb-2" style={{ fontFamily }}>{t('predictor.title')}</h1>
          </div>

          {/* Prize wheel after submission */}
          {predictorSubmitted && showPredictorWheel && !predictorPrizeRevealed && hasPrizes ? (
            <div className="flex-1 flex flex-col items-center justify-center px-6 pb-8">
              <h2 className="text-white text-xl font-bold mb-6">{t('predictor.spinToWin')}</h2>
              <Suspense fallback={<div className="flex flex-col items-center justify-center p-8"><Loader2 className="w-8 h-8 animate-spin text-white/50" /></div>}>
                <PrizeWheel
                  prizes={predictorPrizes}
                  primaryColor={primaryColor}
                  translatePrize={getPrizeLabel}
                  onSpinComplete={(prize) => {
                    setPredictorWonPrize(prize);
                    setTimeout(() => setPredictorPrizeRevealed(true), 500);
                    // Record prize win in DB
                    if (predictorInteractionId) {
                      supabase.from('interactions_score_predictions')
                        .update({
                          won_prize_id: prize.id,
                          won_prize_name: getPrizeLabel(prize)
                        })
                        .eq('id', predictorInteractionId)
                        .then(({ error }) => {
                          if (error) console.error('Error saving winner prize:', error);
                        });
                    }
                  }}
                />
              </Suspense>
            </div>
          ) : predictorSubmitted && predictorPrizeRevealed && predictorWonPrize ? (
            <div className="flex-1 flex flex-col items-center justify-center px-6 pb-8 text-center">
              <div className="w-20 h-20 rounded-full bg-white/20 flex items-center justify-center mb-4">
                <Gift className="w-10 h-10 text-white" />
              </div>
              <p className="font-bold text-white text-lg mb-4">{getPrizeResultText(predictorWonPrize)}</p>
              <p className="text-white/90 text-base leading-relaxed max-w-xs">
                {t('predictor.prizeWinMessage')}
              </p>
            </div>
          ) : predictorSubmitted ? (
            <div className="flex-1 flex flex-col items-center justify-center px-6 pb-8">
              <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center mb-4">
                <Check className="w-8 h-8 text-white" />
              </div>
              <p className="font-medium text-white text-lg">{t('predictor.submitted')}</p>
              <p className="text-white/70 mt-1">{homeScore} - {awayScore}</p>
            </div>
          ) : (
            <>
              {/* Score Input Card */}
              <div className="px-6 pb-4">
                <div
                  className="rounded-2xl p-6"
                  style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}
                >
                  <div className="flex items-center justify-center gap-4 mb-6">
                    {/* Home team logo */}
                    <TeamLogo
                      src={event?.home_team_logo_url}
                      name={event?.home_team_name}
                      className="w-16 h-16"
                      imgClassName="w-14 h-14"
                    />

                    <span className="text-white text-2xl font-bold">-</span>

                    {/* Away team logo */}
                    <TeamLogo
                      src={event?.away_team_logo_url}
                      name={event?.away_team_name}
                      className="w-16 h-16"
                      imgClassName="w-14 h-14"
                    />
                  </div>

                  {/* Score inputs - using text type to avoid leading zeros */}
                  <div className="flex items-center justify-center gap-6">
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={2}
                      value={homeScore}
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, '');
                        if (val === '' || parseInt(val) <= 20) {
                          setHomeScore(val);
                        }
                      }}
                      placeholder="0"
                      className="w-20 h-20 text-center text-3xl font-bold rounded-xl focus:outline-none focus:ring-2 focus:ring-white/50 placeholder:text-white/50"
                      style={{ backgroundColor: 'rgba(255,255,255,0.15)', color: 'white' }}
                    />
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={2}
                      value={awayScore}
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, '');
                        if (val === '' || parseInt(val) <= 20) {
                          setAwayScore(val);
                        }
                      }}
                      placeholder="0"
                      className="w-20 h-20 text-center text-3xl font-bold rounded-xl focus:outline-none focus:ring-2 focus:ring-white/50 placeholder:text-white/50"
                      style={{ backgroundColor: 'rgba(255,255,255,0.15)', color: 'white' }}
                    />
                  </div>
                </div>
              </div>

              {/* Email and consent */}
              <div className="px-6 pb-4 space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="predictor-email" className="text-white/90 font-medium">
                    {t('common.email')} <span className="text-red-300">*</span>
                  </Label>
                  <Input
                    id="predictor-email"
                    type="email"
                    placeholder="your@email.com"
                    value={predictorEmail}
                    onChange={(e) => setPredictorEmail(e.target.value)}
                    className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
                  />
                </div>

                {/* Terms & Privacy consent - only show if links are configured */}
                {(effectiveSettings?.privacy_policy_url || effectiveSettings?.terms_conditions_url) && (
                  <div className="flex items-start space-x-3">
                    <Checkbox
                      id="predictor-terms"
                      checked={predictorTermsConsent}
                      onCheckedChange={(checked) => setPredictorTermsConsent(checked === true)}
                      className="border-white/50 data-[state=checked]:bg-white data-[state=checked]:text-black mt-0.5"
                    />
                    <Label htmlFor="predictor-terms" className="text-sm text-white/70 font-normal cursor-pointer leading-tight">
                      {t('consent.termsPrefix')} {clubName || 'the club'}'s{' '}
                      {effectiveSettings?.terms_conditions_url && (
                        <a
                          href={effectiveSettings.terms_conditions_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="underline text-white/90 hover:text-white"
                        >
                          {t('common.termsConditions')}
                        </a>
                      )}
                      {effectiveSettings?.terms_conditions_url && effectiveSettings?.privacy_policy_url && ` ${t('consent.and')} `}
                      {effectiveSettings?.privacy_policy_url && (
                        <a
                          href={effectiveSettings.privacy_policy_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="underline text-white/90 hover:text-white"
                        >
                          {t('common.privacyPolicy')}
                        </a>
                      )}
                      {' '}{t('consent.termsSuffix')}
                    </Label>
                  </div>
                )}

                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="predictor-marketing"
                    checked={predictorMarketingConsent}
                    onCheckedChange={(checked) => setPredictorMarketingConsent(checked === true)}
                    className="border-white/50 data-[state=checked]:bg-white data-[state=checked]:text-black"
                  />
                  <Label htmlFor="predictor-marketing" className="text-sm text-white/70 font-normal cursor-pointer leading-tight">
                    {t('consent.marketing', { clubName: clubName || 'the club' })}
                  </Label>
                </div>
              </div>

              {/* Submit button - fixed at bottom, highlights when email is valid */}
              <div className="mt-auto px-6 pb-4">
                <Button
                  onClick={handlePredictorSubmit}
                  className={cn(
                    "w-full h-14 text-base font-semibold rounded-xl transition-all duration-200",
                    isPredictorEmailValid
                      ? "shadow-lg scale-[1.02]"
                      : "opacity-70"
                  )}
                  style={{
                    backgroundColor: isPredictorEmailValid ? 'white' : 'rgba(255,255,255,0.7)',
                    color: primaryColor
                  }}
                  disabled={submitting}
                >
                  {submitting ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <>
                      <Swords className="w-5 h-5 mr-2" />
                      {t('predictor.submitButton')}
                    </>
                  )}
                </Button>
              </div>
            </>
          )}

          {renderSponsorFooter()}
        </div>
      </div>
    );
  };

  // SURVEY VIEW
  const renderSurveyView = () => {
    if (!effectiveSurvey) return null;
    const hasPrizes = surveyPrizes.length >= 1;

    // HARD BLOCK: If gate is 'unknown' or 'checking', show loading spinner.
    // This guarantees we never flash the intro/form before server check completes.
    if (surveyGate === 'unknown' || surveyGate === 'checking') {
      return (
        <div className="min-h-screen flex flex-col w-full" style={{ fontFamily }}>
          {renderHeader()}
          {renderBackButton()}
          <div className="flex-1 flex items-center justify-center bg-neutral-100">
            <Loader2 className="h-6 w-6 animate-spin text-neutral-700" />
          </div>
        </div>
      );
    }

    // Survey closed screen (30 minutes after match finished)
    if (surveyClosed && !surveySubmitted && !surveyAlreadyCompleted) {
      return (
        <div className="min-h-screen flex flex-col w-full" style={{ fontFamily }}>
          {renderHeader()}
          {renderBackButton()}

          <div className="flex-1 flex flex-col bg-neutral-100">
            <div className="flex-1 flex flex-col items-center justify-center px-8 py-12">
              <div className="w-20 h-20 rounded-full bg-orange-100 flex items-center justify-center mb-6">
                <MessageSquare className="w-10 h-10 text-orange-600" />
              </div>
              <h1 className="text-2xl font-bold text-neutral-900 mb-4 text-center" style={{ fontFamily }}>{t('survey.closedTitle')}</h1>
              <p className="text-neutral-600 text-center leading-relaxed text-lg">
                {t('survey.closedMessage')}
              </p>
            </div>
            {renderSponsorInline()}
          </div>
        </div>
      );
    }

    // Already completed screen
    if (surveyAlreadyCompleted && !surveySubmitted) {
      return (
        <div className="min-h-screen flex flex-col w-full" style={{ fontFamily }}>
          {renderHeader()}
          {renderBackButton()}

          <div className="flex-1 flex flex-col bg-neutral-100">
            <div className="flex-1 flex flex-col items-center justify-center px-8 py-12">
              <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mb-6">
                <Check className="w-10 h-10 text-green-600" />
              </div>
              <h1 className="text-2xl font-bold text-neutral-900 mb-4 text-center" style={{ fontFamily }}>{t('survey.alreadyCompletedTitle')}</h1>
              <p className="text-neutral-600 text-center leading-relaxed text-lg">
                {t('survey.alreadyCompletedMessage')}
              </p>
            </div>
            {renderSponsorInline()}
          </div>
        </div>
      );
    }

    // Survey intro screen
    if (!surveyStarted && !surveySubmitted && !showSurveyWheel) {
      return (
        <div className="min-h-screen flex flex-col w-full" style={{ fontFamily }}>
          {renderHeader()}
          {renderBackButton()}

          {/* Brand-coloured like the ordering view (client feedback) — text and
              chips take their colours from the computed fan palette. */}
          <div className="flex-1 flex flex-col" style={{ background: palette.bg }}>
            {/* Large icon */}
            <div className="flex flex-col items-center px-8 pt-10 pb-4">
              <Gift className="w-16 h-16 mb-4" style={{ color: palette.pageInk }} strokeWidth={1.5} />
              <h1 className="text-2xl font-bold mb-4 text-center" style={{ fontFamily, color: palette.pageInk }}>{getSurveyTitle(effectiveSurvey)}</h1>
              <p className="text-center leading-relaxed text-lg" style={{ color: palette.pageInkSoft }}>
                {hasPrizes ? t('survey.introWithPrizes') : t('survey.introNoPrizes')}
              </p>

              {/* Mini prize preview */}
              {hasPrizes && (
                <div className="mt-8 flex flex-wrap justify-center gap-2 max-w-xs">
                  {surveyPrizes.slice(0, 6).map((prize) => (
                    <span
                      key={prize.id}
                      className="px-3 py-1.5 rounded-full text-sm font-medium shadow-sm"
                      style={{ background: palette.cardBg, color: palette.ink, border: `1px solid ${palette.cardBorder}` }}
                    >
                      {t(`prize.${prize.id}`) !== `prize.${prize.id}` ? t(`prize.${prize.id}`) : prize.name}
                    </span>
                  ))}
                  {surveyPrizes.length > 6 && (
                    <span
                      className="px-3 py-1.5 rounded-full text-sm font-medium"
                      style={{ background: palette.cardBg, color: palette.inkSoft, border: `1px solid ${palette.cardBorder}` }}
                    >
                      +{surveyPrizes.length - 6} {t('prizes.more')}
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Get Started button - the gate should already be 'open' if we reached here */}
            <div className="px-6 py-6">
              <Button
                onClick={() => {
                  setSurveyStarted(true);
                  setSurveyStep(0);
                }}
                className="w-full h-14 text-base font-semibold rounded-xl border-0 hover:opacity-90"
                style={{ backgroundColor: palette.accent, color: palette.accentInk }}
              >
                {t('common.getStarted')}
              </Button>
            </div>
            {renderSponsorInline()}
          </div>
        </div>
      );
    }

    const isSurveyEmailValid = isValidEmail(surveyEmail);
    const questions = effectiveSurvey?.survey_questions ?? [];
    const totalSteps = questions.length + 1; // questions + email/consent page
    const currentQuestion = surveyStep < questions.length
      ? questions[surveyStep]
      : null;

    // Check if current question step is answered (for Next button validation)
    const isCurrentStepAnswered = (() => {
      if (!currentQuestion) return true; // email/consent page
      if (!currentQuestion.required) return true;
      const response = surveyResponses[currentQuestion.id];
      return response !== undefined && response !== '';
    })();

    // Survey form
    return (
      <div className="min-h-screen flex flex-col w-full" style={{ fontFamily }}>
        {renderHeader()}
        {renderBackButton()}

        <div className="flex-1 flex flex-col" style={{ backgroundColor: primaryColor }}>
          {/* Progress indicator */}
          <div className="px-6 pt-5 pb-2">
            <div className="flex items-center justify-between mb-2">
              {surveyStep > 0 && (
                <button
                  onClick={() => setSurveyStep(s => s - 1)}
                  className="flex items-center gap-1 text-white/80 text-sm hover:text-white transition-colors"
                >
                  {t('common.back')}
                </button>
              )}
              <span className={cn("text-white/90 text-sm font-medium", surveyStep === 0 && "ml-auto")}>
                {surveyStep + 1}/{totalSteps}
              </span>
            </div>
            <div className="w-full h-1.5 rounded-full bg-white/20 overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-300 ease-out"
                style={{
                  width: `${((surveyStep + 1) / totalSteps) * 100}%`,
                  backgroundColor: 'white'
                }}
              />
            </div>
          </div>

          <div className="px-6 pt-2 pb-4 text-center">
            <h1 className="text-white text-xl font-bold uppercase tracking-wide" style={{ fontFamily }}>{getSurveyTitle(effectiveSurvey)}</h1>
          </div>

          {/* Prize wheel after submission */}
          {surveySubmitted && showSurveyWheel && !surveyPrizeRevealed && hasPrizes ? (
            <div className="flex-1 flex flex-col items-center justify-center px-6 pb-8">
              <h2 className="text-white text-xl font-bold mb-6">{t('predictor.spinToWin')}</h2>
              <Suspense fallback={<div className="flex flex-col items-center justify-center p-8"><Loader2 className="w-8 h-8 animate-spin text-white/50" /></div>}>
                <PrizeWheel
                  prizes={surveyPrizes}
                  primaryColor={primaryColor}
                  translatePrize={getPrizeLabel}
                  onSpinComplete={(prize) => {
                    setSurveyWonPrize(prize);
                    setTimeout(() => setSurveyPrizeRevealed(true), 500);
                    // Record prize win in DB
                    if (surveyInteractionId) {
                      supabase.from('interactions_survey_responses')
                        .update({
                          won_prize_id: prize.id,
                          won_prize_name: getPrizeLabel(prize)
                        })
                        .eq('id', surveyInteractionId)
                        .then(({ error }) => {
                          if (error) console.error('Error saving winner prize:', error);
                        });
                    }
                  }}
                />
              </Suspense>
            </div>
          ) : surveySubmitted && surveyPrizeRevealed && surveyWonPrize ? (
            <div className="flex-1 flex flex-col items-center justify-center px-6 pb-8 text-center">
              <div className="w-20 h-20 rounded-full bg-white/20 flex items-center justify-center mb-4">
                <Gift className="w-10 h-10 text-white" />
              </div>
              <p className="font-bold text-white text-lg mb-4">{getPrizeResultText(surveyWonPrize)}</p>
              <p className="text-white/90 text-base leading-relaxed max-w-xs">
                {t('survey.prizeWinMessage')}
              </p>
            </div>
          ) : surveySubmitted ? (
            <div className="flex-1 flex flex-col items-center justify-center px-6 pb-8">
              <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center mb-4">
                <Check className="w-8 h-8 text-white" />
              </div>
              <p className="font-medium text-white text-lg">{t('survey.thankYou')}</p>
            </div>
          ) : (
            <>
              <div className="flex-1 px-6 pb-4">
                <div
                  className="rounded-2xl p-5"
                  style={{ backgroundColor: 'rgba(255,255,255,0.95)' }}
                >
                  {/* Question steps */}
                  {currentQuestion ? (() => {
                    const question = currentQuestion;
                    const questionKey = `survey.question.${question.id}`;
                    const translatedPrompt = t(questionKey) !== questionKey ? t(questionKey) : question.prompt;

                    return (
                      <div className="space-y-4">
                        <Label className="font-medium text-neutral-900 text-base">
                          {translatedPrompt}
                          {question.required && <span className="text-red-500 ml-1">*</span>}
                        </Label>

                        {/* Several answers allowed (survey_questions.allow_multiple): checkboxes,
                            stored as string[]. Otherwise the radio group stores one string. */}
                        {question.question_type === 'multiple_choice' && question.options_json && question.allow_multiple && (
                          <div role="group" aria-label={translatedPrompt} className="space-y-1">
                            {(question.options_json as string[]).map((option, optionIndex) => {
                              const optionKey = `survey.option.${question.id}.${optionIndex}`;
                              const translatedOption = t(optionKey) !== optionKey ? t(optionKey) : option;
                              const current = surveyResponses[question.id];
                              const checked = Array.isArray(current) && current.includes(option);

                              return (
                                <div key={option} className="flex items-center space-x-2 py-1">
                                  <Checkbox
                                    id={`${question.id}-${option}`}
                                    checked={checked}
                                    onCheckedChange={(value) => setSurveyResponses(prev => ({
                                      ...prev,
                                      [question.id]: toggleSurveyOption(prev[question.id], option, value === true),
                                    }))}
                                  />
                                  <Label htmlFor={`${question.id}-${option}`} className="font-normal cursor-pointer text-neutral-700">
                                    {translatedOption}
                                  </Label>
                                </div>
                              );
                            })}
                            <p className="text-xs text-neutral-500">{t('survey.selectSeveral')}</p>
                          </div>
                        )}
                        {question.question_type === 'multiple_choice' && question.options_json && !question.allow_multiple && (
                          <RadioGroup
                            value={typeof surveyResponses[question.id] === 'string' ? (surveyResponses[question.id] as string) : ''}
                            onValueChange={(value) => setSurveyResponses(prev => ({ ...prev, [question.id]: value }))}
                          >
                            {(question.options_json as string[]).map((option, optionIndex) => {
                              const optionKey = `survey.option.${question.id}.${optionIndex}`;
                              const translatedOption = t(optionKey) !== optionKey ? t(optionKey) : option;

                              return (
                                <div key={option} className="flex items-center space-x-2 py-1">
                                  <RadioGroupItem value={option} id={`${question.id}-${option}`} />
                                  <Label htmlFor={`${question.id}-${option}`} className="font-normal cursor-pointer text-neutral-700">
                                    {translatedOption}
                                  </Label>
                                </div>
                              );
                            })}
                          </RadioGroup>
                        )}

                        {question.question_type === 'nps' && (
                          <div className="flex flex-wrap gap-2">
                            {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                              <button
                                key={num}
                                type="button"
                                onClick={() => setSurveyResponses(prev => ({ ...prev, [question.id]: num }))}
                                className={cn(
                                  'w-8 h-8 rounded-lg border-2 text-sm font-medium transition-colors',
                                  surveyResponses[question.id] === num
                                    ? 'bg-neutral-900 text-white border-neutral-900'
                                    : 'bg-white text-neutral-700 border-neutral-200 hover:border-neutral-400'
                                )}
                              >
                                {num}
                              </button>
                            ))}
                          </div>
                        )}

                        {question.question_type === 'text' && (
                          <Textarea
                            placeholder={t('survey.yourAnswer')}
                            value={surveyResponses[question.id] as string || ''}
                            onChange={(e) => setSurveyResponses(prev => ({ ...prev, [question.id]: e.target.value }))}
                            className="resize-none border-neutral-200"
                            rows={3}
                          />
                        )}
                      </div>
                    );
                  })() : (
                    /* Final step: Email + consent */
                    <div className="space-y-5">
                      <div className="space-y-2">
                        <Label htmlFor="survey-email" className="text-neutral-900 font-medium">
                          {t('common.email')} <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="survey-email"
                          type="email"
                          placeholder="your@email.com"
                          value={surveyEmail}
                          onChange={(e) => setSurveyEmail(e.target.value)}
                          className="border-neutral-200"
                        />
                      </div>

                      {/* Terms & Privacy consent */}
                      {(effectiveSettings?.privacy_policy_url || effectiveSettings?.terms_conditions_url) && (
                        <div className="flex items-start space-x-3">
                          <Checkbox
                            id="survey-terms"
                            checked={surveyTermsConsent}
                            onCheckedChange={(checked) => setSurveyTermsConsent(checked === true)}
                            className="mt-0.5"
                          />
                          <Label htmlFor="survey-terms" className="text-sm text-neutral-600 font-normal cursor-pointer leading-tight">
                            {t('consent.termsPrefix')} {clubName || 'the club'}'s{' '}
                            {effectiveSettings?.terms_conditions_url && (
                              <a
                                href={effectiveSettings.terms_conditions_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="underline text-neutral-800 hover:text-neutral-900"
                              >
                                {t('common.termsConditions')}
                              </a>
                            )}
                            {effectiveSettings?.terms_conditions_url && effectiveSettings?.privacy_policy_url && ` ${t('consent.and')} `}
                            {effectiveSettings?.privacy_policy_url && (
                              <a
                                href={effectiveSettings.privacy_policy_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="underline text-neutral-800 hover:text-neutral-900"
                              >
                                {t('common.privacyPolicy')}
                              </a>
                            )}
                            {' '}{t('consent.termsSuffix')}
                          </Label>
                        </div>
                      )}

                      {/* Marketing consent */}
                      <div className="flex items-start space-x-3">
                        <Checkbox
                          id="survey-marketing"
                          checked={surveyMarketingConsent}
                          onCheckedChange={(checked) => setSurveyMarketingConsent(checked === true)}
                        />
                        <Label htmlFor="survey-marketing" className="text-sm text-neutral-600 font-normal cursor-pointer leading-tight">
                          {t('consent.marketing', { clubName: clubName || 'the club' })}
                        </Label>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Next / Submit button */}
              <div className="px-6 pb-4">
                {currentQuestion ? (
                  <Button
                    onClick={() => setSurveyStep(s => s + 1)}
                    className={cn(
                      "w-full h-14 text-base font-semibold rounded-xl transition-all duration-200",
                      isCurrentStepAnswered
                        ? "shadow-lg scale-[1.02]"
                        : "opacity-50"
                    )}
                    style={{
                      backgroundColor: isCurrentStepAnswered ? 'white' : 'rgba(255,255,255,0.7)',
                      color: primaryColor
                    }}
                    disabled={!isCurrentStepAnswered}
                  >
                    {t('common.next')}
                    <ChevronRight className="w-5 h-5 ml-1" />
                  </Button>
                ) : (
                  <Button
                    onClick={handleSurveySubmit}
                    className={cn(
                      "w-full h-14 text-base font-semibold rounded-xl transition-all duration-200",
                      isSurveyEmailValid
                        ? "shadow-lg scale-[1.02]"
                        : "opacity-70"
                    )}
                    style={{
                      backgroundColor: isSurveyEmailValid ? 'white' : 'rgba(255,255,255,0.7)',
                      color: primaryColor
                    }}
                    disabled={submitting}
                  >
                    {submitting ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      t('survey.submitButton')
                    )}
                  </Button>
                )}
              </div>
            </>
          )}

          {renderSponsorFooter()}
        </div>
      </div>
    );
  };

  // LINEUPS VIEW
  // MATCH ANALYTICS VIEW (Timeline / Stats / Lineup / Standings)
  const renderMatchView = () => {
    if (!event?.id) return null;
    return (
      <div className="min-h-screen flex flex-col w-full" style={{ fontFamily }}>
        {renderHeader()}
        {renderBackButton()}

        <div className="flex-1 flex flex-col" style={{ backgroundColor: primaryColor }}>
          <div className="px-6 pt-6 pb-4 text-center">
            <h1 className="text-white text-xl font-bold uppercase tracking-wide mb-2" style={{ fontFamily }}>
              {t('match.title', { defaultValue: 'Match Analytics' })}
            </h1>
            <p className="text-white/80 text-sm">
              {t('match.description', { defaultValue: 'Live timeline, stats, lineups & standings' })}
            </p>
          </div>

          <div className="flex-1 px-4 pb-4">
            <Suspense fallback={<div className="text-center text-white/70 py-8 text-sm">{t('common.loading', { defaultValue: 'Loading…' })}</div>}>
              <MatchSection event={event} clubColor={primaryColor} t={t} />
            </Suspense>
          </div>

          {renderSponsorFooter()}
        </div>
      </div>
    );
  };

  const renderLineupsView = () => {
    // Placeholder squads (SportMonks pre-announcement: no formation, every
    // player marked GK) are not the real XI — treat them as "not available yet".
    const realLineups = effectiveLineups.filter(hasRealLineupData);
    const homeLineup = realLineups.find(l => l.team_type === 'home');
    const awayLineup = realLineups.find(l => l.team_type === 'away');

    return (
      <div className="min-h-screen flex flex-col w-full" style={{ fontFamily }}>
        {renderHeader()}
        {renderBackButton()}

        <div className="flex-1 flex flex-col" style={{ backgroundColor: primaryColor }}>
          <div className="px-6 pt-6 pb-4 text-center">
            <h1 className="text-white text-xl font-bold uppercase tracking-wide mb-2" style={{ fontFamily }}>{t('lineups.title')}</h1>
            <p className="text-white/80 text-sm">{t('lineups.description')}</p>
          </div>

          <div className="flex-1 px-4 pb-4">
            <div className="grid grid-cols-2 gap-3">
              {/* Home Team */}
              <div
                className="rounded-2xl p-4"
                style={{ backgroundColor: 'rgba(255,255,255,0.95)' }}
              >
                <div className="flex items-center gap-2 mb-3 pb-2 border-b border-neutral-200">
                  {homeLineup?.team_logo_url ? (
                    <img src={homeLineup.team_logo_url} alt="" className="w-8 h-8 object-contain" />
                  ) : event?.home_team_logo_url ? (
                    <img src={event.home_team_logo_url} alt="" className="w-8 h-8 object-contain" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-neutral-200 flex items-center justify-center">
                      <span className="text-neutral-600 text-sm font-bold">
                        {(homeLineup?.team_name || event?.home_team_name || 'H').charAt(0)}
                      </span>
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-neutral-900 text-sm truncate">
                      {homeLineup?.team_name || event?.home_team_name || 'Home'}
                    </h3>
                    {homeLineup?.formation && (
                      <p className="text-xs text-neutral-500">{homeLineup.formation}</p>
                    )}
                  </div>
                </div>
                <div className="space-y-1">
                  {(homeLineup?.players_json || []).map((player, idx) => (
                    <div key={player.player_id || idx} className="flex items-center gap-2 py-1">
                      <span className="w-6 h-6 rounded-full bg-neutral-100 flex items-center justify-center text-xs font-bold text-neutral-700">
                        {player.jersey_number}
                      </span>
                      <span className="text-sm text-neutral-800 truncate">{player.player_name}</span>
                    </div>
                  ))}
                  {(!homeLineup || homeLineup.players_json.length === 0) && (
                    <p className="text-sm text-neutral-500 italic">{t('lineups.notAvailable', { defaultValue: 'Not available yet' })}</p>
                  )}
                </div>
              </div>

              {/* Away Team */}
              <div
                className="rounded-2xl p-4"
                style={{ backgroundColor: 'rgba(255,255,255,0.95)' }}
              >
                <div className="flex items-center gap-2 mb-3 pb-2 border-b border-neutral-200">
                  {awayLineup?.team_logo_url ? (
                    <img src={awayLineup.team_logo_url} alt="" className="w-8 h-8 object-contain" />
                  ) : event?.away_team_logo_url ? (
                    <img src={event.away_team_logo_url} alt="" className="w-8 h-8 object-contain" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-neutral-200 flex items-center justify-center">
                      <span className="text-neutral-600 text-sm font-bold">
                        {(awayLineup?.team_name || event?.away_team_name || 'A').charAt(0)}
                      </span>
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-neutral-900 text-sm truncate">
                      {awayLineup?.team_name || event?.away_team_name || 'Away'}
                    </h3>
                    {awayLineup?.formation && (
                      <p className="text-xs text-neutral-500">{awayLineup.formation}</p>
                    )}
                  </div>
                </div>
                <div className="space-y-1">
                  {(awayLineup?.players_json || []).map((player, idx) => (
                    <div key={player.player_id || idx} className="flex items-center gap-2 py-1">
                      <span className="w-6 h-6 rounded-full bg-neutral-100 flex items-center justify-center text-xs font-bold text-neutral-700">
                        {player.jersey_number}
                      </span>
                      <span className="text-sm text-neutral-800 truncate">{player.player_name}</span>
                    </div>
                  ))}
                  {(!awayLineup || awayLineup.players_json.length === 0) && (
                    <p className="text-sm text-neutral-500 italic">{t('lineups.notAvailable', { defaultValue: 'Not available yet' })}</p>
                  )}
                </div>
              </div>
            </div>
          </div>
          {renderSponsorFooter()}
        </div>
      </div>
    );
  };

  // ORDERING VIEW
  const renderOrderingView = () => {
    // Shadows the hooked addToCart for the menu's own add buttons: same event.
    const addToCart = (itemId: string, selectedAddons: any[] = []) => {
      if (itemBlockedInSection(itemId)) return;
      posthog.capture('fan_item_added', fanEventProps({ item_id: itemId, cart_count: cartCount + 1, event_id: event?.id ?? null, club_id: finalClubId }, isDemoSeat));
      // Create a unique key for the item+addons combination
      const addonNames = [...selectedAddons].sort((a, b) => a.name.localeCompare(b.name)).map(a => a.name).join(',');
      const cartKey = addonNames ? `${itemId}:${addonNames}` : itemId;

      setCart(prev => {
        const newCart = new Map(prev);
        const existing = newCart.get(cartKey);
        if (existing) {
          newCart.set(cartKey, { ...existing, qty: existing.qty + 1 });
        } else {
          newCart.set(cartKey, { qty: 1, addons: selectedAddons });
        }
        return newCart;
      });
    };

    const removeFromCart = (cartKey: string) => {
      setCart(prev => {
        const newCart = new Map(prev);
        const existing = newCart.get(cartKey);
        if (!existing) return prev;

        if (existing.qty <= 1) newCart.delete(cartKey);
        else newCart.set(cartKey, { ...existing, qty: existing.qty - 1 });
        return newCart;
      });
    };

    const cartTotal = Array.from(cart.entries()).reduce((sum, [key, val]) => {
      const itemId = key.split(':')[0];
      const item = menuItems.find(i => i.id === itemId);
      const addonsTotal = (val.addons || []).reduce((s: number, a: any) => s + (a.price || 0), 0);
      return sum + (item ? (item.price + addonsTotal) * val.qty : 0);
    }, 0);

    const cartCount = Array.from(cart.values()).reduce((sum, val) => sum + val.qty, 0);

    const serviceFeeLocal = cartCount > 0 && effectiveSettings?.service_fee_feature_enabled && (effectiveSettings.service_fee_cents ?? 0) > 0
      ? (effectiveSettings.service_fee_cents ?? 0) / 100
      : 0;
    const cartGrandTotalLocal = cartTotal + serviceFeeLocal;

    // The cart's fulfilment summary for a pick-up order: one card saying when
    // (half-time pre-order, or pick-up now) and where. The choosers above keep
    // the choice; this card only restates it, so Pay stays the only white
    // control. Nothing when pick-up is blocked and no slot is held — the
    // closed note in the "When" block already explains why.
    const renderFulfilmentSummary = (cp: CollectionPoint) => {
      if (pickupBlocked && !pickupSlot) return null;
      // With pick-up now off, half-time is the only pick-up there is; treat it
      // as chosen even on the render before the auto-slot effect sets the slot.
      const showHalfTime = !!pickupSlot || (!pickupNowEnabled && preorderOfferable);
      const instructions = cp.instructions_text
        ? t(`pickup.instructions.${cp.id}`, { defaultValue: cp.instructions_text })
        : '';
      const row = (icon: React.ReactNode, title: React.ReactNode, description: React.ReactNode) => (
        <div className="grid grid-cols-[28px_1fr] gap-2.5 items-start">
          <div className="w-7 h-7 rounded-full bg-white/15 flex items-center justify-center">{icon}</div>
          <div>
            <p className="text-white font-semibold text-sm">{title}</p>
            {description}
          </div>
        </div>
      );
      return (
        <div className="rounded-2xl p-3 space-y-2.5 bg-black/25 border border-white/15">
          {showHalfTime ? (
            <>
              {row(
                <Clock className="w-3.5 h-3.5 text-white" />,
                <>
                  {t('preorder.halfTime')}
                  <span
                    className="ml-1.5 align-middle text-[10px] font-bold uppercase tracking-wide rounded-full px-2 py-0.5"
                    style={{ backgroundColor: palette.accent, color: palette.accentInk }}
                  >
                    {t('preorder.paidNowTag')}
                  </span>
                </>,
                <p className="text-white/70 text-xs">{t('preorder.summaryReady', { minute: String(preorderCutoffMinute) })}</p>,
              )}
              {row(
                <MapPin className="w-3.5 h-3.5 text-white" />,
                t('preorder.collectAtPoint', { point: preorderPointName }),
                instructions ? <p className="text-white/70 text-xs">{instructions}</p> : null,
              )}
            </>
          ) : (
            row(
              <MapPin className="w-3.5 h-3.5 text-white" />,
              t('ordering.pickupNowAt', { point: preorderPointName }),
              <>
                {instructions && <p className="text-white/70 text-xs">{instructions}</p>}
                <p className="text-white/70 text-xs">{t('ordering.pickupNowReadyNote')}</p>
              </>,
            )
          )}
          {cp.phone_number && (
            <a href={`tel:${cp.phone_number.replace(/\s/g, '')}`} className="text-white/70 text-xs pl-[38px] flex items-center gap-1 hover:text-white/90">
              <Phone className="w-3 h-3" /> {cp.phone_number}
            </a>
          )}
          {cp.instructions_image_url && (
            <div className="pl-[38px]">
              <img
                src={cp.instructions_image_url}
                alt={t(`pickup.name.${cp.id}`, { defaultValue: cp.name })}
                className="w-full rounded-lg mt-2 object-cover max-h-40"
              />
            </div>
          )}
        </div>
      );
    };

    // handleSubmitOrder is now replaced by handleInitiatePayment (defined above via useCallback)

    // The half-time pre-order's own screen wins over every now-order screen
    // while it's open (strip / auto-open on ready / payment success).
    if (showPreorderDetail && preorder.preorder) return renderPreorderDetail();

    if (checkingPendingOrder) {
      return (
        <div className="min-h-screen flex flex-col w-full" style={{ fontFamily }}>
          {renderHeader()}
          {renderBackButton()}
          <div className="flex-1 flex items-center justify-center" style={{ backgroundColor: primaryColor }}>
            <Loader2 className="w-8 h-8 animate-spin text-white" />
          </div>
        </div>
      );
    }

    // Refunded order screen
    if (pendingOrderId && pendingPaymentStatus === 'refunded') {
      return (
        <div className="min-h-screen flex flex-col w-full" style={{ fontFamily }}>
          {renderHeader()}
          {renderBackButton()}
          <div className="flex-1 flex flex-col" style={{ backgroundColor: primaryColor }}>
            <div className="flex-1 flex flex-col items-center justify-center px-8 py-12 text-center">
              <div className="w-20 h-20 rounded-full bg-white/20 flex items-center justify-center mb-6">
                <RotateCcw className="w-10 h-10 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-4">
                {t('payment.orderRefundedTitle', { defaultValue: 'Order Refunded' })}
              </h1>
              <p className="text-white/90 text-base leading-relaxed max-w-xs mb-6">
                {t('payment.orderRefundedMessage', { defaultValue: 'Your order has been refunded. The amount will be returned to your payment method within a few business days.' })}
              </p>
              <div className="flex items-center justify-between w-full max-w-xs px-4 py-3 rounded-lg mb-6" style={{ backgroundColor: 'rgba(255,255,255,0.15)' }}>
                <span className="text-white font-bold">{t('payment.refundAmount', { defaultValue: 'Refund Amount' })}</span>
                <span className="text-white font-bold">€{pendingOrderTotal.toFixed(2)}</span>
              </div>
              <button
                onClick={dismissPendingOrder}
                className="px-6 py-2 rounded-full text-sm font-medium text-white/80 hover:text-white transition-colors"
                style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}
              >
                {t('common.back')}
              </button>
            </div>
            {renderSponsorFooter()}
          </div>
        </div>
      );
    }

    // Cancelled order screen
    if (pendingOrderId && pendingOrderStatus === 'cancelled') {
      return (
        <div className="min-h-screen flex flex-col w-full" style={{ fontFamily }}>
          {renderHeader()}
          {renderBackButton()}
          <div className="flex-1 flex flex-col" style={{ backgroundColor: primaryColor }}>
            <div className="flex-1 flex flex-col items-center justify-center px-8 py-12 text-center">
              <div className="w-20 h-20 rounded-full bg-red-400/30 flex items-center justify-center mb-6">
                <XCircle className="w-10 h-10 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-4">
                {t('payment.orderCancelledTitle', { defaultValue: 'Order Cancelled' })}
              </h1>
              <p className="text-white/90 text-base leading-relaxed max-w-xs mb-6">
                {t('payment.orderCancelledMessage', { defaultValue: 'Your order has been cancelled. No payment was charged.' })}
              </p>
              <button
                onClick={dismissPendingOrder}
                className="px-6 py-2 rounded-full text-sm font-medium text-white/80 hover:text-white transition-colors"
                style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}
              >
                {t('ordering.orderAgain', { defaultValue: 'Order Again' })}
              </button>
            </div>
            {renderSponsorFooter()}
          </div>
        </div>
      );
    }

    // Pickup: show 'ready at collection point' screen as intermediate
    if (pendingOrderId && pendingOrderStatus === 'ready_at_collection_point') {
      // The point the ORDER was placed against (restored via get_order_status),
      // falling back to the section's — on a 'both' section they can differ.
      const orderCp = pendingCollectionPoint ?? collectionPoint;
      return (
        <div className="min-h-screen flex flex-col w-full" style={{ fontFamily }}>
          {renderHeader()}
          {renderBackButton()}
          <div className="flex-1 flex flex-col" style={{ backgroundColor: primaryColor }}>
            <div className="flex-1 flex flex-col items-center justify-center px-8 py-12 text-center">
              {renderCollectionPointReadyBody(orderCp)}
              {pendingPickupCode && renderOrderCodeBlock(pendingPickupCode)}
            </div>
            {renderSponsorFooter()}
          </div>
        </div>
      );
    }

    if (pendingOrderId && (pendingOrderStatus === 'delivered' || pendingOrderStatus === 'collected')) {
      const wasPickup = pendingOrderStatus === 'collected';
      return (
        <div className="min-h-screen flex flex-col w-full" style={{ fontFamily }}>
          {renderHeader()}
          {renderBackButton()}
          <div className="flex-1 flex flex-col" style={{ backgroundColor: primaryColor }}>
            <div className="flex-1 flex flex-col items-center justify-center px-8 py-12 text-center">
              {/* Green "done" badge with the demo's spring-in (client feedback:
                  match the artifact styling on the terminal screen too). */}
              <div className="w-20 h-20 rounded-full bg-emerald-500 shadow-lg flex items-center justify-center mb-6 animate-pop-in">
                <Check className="w-10 h-10 text-white" strokeWidth={3} />
              </div>
              <h1 className="text-2xl font-bold text-white mb-4">
                {wasPickup
                  ? t('ordering.orderCollected', { defaultValue: 'Order Collected!' })
                  : t('ordering.orderDelivered')}
              </h1>
              <p className="text-white/90 text-base leading-relaxed max-w-xs mb-6">
                {wasPickup
                  ? t('ordering.orderCollectedMessage', { defaultValue: 'Your order has been picked up. Enjoy!' })
                  : t('ordering.orderDeliveredMessage')}
              </p>
              {renderGiveawayEnteredBanner(true)}
              <div className="w-full max-w-xs space-y-2 mb-6">
                {pendingOrderItems.map((item, idx) => (
                  <OrderLineRow
                    key={idx}
                    name={getLocalizedMenuItemName(item)}
                    size={item.item_size ? getLocalizedMenuItemSize(item) : null}
                    addonsLabel={formatAddonsLabel(item.selected_addons)}
                    quantity={item.quantity}
                    lineTotal={item.item_price * item.quantity}
                  />
                ))}
              </div>
              {pendingServiceFee > 0.005 && (
                <div className="flex items-center justify-between w-full max-w-xs px-4 py-2 mb-2 text-white/80 text-sm">
                  <span>{t('ordering.serviceFee', { defaultValue: 'Service fee' })}</span>
                  <span className="tabular-nums">€{pendingServiceFee.toFixed(2)}</span>
                </div>
              )}
              <div className="flex items-center justify-between w-full max-w-xs px-4 py-3 rounded-lg" style={{ backgroundColor: 'rgba(255,255,255,0.15)' }}>
                <span className="text-white font-bold">{t('ordering.total')}</span>
                <span className="text-white font-bold">€{pendingOrderTotal.toFixed(2)}</span>
              </div>
              {pendingOrderId && pendingPaymentStatus === 'paid' && (
                <button
                  onClick={() => window.open(`/receipt/${pendingOrderId}`, '_blank')}
                  className="mt-4 px-6 py-2.5 rounded-full text-sm font-medium text-white flex items-center gap-2 transition-colors hover:bg-white/20"
                  style={{ backgroundColor: 'rgba(255,255,255,0.15)' }}
                >
                  <ExternalLink className="w-4 h-4" />
                  {t('receipt.viewReceipt', { defaultValue: 'View Receipt' })}
                </button>
              )}
              {/* Frosted Order Again pill, per the approved demo. */}
              <button
                onClick={dismissPendingOrder}
                className="mt-5 px-7 py-2.5 rounded-xl text-sm font-bold text-white border border-white/30 hover:bg-white/25 transition-colors"
                style={{ backgroundColor: 'rgba(255,255,255,0.14)' }}
              >
                {t('ordering.orderAgain', { defaultValue: 'Order Again' })}
              </button>
            </div>
            {renderSponsorFooter()}
          </div>
        </div>
      );
    }

    // Fan returned from a redirect-based payment (MobilePay / Klarna / iDEAL
    // / Bancontact) but the webhook hasn't flipped the order to 'paid' yet.
    // Show a "Processing payment…" UI with the items they ordered. The
    // real-time subscription on this order will catch the webhook UPDATE
    // (status → 'pending', payment_status → 'paid') and the next render
    // falls through to the "Order Received" branch below.
    if (pendingOrderId && pendingOrderStatus === 'pending_payment') {
      return (
        <div className="min-h-screen flex flex-col w-full" style={{ fontFamily }}>
          {renderHeader()}
          {renderBackButton()}
          <div className="flex-1 flex flex-col" style={{ backgroundColor: primaryColor }}>
            <div className="flex-1 flex flex-col items-center justify-center px-8 py-12 text-center">
              <div className="w-20 h-20 rounded-full bg-white/20 flex items-center justify-center mb-6">
                <Loader2 className="w-10 h-10 text-white animate-spin" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-4">
                {t('ordering.processingPayment', { defaultValue: 'Processing payment…' })}
              </h1>
              <p className="text-white/90 text-base leading-relaxed max-w-xs mb-6">
                {t('ordering.processingPaymentMessage', { defaultValue: 'We\'re confirming your payment with the provider. This usually takes a few seconds.' })}
              </p>
              {pendingOrderItems.length > 0 && (
                <div className="w-full max-w-xs space-y-2 mb-6">
                  {pendingOrderItems.map((item, idx) => (
                    <OrderLineRow
                      key={idx}
                      name={getLocalizedMenuItemName(item)}
                      size={item.item_size ? getLocalizedMenuItemSize(item) : null}
                      addonsLabel={formatAddonsLabel(item.selected_addons)}
                      quantity={item.quantity}
                      lineTotal={item.item_price * item.quantity}
                    />
                  ))}
                </div>
              )}
              {pendingOrderTotal > 0 && (
                <>
                  {pendingServiceFee > 0.005 && (
                    <div className="flex items-center justify-between w-full max-w-xs px-4 py-2 mb-2 text-white/80 text-sm">
                      <span>{t('ordering.serviceFee', { defaultValue: 'Service fee' })}</span>
                      <span className="tabular-nums">€{pendingServiceFee.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between w-full max-w-xs px-4 py-3 rounded-lg" style={{ backgroundColor: 'rgba(255,255,255,0.15)' }}>
                    <span className="text-white font-bold">{t('ordering.total')}</span>
                    <span className="text-white font-bold">€{pendingOrderTotal.toFixed(2)}</span>
                  </div>
                </>
              )}
              {/* Escape hatch for fans who came back without completing the
                  redirect payment (e.g. closed the MobilePay app, hit browser
                  back). Cancels the stuck order server-side and drops them
                  back into the cart with their items still selected. */}
              <button
                onClick={() => {
                  if (pendingOrderId && tag?.id) {
                    const idToAbandon = pendingOrderId;
                    const tagId = tag.id;
                    supabase.functions.invoke('abandon-order', {
                      body: { order_id: idToAbandon, tag_id: tagId },
                    }).catch((e) => console.warn('[abandon-order] from cancel-processing failed:', e));
                    localStorage.removeItem(`idseat_pending_order_${tagId}`);
                  }
                  setPendingOrderId(null);
                  setPendingOrderStatus(null);
                  setPendingPaymentStatus(null);
                  setPendingOrderItems([]);
                  setPendingOrderTotal(0);
                  setOrderSubmitted(false);
                  setPendingOrderingMode(null);
                  setPendingCollectionPoint(null);
                  setPendingGiveawayEntered(false);
                  setShowCart(true);
                }}
                className="mt-6 px-6 py-2.5 rounded-full text-sm font-medium text-white/90 hover:text-white border border-white/30 hover:border-white/60 transition-colors"
              >
                {t('ordering.cancelAndChange', { defaultValue: 'Cancel & change items' })}
              </button>
            </div>
            {renderSponsorFooter()}
          </div>
        </div>
      );
    }

    if (pendingOrderId && pendingOrderStatus && pendingOrderStatus !== 'pending_payment' && pendingOrderStatus !== 'delivered' && pendingOrderStatus !== 'collected' && pendingOrderStatus !== 'ready_at_collection_point') {
      // The placed ORDER's mode; falls back to the section mode collapsed with
      // no choice (covers a stale get_order_status without ordering_mode).
      const isPickupOrder = (pendingOrderingMode ?? resolveEffectiveOrderingMode(orderingMode, null)) === 'pickup';
      return (
        <div className="min-h-screen flex flex-col w-full" style={{ fontFamily }}>
          {renderHeader()}
          {renderBackButton()}
          <div className="flex-1 flex flex-col" style={{ backgroundColor: primaryColor }}>
            <div className="flex-1 flex flex-col items-center justify-center px-8 py-12 text-center">
              <OrderReceivedBadge />
              <h1 className="text-2xl font-bold text-white mb-4">{t('ordering.orderReceived')}</h1>
              <p className={`text-white/90 text-base leading-relaxed max-w-xs ${orderReceivedNotice ? 'mb-4' : 'mb-6'}`}>
                {isPickupOrder
                  ? t('ordering.orderReceivedPickupMessage', { defaultValue: 'Your order has been received. You will be notified when it is ready for pick-up.' })
                  : t('ordering.orderReceivedMessage')}
              </p>
              {renderOrderReceivedNotice()}
              {renderGiveawayEnteredBanner()}
              <div className="w-full max-w-xs space-y-2 mb-6">
                {pendingOrderItems.map((item, idx) => (
                  <OrderLineRow
                    key={idx}
                    name={getLocalizedMenuItemName(item)}
                    size={item.item_size ? getLocalizedMenuItemSize(item) : null}
                    addonsLabel={formatAddonsLabel(item.selected_addons)}
                    quantity={item.quantity}
                    lineTotal={item.item_price * item.quantity}
                  />
                ))}
              </div>
              {pendingServiceFee > 0.005 && (
                <div className="flex items-center justify-between w-full max-w-xs px-4 py-2 mb-2 text-white/80 text-sm">
                  <span>{t('ordering.serviceFee', { defaultValue: 'Service fee' })}</span>
                  <span className="tabular-nums">€{pendingServiceFee.toFixed(2)}</span>
                </div>
              )}
              <div className="flex items-center justify-between w-full max-w-xs px-4 py-3 rounded-lg" style={{ backgroundColor: 'rgba(255,255,255,0.15)' }}>
                <span className="text-white font-bold">{t('ordering.total')}</span>
                <span className="text-white font-bold">€{pendingOrderTotal.toFixed(2)}</span>
              </div>
              {isPickupOrder && pendingPickupCode && renderOrderCodeBlock(pendingPickupCode)}
              {pendingOrderId && pendingPaymentStatus === 'paid' && (
                <button
                  onClick={() => window.open(`/receipt/${pendingOrderId}`, '_blank')}
                  className="mt-4 px-5 py-2.5 rounded-full text-sm font-medium text-white flex items-center gap-2 transition-colors hover:bg-white/20"
                  style={{ backgroundColor: 'rgba(255,255,255,0.15)' }}
                >
                  <ExternalLink className="w-4 h-4" />
                  {t('receipt.viewReceipt', { defaultValue: 'View Receipt' })}
                </button>
              )}
            </div>
            {renderSponsorFooter()}
          </div>
        </div>
      );
    }

    if (orderSubmitted) {
      // The placed ORDER's mode; falls back to the section mode collapsed with
      // no choice (covers a stale get_order_status without ordering_mode).
      const isPickupOrder = (pendingOrderingMode ?? resolveEffectiveOrderingMode(orderingMode, null)) === 'pickup';
      return (
        <div className="min-h-screen flex flex-col w-full" style={{ fontFamily }}>
          {renderHeader()}
          {renderBackButton()}
          <div className="flex-1 flex flex-col" style={{ backgroundColor: primaryColor }}>
            <div className="flex-1 flex flex-col items-center justify-center px-8 py-12 text-center">
              <OrderReceivedBadge />
              <h1 className="text-2xl font-bold text-white mb-4">{t('ordering.orderReceived')}</h1>
              <p className={`text-white/90 text-base leading-relaxed max-w-xs ${orderReceivedNotice ? 'mb-4' : 'mb-6'}`}>
                {isPickupOrder
                  ? t('ordering.orderReceivedPickupMessage', { defaultValue: 'Your order has been received. You will be notified when it is ready for pick-up.' })
                  : t('ordering.orderReceivedMessage')}
              </p>
              {renderOrderReceivedNotice()}
              {renderGiveawayEnteredBanner()}
              {isPickupOrder && pendingPickupCode && renderOrderCodeBlock(pendingPickupCode)}
              {pendingOrderId && pendingPaymentStatus === 'paid' && (
                <button
                  onClick={() => window.open(`/receipt/${pendingOrderId}`, '_blank')}
                  className="mt-4 px-5 py-2.5 rounded-full text-sm font-medium text-white flex items-center gap-2 transition-colors hover:bg-white/20"
                  style={{ backgroundColor: 'rgba(255,255,255,0.15)' }}
                >
                  <ExternalLink className="w-4 h-4" />
                  {t('receipt.viewReceipt', { defaultValue: 'View Receipt' })}
                </button>
              )}

              <button
                onClick={dismissPendingOrder}
                className="mt-2 px-6 py-2 rounded-full text-sm font-medium text-white/60 hover:text-white transition-colors"
              >
                {t('common.back')}
              </button>
            </div>
            {renderSponsorFooter()}
          </div>
        </div>
      );
    }

    if (showCart) {
      return (
        <div className="min-h-screen flex flex-col w-full" style={{ fontFamily }}>
          {renderHeader()}
          <button onClick={() => setShowCart(false)} className="flex items-center gap-1 text-white/90 text-sm py-3 px-4 hover:text-white transition-colors" style={{ backgroundColor: 'rgba(0,0,0,0.3)' }}><ChevronLeft className="w-4 h-4" /> {t('ordering.backToMenu', { defaultValue: 'Back to Menu' })}</button>
          <div className="flex-1 flex flex-col" style={{ backgroundColor: primaryColor }}>
            <div className="px-6 pt-6 pb-4 text-center">
              <h1 className="text-white text-xl font-bold uppercase tracking-wide">{t('ordering.cart')}</h1>
            </div>
            <div className="flex-1 px-6 space-y-3">
              {Array.from(cart.entries()).map(([cartKey, val]) => {
                const itemId = cartKey.split(':')[0];
                const item = menuItems.find(i => i.id === itemId);
                if (!item) return null;
                const addonsPrice = (val.addons || []).reduce((s: number, a: any) => s + (a.price || 0), 0);
                return (
                  <div key={cartKey} className="flex flex-col p-3 rounded-xl" style={{ background: palette.cardBg, border: `1px solid ${palette.cardBorder}` }}>
                    <div className="flex items-start gap-3">
                      {item.image_url && <img src={item.image_url} alt={item.name} className="w-12 h-12 rounded-lg object-cover flex-shrink-0" />}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm leading-snug break-words line-clamp-2" style={{ color: palette.ink }}>{t(`menu.item.name.${item.id}`, { defaultValue: item.name })}</p>
                        {item.size && <p className="text-xs break-words" style={{ color: palette.inkSoft }}>{t(`menu.item.size.${item.id}`, { defaultValue: item.size })}</p>}
                        {/* One line per add-on: this is where fans verify their
                            customization, and a joined+truncated line hid every
                            add-on after the first. */}
                        {val.addons && val.addons.length > 0 && (
                          <ul className="mt-0.5 space-y-0.5">
                            {val.addons.map((a: any, i: number) => (
                              <li key={i} className="text-[11px] leading-snug break-words" style={{ color: palette.inkSoft }}>
                                + {t(`menu.addon.${a.name}`, { defaultValue: a.name })}
                                {a.price > 0 && <span className="tabular-nums"> (+€{a.price.toFixed(2)})</span>}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center justify-between gap-3 mt-2.5">
                      <span className="text-sm font-semibold tabular-nums" style={{ color: palette.ink }}>€{((item.price + addonsPrice) * val.qty).toFixed(2)}</span>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <button onClick={() => removeFromCart(cartKey)} className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: palette.accent, color: palette.accentInk }}><Minus className="w-4 h-4" /></button>
                        <span className="font-bold w-6 text-center tabular-nums" style={{ color: palette.ink }}>{val.qty}</span>
                        <button
                          onClick={() => {
                            // Ask, don't assume (client feedback): "+" used to
                            // silently clone this line's add-ons, and there was
                            // no way to add a plain one. When the item has
                            // add-ons, reopen the sheet prefilled with this
                            // line's picks — "same again" is two taps, and
                            // unchecking gives the plain item. The sheet's Add
                            // lands on whichever cart line matches the picks.
                            if (item.addons && item.addons.length > 0) {
                              setAddonsPrefill(val.addons || []);
                              setItemToAddAddons(item);
                            } else {
                              addToCart(itemId, val.addons);
                            }
                          }}
                          className="w-8 h-8 rounded-full flex items-center justify-center"
                          style={{ background: palette.accent, color: palette.accentInk }}
                        ><Plus className="w-4 h-4" /></button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="px-6 py-4 space-y-3 bg-black/20">

              {/* "Both" sections: the fan picks delivery vs pick-up here —
                  how first, then (pick-up only) when. The banners and phone
                  field below react to the choice live. The pickup option is
                  only offered when a collection point exists — a
                  destinationless pickup would be a broken promise. Seat
                  delivery is always "now", so choosing it drops any half-time
                  slot; a pick-up-only item in the cart disables it. */}
              {canFanChoose(orderingMode) && collectionPoint && (
                <div className="space-y-1.5">
                  <Label className="text-white/70 text-xs font-medium pl-1">
                    {t('ordering.chooseFulfillment', { defaultValue: 'How do you want to receive your order?' })}
                  </Label>
                  <div className="grid grid-cols-2 gap-1 p-1 rounded-xl bg-white/10 border border-white/20">
                    <button
                      onClick={cartNeedsPickup ? undefined : () => { setFanFulfillmentChoice('delivery'); setPickupSlot(null); }}
                      disabled={cartNeedsPickup}
                      aria-disabled={cartNeedsPickup}
                      className={`flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-sm font-semibold transition-colors${cartNeedsPickup ? ' opacity-40 cursor-not-allowed' : ''}`}
                      style={fanFulfillmentChoice === 'delivery'
                        ? { backgroundColor: palette.accent, color: palette.accentInk }
                        : { color: 'rgba(255,255,255,0.8)' }}
                    >
                      <Truck className="w-4 h-4" />
                      {t('ordering.deliveryOption', { defaultValue: 'Seat delivery' })}
                    </button>
                    <button
                      onClick={pickupBlocked ? undefined : () => setFanFulfillmentChoice('pickup')}
                      disabled={pickupBlocked}
                      aria-disabled={pickupBlocked}
                      className={`flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-sm font-semibold transition-colors${pickupBlocked ? ' opacity-40 cursor-not-allowed' : ''}`}
                      style={fanFulfillmentChoice === 'pickup'
                        ? { backgroundColor: palette.accent, color: palette.accentInk }
                        : { color: 'rgba(255,255,255,0.8)' }}
                    >
                      <MapPin className="w-4 h-4" />
                      {t('ordering.pickupOption', { defaultValue: 'Pick-up' })}
                    </button>
                  </div>
                  {cartNeedsPickup && (
                    <p className="text-sm text-white font-medium leading-snug pl-1">
                      {t('ordering.pickupOnlyNote', {
                        // Quoted and joined here: the copy reads
                        // `…to your seat: "Beer", "Nachos". … or remove "Beer", "Nachos" …`.
                        items: menuItems
                          .filter((i) => cartItemIds.includes(i.id) && isPickupOnly(i))
                          .map((i) => `"${t(`menu.item.name.${i.id}`, { defaultValue: i.name })}"`)
                          .join(', '),
                        point: preorderPointName || collectionPoint?.name || '',
                      })}
                    </p>
                  )}
                  {!pickupNowEnabled && !preorderOfferable && !effectiveIsPickup && (
                    <p className="text-white/85 text-xs pl-1">{pickupHalfTimeOnlyNote}</p>
                  )}
                </div>
              )}

              {/* Half-time pre-order: now vs half-time. Pick-up only — shown
                  once the order is a pick-up (pick-up section, or Pick-up
                  chosen above); seat delivery is always now. With pick-up now
                  off and the window open there is nothing to choose — the
                  block stays out (no empty spacer) and the summary card below
                  carries when and where. */}
              {pickupWhenShown && (pickupNowEnabled || !preorderOfferable) && (
                <div className="space-y-1.5">
                  {/* The question only makes sense when there is a choice; with
                      pick-up now off the summary card below says it all. */}
                  {pickupNowEnabled && (
                    <Label className="text-white/70 text-xs font-medium pl-1">{t('preorder.whenTitle')}</Label>
                  )}
                  {!pickupNowEnabled ? (
                    // Pick-up now off and no pre-order on offer: say why.
                    <div className="flex items-center gap-2 rounded-xl bg-black/25 px-3 py-2.5 text-xs font-medium text-white/80">
                      <Lock className="w-3.5 h-3.5 flex-shrink-0" />
                      {pickupHalfTimeOnlyNote}
                    </div>
                  ) : preorderOfferable ? (
                    <>
                      <div className="grid grid-cols-2 gap-1 p-1 rounded-xl bg-white/10 border border-white/20">
                        <button
                          onClick={() => setPickupSlot(null)}
                          className="flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-sm font-semibold transition-colors"
                          style={!pickupSlot ? { backgroundColor: palette.accent, color: palette.accentInk } : { color: 'rgba(255,255,255,0.8)' }}
                        >
                          {t('preorder.now')}
                        </button>
                        <button
                          onClick={() => { setPickupSlot(PREORDER_SLOT); setFanFulfillmentChoice('pickup'); }}
                          className="flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-sm font-semibold transition-colors"
                          style={pickupSlot ? { backgroundColor: palette.accent, color: palette.accentInk } : { color: 'rgba(255,255,255,0.8)' }}
                        >
                          <Clock className="w-4 h-4" />
                          {t('preorder.halfTime')}
                        </button>
                      </div>
                    </>
                  ) : (
                    <div className="flex items-center gap-2 rounded-xl bg-black/25 px-3 py-2.5 text-xs font-medium text-white/80">
                      <Lock className="w-3.5 h-3.5 flex-shrink-0" />
                      {t('preorder.closed')}
                    </div>
                  )}
                </div>
              )}

              {/* Fulfilment summary: when + where for a pick-up order */}
              {effectiveOrderingMode === 'pickup' && collectionPoint && renderFulfilmentSummary(collectionPoint)}

              {/* Delivery Mode Banner */}
              {effectiveOrderingMode === 'delivery' && (
                <div className="flex items-center gap-2 text-white/60 text-xs pl-1">
                  <Truck className="w-3.5 h-3.5" />
                  {t('ordering.deliveryMode', { defaultValue: 'Delivery to your seat' })}
                </div>
              )}

              {/* Phone number for pickup */}
              {effectiveOrderingMode === 'pickup' && (
                <div className="space-y-1.5">
                  <Label className="text-white/70 text-xs font-medium pl-1">
                    {t('ordering.phoneNumber', { defaultValue: 'Phone number (optional)' })}
                  </Label>
                  <Input
                    value={fanPhone}
                    onChange={(e) => setFanPhone(e.target.value)}
                    placeholder="+358 40 123 4567"
                    type="tel"
                    className="bg-white/10 border-white/20 text-white placeholder:text-white/30"
                  />
                </div>
              )}

              {/* Club-controlled: some kitchens don't take free-text
                  instructions. Absent column (pre-migration) means enabled —
                  the historical behaviour. */}
              {(effectiveSettings?.order_comments_enabled ?? true) && (
                <div className="space-y-1.5">
                  <Label className="text-white/70 text-xs font-medium pl-1">{t('ordering.specialInstructions')}</Label>
                  <Textarea
                    value={fanComments}
                    onChange={(e) => setFanComments(e.target.value)}
                    placeholder={t('ordering.commentsPlaceholder')}
                    className="bg-white/10 border-white/20 text-white placeholder:text-white/30 min-h-[80px]"
                  />
                </div>
              )}

              {/* Order giveaway — only when the club enabled one and named a
                  prize (resolveGiveawayPrize gates both and translates). The
                  email is optional; validation happens on submit. */}
              {(() => {
                const giveawayPrize = resolveGiveawayPrize(effectiveSettings as any, t);
                if (!giveawayPrize) return null;
                return (
                  <div className="p-3 rounded-xl border border-white/20 bg-white/5 space-y-2">
                    <p className="text-white/90 text-xs leading-relaxed">
                      🎁 {t('ordering.giveawayPitch', { prize: giveawayPrize })}
                    </p>
                    <Input
                      type="email"
                      value={giveawayEmail}
                      onChange={(e) => setGiveawayEmail(e.target.value)}
                      placeholder={t('ordering.giveawayEmailPlaceholder', { defaultValue: 'your@email.com' })}
                      className="bg-white/10 border-white/20 text-white placeholder:text-white/30"
                    />
                    {/* Always visible; it becomes a link only when the club
                        has a policy URL configured. */}
                    {(effectiveSettings?.privacy_policy_url || effectiveSettings?.terms_conditions_url) ? (
                      <a
                        href={effectiveSettings?.privacy_policy_url || effectiveSettings?.terms_conditions_url || '#'}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block text-white/40 text-[10px] underline underline-offset-2"
                      >
                        {t('ordering.giveawayPrivacy', { defaultValue: "Optional. By entering you agree to the club's Privacy Policy and Terms and Conditions." })}
                      </a>
                    ) : (
                      <p className="text-white/40 text-[10px]">
                        {t('ordering.giveawayPrivacy', { defaultValue: "Optional. By entering you agree to the club's Privacy Policy and Terms and Conditions." })}
                      </p>
                    )}
                  </div>
                );
              })()}
              {serviceFeeLocal > 0 && (
                <div className="space-y-1.5 text-white/80 text-sm">
                  <div className="flex items-center justify-between">
                    <span>{t('ordering.subtotal', { defaultValue: 'Subtotal' })}</span>
                    <span>€{cartTotal.toFixed(2)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>{t('ordering.serviceFee', { defaultValue: 'Service fee' })}</span>
                    <span>€{serviceFeeLocal.toFixed(2)}</span>
                  </div>
                </div>
              )}
              <div className="flex items-center justify-between text-white text-lg font-bold">
                <span>{t('ordering.total')}</span>
                <span>€{cartGrandTotalLocal.toFixed(2)}</span>
              </div>
              {/* Payment buttons based on club payment mode.
                  If online isn't ready (Stripe inactive OR business info missing),
                  we silently downgrade: online_only/fan_choice -> in_person_only.
                  Fans still get to order; the club admin sees the warning in their portal. */}
              {(() => {
                // Pick-up now off and no half-time slot: there is no pick-up
                // to pay for (window closed, a pre-order already held, a
                // pick-up-only item pinning pick-up, or the render before the
                // auto-slot effect runs) — no pay buttons. The note already
                // sits in the "When" block when that renders; only repeat it
                // here when it doesn't.
                if (effectiveIsPickup && !pickupSlot && !pickupNowEnabled) {
                  return pickupWhenShown ? null : (
                    <div className="flex items-center gap-2 rounded-xl bg-black/25 px-3 py-2.5 text-sm font-medium text-white/85">
                      <Lock className="w-4 h-4 flex-shrink-0" />
                      {pickupHalfTimeOnlyNote}
                    </div>
                  );
                }
                const effectivePaymentMode: PaymentMode =
                  onlinePaymentReady === false && (paymentMode === 'online_only' || paymentMode === 'fan_choice')
                    ? 'in_person_only'
                    : paymentMode;
                // Half-time pre-order: online only — one pay button, and the
                // in-person button must never render for it.
                if (pickupSlot) {
                  return <>
              {orderingPaused && (
                <div className="mb-2 flex items-center gap-2 rounded-xl bg-black/25 backdrop-blur-sm px-3 py-2.5 text-sm font-semibold text-white">
                  <Lock className="w-4 h-4 flex-shrink-0" />
                  {t('ordering.paused')}
                </div>
              )}
              <Button
                onClick={() => handleInitiatePayment('online')}
                className="w-full h-14 text-base font-semibold rounded-xl flex items-center justify-center gap-2"
                style={{ backgroundColor: 'white', color: primaryColor }}
                disabled={submitting || cartCount === 0 || orderingPaused}
              >
                {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : (
                  <><Clock className="w-4 h-4" /> {t('preorder.payButton')} — €{cartGrandTotalLocal.toFixed(2)}</>
                )}
              </Button>
                  </>;
                }
                return <>
              {/* Ordering-paused banner — pause can flip on live (realtime
                  club_settings merge) while a fan already has the cart open. */}
              {orderingPaused && (
                <div className="mb-2 flex items-center gap-2 rounded-xl bg-black/25 backdrop-blur-sm px-3 py-2.5 text-sm font-semibold text-white">
                  <Lock className="w-4 h-4 flex-shrink-0" />
                  {t('ordering.paused')}
                </div>
              )}
              {effectivePaymentMode === 'online_only' && (
                <Button
                  onClick={() => handleInitiatePayment('online')}
                  className="w-full h-14 text-base font-semibold rounded-xl flex items-center justify-center gap-2"
                  style={{ backgroundColor: 'white', color: primaryColor }}
                  disabled={submitting || cartCount === 0 || orderingPaused}
                >
                  {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : (
                    <><Lock className="w-4 h-4" /> {t('payment.payNow', { defaultValue: 'Pay Now' })} — €{cartGrandTotalLocal.toFixed(2)}</>
                  )}
                </Button>
              )}
              {effectivePaymentMode === 'in_person_only' && (
                <Button
                  onClick={() => handleInitiatePayment('in_person')}
                  className="w-full h-14 text-base font-semibold rounded-xl"
                  style={{ backgroundColor: 'white', color: primaryColor }}
                  disabled={submitting || cartCount === 0 || orderingPaused}
                >
                  {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : t('ordering.confirmOrder')}
                </Button>
              )}
              {effectivePaymentMode === 'fan_choice' && (
                <div className="space-y-2">
                  <Button
                    onClick={() => handleInitiatePayment('online')}
                    className="w-full h-14 text-base font-semibold rounded-xl flex items-center justify-center gap-2"
                    style={{ backgroundColor: 'white', color: primaryColor }}
                    disabled={submitting || cartCount === 0 || orderingPaused}
                  >
                    {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : (
                      <><Lock className="w-4 h-4" /> {t('payment.payNow', { defaultValue: 'Pay Now' })} — €{cartGrandTotalLocal.toFixed(2)}</>
                    )}
                  </Button>
                  <Button
                    onClick={() => handleInitiatePayment('in_person')}
                    className="w-full h-12 text-sm font-medium rounded-xl border border-white/30"
                    variant="ghost"
                    style={{ color: 'white' }}
                    disabled={submitting || cartCount === 0 || orderingPaused}
                  >
                    {t('payment.payInPerson', { defaultValue: 'Pay In Person' })}
                  </Button>
                </div>
              )}
              </>;
              })()}

              {/* Payment Sheet Modal */}
              {showPaymentSheet && paymentClientSecret && (
                <PaymentSheet
                  locale={locale}
                  clientSecret={paymentClientSecret}
                  subtotal={cartTotal}
                  serviceFee={serviceFeeLocal}
                  total={cartGrandTotalLocal}
                  primaryColor={primaryColor}
                  stripeAccount={paymentStripeAccount}
                  returnUrl={paymentOrderId
                    ? buildStripeReturnUrl(window.location.href, { orderId: paymentOrderId, preorder: !!paymentPickupSlot })
                    : undefined}
                  onSuccess={handlePaymentSuccess}
                  onError={(msg) => {
                    // Stripe.confirmPayment errored. The order is still pending_payment
                    // server-side; abandon it so it doesn't reappear as a phantom on reload.
                    // Critically, restore the cart UI (orderSubmitted=false, showCart=true)
                    // so the fan can re-tap Pay without having to re-add every item — the
                    // cart Map state is preserved across this failure.
                    // Never for an order this client already saw paid: that would abandon
                    // a captured payment and drop the fan's pointer to it.
                    if (paymentOrderId && tag?.id && nowOrderConfirmedPaidRef.current?.id !== paymentOrderId) {
                      const orderToAbandon = paymentOrderId;
                      const tagId = tag.id;
                      supabase.functions.invoke('abandon-order', {
                        body: { order_id: orderToAbandon, tag_id: tagId },
                      }).catch((e) => console.warn('[abandon-order] failed:', e));
                      localStorage.removeItem(paymentPickupSlot ? preorderStorageKey(tagId) : `idseat_pending_order_${tagId}`);
                    }
                    // paymentPickupSlot is NOT cleared here: the sheet stays open
                    // (paymentOrderId too) and a retry that succeeds must still
                    // route to the pre-order path. onClose / success clear it.
                    setOrderSubmitted(false);
                    setShowCart(true);
                    toast.error(msg);
                  }}
                  onClose={() => {
                    // Fan closed without paying. Mark the order cancelled so it doesn't
                    // resurface as a stuck "your order is pending" view on the next load.
                    // Same as onError: keep their cart visible + populated so retrying
                    // is one tap, not a full re-selection. Never for an order this
                    // client already saw paid (the sheet can stay open if the
                    // post-payment bookkeeping threw).
                    if (paymentOrderId && tag?.id && nowOrderConfirmedPaidRef.current?.id !== paymentOrderId) {
                      const orderToAbandon = paymentOrderId;
                      const tagId = tag.id;
                      supabase.functions.invoke('abandon-order', {
                        body: { order_id: orderToAbandon, tag_id: tagId },
                      }).catch((e) => console.warn('[abandon-order] failed:', e));
                      localStorage.removeItem(paymentPickupSlot ? preorderStorageKey(tagId) : `idseat_pending_order_${tagId}`);
                    }
                    setShowPaymentSheet(false);
                    setPaymentClientSecret(null);
                    setPaymentStripeAccount(null);
                    setPaymentOrderId(null);
                    setPaymentPickupSlot(null);
                    setOrderSubmitted(false);
                    setShowCart(true);
                    setSubmitting(false);
                  }}
                  t={t}
                />
              )}
            </div>
            {renderSponsorFooter()}
          </div>
          {/* The cart's "+" reopens the add-ons chooser, so the sheet must
              exist on this screen too — not only on the menu. */}
          {renderAddonsSheet()}
        </div>
      );
    }

    return (
      <div className="min-h-screen flex flex-col w-full" style={{ fontFamily }}>
        {renderHeader()}
        {renderBackButton()}
        <div className="flex-1 flex flex-col" style={{ backgroundColor: primaryColor }}>
          <div className="px-6 pt-6 pb-4 text-center">
            {/* Ink comes from the brand colour: white is unreadable on a light
                brand (Aris F.C.'s yellow measures 1.93:1). */}
            <h1 className="text-xl font-bold uppercase tracking-wide" style={{ color: palette.pageInk }}>
              {t(menuTitleKey(orderingMode))}
            </h1>
            <p className="text-sm mt-1.5" style={{ color: palette.pageInkSoft }}>
              {t('ordering.tapToAdd', { defaultValue: 'Tap an item to add it to your order' })}
            </p>
            {/* Advertises the half-time offer; with items already in the cart
                it opens the cart with half-time preselected. */}
            {preorderOfferable && !pickupSlot && (
              <button
                onClick={() => { setPickupSlot(PREORDER_SLOT); setFanFulfillmentChoice('pickup'); if (cartCount > 0) setShowCart(true); }}
                className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold shadow-sm"
                style={{ backgroundColor: palette.accent, color: palette.accentInk }}
              >
                <Clock className="w-3.5 h-3.5" />
                {t('preorder.chip', { point: preorderPointName })}
              </button>
            )}
          </div>
          {/* Category jump bar. Sticks to the very top of the viewport — the
              page header scrolls away, so any offset here would strand the
              chips mid-screen with list items scrolling through the gap. The
              solid club-color fill + under-shadow make it read as a toolbar
              (not loose pills) while content passes beneath it. */}
          {menuCategories.length > 0 && (
            <div
              className="sticky top-0 z-30 px-4 pt-2.5 pb-2.5"
              style={{ backgroundColor: primaryColor, boxShadow: '0 10px 14px -10px rgba(0,0,0,0.45)' }}
            >
              <div className="flex gap-2 overflow-x-auto scrollbar-hide no-scrollbar">
                {menuCategories.map(cat => (
                  <button
                    key={cat.id}
                    onClick={() => document.getElementById(`category-${cat.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                    className="whitespace-nowrap px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider shadow-sm transition-all"
                    style={{ backgroundColor: palette.accent, color: palette.accentInk }}
                  >
                    {t(`menu.category.${cat.id}`, { defaultValue: cat.name })}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="flex-1 px-4 pb-20 space-y-6 pt-2">
            {menuItems.length > 0 ? (
              <>
                {/* Items without a category (or when no categories exist) */}
                {(() => {
                  const categorizedItemIds = new Set<string>();
                  menuCategories.forEach(cat => {
                    menuItems.forEach(i => {
                      if (itemInCategory(i, cat)) categorizedItemIds.add(i.id);
                    });
                  });
                  const uncategorized = menuItems.filter(i => !categorizedItemIds.has(i.id));
                  if (uncategorized.length === 0) return null;
                  return (
                    <div className="space-y-3 scroll-mt-16">
                      {menuCategories.length > 0 && (
                        <h3 className="text-xs font-bold uppercase tracking-wider px-2" style={{ color: palette.pageInkSoft }}>{t('ordering.menu', { defaultValue: 'Menu' })}</h3>
                      )}
                      <div className="grid grid-cols-1 gap-3">
                        {uncategorized.map((item, idx) => (
                          // Staggered entrance; fill-mode backwards keeps late
                          // cards invisible until their delay elapses. The
                          // wrapper only replays on mount, never on qty edits.
                          <div
                            key={item.id}
                            className="animate-fade-in-up"
                            style={{ animationDelay: `${Math.min(idx * 45, 360)}ms`, animationFillMode: 'backwards' }}
                          >
                            <MenuItemRow
                              item={item}
                              qty={getItemCartCount(item.id)}
                              t={t}
                              palette={palette}
                              onAdd={(i) => i.addons && i.addons.length > 0 ? setItemToAddAddons(i) : addToCart(i.id)}
                              onRemove={(i) => { const k = getFirstCartKeyForItem(i.id); if (k) removeFromCart(k); }}
                              onInfo={setItemInfo}
                              badge={orderingMode === 'both' && !pickupBlocked && isPickupOnly(item) ? t('ordering.pickupOnlyBadge') : undefined}
                              unavailableLabel={pickupBlocked && isPickupOnly(item) ? t('ordering.notAvailableInSection') : undefined}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}
                {menuCategories.map(cat => {
                  const itemsInCategory = menuItems.filter(i => itemInCategory(i, cat));
                  if (itemsInCategory.length === 0) return null;
                  return (
                    // scroll-mt clears the top-0 sticky category bar (~56px).
                    <div key={cat.id} id={`category-${cat.id}`} className="space-y-3 scroll-mt-16">
                      <h3 className="text-xs font-bold uppercase tracking-wider px-2" style={{ color: palette.pageInkSoft }}>
                        {t(`menu.category.${cat.id}`, { defaultValue: cat.name })}
                      </h3>
                      <div className="grid grid-cols-1 gap-3">
                        {itemsInCategory.map((item, idx) => (
                          <div
                            key={item.id}
                            className="animate-fade-in-up"
                            style={{ animationDelay: `${Math.min(idx * 45, 360)}ms`, animationFillMode: 'backwards' }}
                          >
                            <MenuItemRow
                              item={item}
                              qty={getItemCartCount(item.id)}
                              t={t}
                              palette={palette}
                              onAdd={(i) => i.addons && i.addons.length > 0 ? setItemToAddAddons(i) : addToCart(i.id)}
                              onRemove={(i) => { const k = getFirstCartKeyForItem(i.id); if (k) removeFromCart(k); }}
                              onInfo={setItemInfo}
                              badge={orderingMode === 'both' && !pickupBlocked && isPickupOnly(item) ? t('ordering.pickupOnlyBadge') : undefined}
                              unavailableLabel={pickupBlocked && isPickupOnly(item) ? t('ordering.notAvailableInSection') : undefined}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </>
            ) : (
              <div
                className="flex flex-col items-center justify-center p-8 mt-4 text-center rounded-2xl"
                style={{ background: palette.cardBg, border: `1px solid ${palette.cardBorder}` }}
              >
                <ShoppingBag className="w-12 h-12 mb-4" style={{ color: palette.tileInk }} />
                <p className="font-medium" style={{ color: palette.ink }}>{t('ordering.emptyMenu')}</p>
              </div>
            )}
          </div>
          {cartCount > 0 && (
            <div className="fixed bottom-0 left-0 right-0 px-4 pb-4 pt-2 z-50">
              <button
                onClick={() => setShowCart(true)}
                className="w-full max-w-md mx-auto flex items-center justify-between px-5 py-4 rounded-2xl shadow-lg font-semibold"
                style={{ backgroundColor: palette.accent, color: palette.accentInk }}
              >
                <div className="flex items-center gap-2">
                  <ShoppingCart className="w-5 h-5" />
                  {/* Menu page bar shows just the items subtotal — the service fee
                      is only surfaced inside the Cart with its own breakdown line. */}
                  <span>{cartCount} {t('ordering.items')} · €{cartTotal.toFixed(2)}</span>
                </div>
                <span className="flex items-center gap-1 font-bold">
                  {t('ordering.viewCart', { defaultValue: 'View cart' })}
                  <ChevronRight className="w-5 h-5" />
                </span>
              </button>
            </div>
          )}
        </div>

        {renderAddonsSheet()}

        {/* Dietary/allergen info sheet, opened from the ⓘ on a menu card. Same
            surface as the add-ons sheet; the resolution rules for the
            ingredients text live in src/lib/dietary.ts. */}
        {itemInfo && (() => {
          const infoTags = knownDietaryTags(itemInfo.dietary_tags);
          const infoAllergens = knownAllergens(itemInfo.allergens);
          const infoIngredients = resolveItemIngredients(itemInfo.ingredients, itemInfo.id, t);
          return (
            <div
              className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm animate-scrim-in"
              onClick={() => setItemInfo(null)}
            >
              <div
                className="w-full max-w-md bg-white rounded-t-3xl sm:rounded-2xl overflow-hidden shadow-2xl animate-sheet-up"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-6">
                  <div className="flex justify-between items-start">
                    <div className="min-w-0 flex-1 pr-2">
                      <h3 className="text-xl font-bold text-neutral-900 leading-snug break-words">{t(`menu.item.name.${itemInfo.id}`, { defaultValue: itemInfo.name })}</h3>
                      {itemInfo.size && (
                        <p className="text-sm text-neutral-500">{t(`menu.item.size.${itemInfo.id}`, { defaultValue: itemInfo.size })}</p>
                      )}
                    </div>
                    <button onClick={() => setItemInfo(null)} className="p-2 text-neutral-400 flex-shrink-0 -mr-2 -mt-1"><X className="w-6 h-6" /></button>
                  </div>

                  <div className="max-h-[55vh] overflow-y-auto mt-4 mb-6 space-y-4">
                    {infoTags.length > 0 && (
                      <div className="flex flex-wrap gap-1.5">
                        {infoTags.map(tag => {
                          const ink = legibleOn(DIETARY_TAG_COLORS[tag], '#ffffff');
                          return (
                            <span
                              key={tag}
                              className="text-[10px] font-bold uppercase tracking-wide rounded-full px-2.5 py-1"
                              style={{ color: ink, background: `${ink}14`, boxShadow: `inset 0 0 0 1px ${ink}55` }}
                            >
                              {t(dietaryTagKey(tag), { defaultValue: DIETARY_TAG_LABELS[tag] })}
                            </span>
                          );
                        })}
                      </div>
                    )}
                    {infoAllergens.length > 0 && (
                      <div>
                        <p className="text-[11px] font-bold uppercase tracking-wider text-neutral-400 mb-1.5">
                          {t('ordering.contains', { defaultValue: 'Contains' })}
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                          {infoAllergens.map(allergen => (
                            <span
                              key={allergen}
                              className="text-xs font-semibold text-amber-800 bg-amber-50 ring-1 ring-inset ring-amber-200 rounded-full px-2.5 py-1"
                            >
                              {t(allergenKey(allergen), { defaultValue: ALLERGEN_LABELS[allergen] })}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {infoIngredients && (
                      <div>
                        <p className="text-[11px] font-bold uppercase tracking-wider text-neutral-400 mb-1.5">
                          {t('ordering.ingredients', { defaultValue: 'Ingredients' })}
                        </p>
                        <p className="text-sm text-neutral-700 leading-relaxed break-words">{infoIngredients}</p>
                      </div>
                    )}
                  </div>

                  <Button
                    className="w-full h-14 text-base font-bold rounded-xl"
                    style={{ backgroundColor: primaryColor }}
                    // Info stays open for a pick-up-only item in a section
                    // without pick-up, but it can't be added from here either.
                    disabled={itemBlockedInSection(itemInfo.id)}
                    onClick={() => {
                      const target = itemInfo;
                      setItemInfo(null);
                      if (target.addons && target.addons.length > 0) {
                        setItemToAddAddons(target);
                      } else {
                        addToCart(target.id);
                        toast.success(t('ordering.addedToCart'));
                      }
                    }}
                  >
                    {itemBlockedInSection(itemInfo.id)
                      ? t('ordering.notAvailableInSection')
                      : <>{t('ordering.addItem')} - €{itemInfo.price.toFixed(2)}</>}
                  </Button>
                </div>
              </div>
            </div>
          );
        })()}
        {renderSponsorFooter()}
      </div>
    );
  };

  // Render active view
  const renderActiveView = () => {
    if (isLoading) {
      return <FanPageSkeleton primaryColor={settings?.primary_color} />;
    }

    switch (activeView) {
      case 'predictor':
        return renderPredictorView();
      case 'survey':
        return renderSurveyView();
      case 'lineups':
        return renderLineupsView();
      case 'match':
        return renderMatchView();
      case 'ordering':
        return renderOrderingView();
      default:
        return renderHomeView();
    }
  };

  // On mobile: render fullscreen without phone frame
  // On desktop: wrap in phone frame mockup for preview
  return (
    <>
      {customFontStyle && <style dangerouslySetInnerHTML={{ __html: customFontStyle }} />}

      {isMobile ? (
        // Mobile: fullscreen layout. The DOCUMENT is the scroller — no
        // overflow/overscroll here: this div's height is auto (min-h-screen),
        // so `overflow-y-auto` made it a scroll container with zero scrollable
        // pixels and `overscroll-contain` then blocked scroll chaining to the
        // document — Chrome Android swallowed every one-finger drag (fans had
        // to scroll with two fingers, JäPS vs Haka feedback, Aug 2026).
        <div className="min-h-screen">
          {renderActiveView()}
        </div>
      ) : (
        // Desktop: phone frame mockup
        <div className="min-h-screen bg-neutral-900 flex items-center justify-center p-4 md:p-8">
          <div
            className="w-full max-w-[375px] mx-auto rounded-[2rem] overflow-hidden shadow-2xl border-[6px] border-neutral-700 bg-neutral-800"
            style={{ height: '85vh', minHeight: '667px', maxHeight: '844px' }}
          >
            <div className="h-full overflow-y-auto overscroll-contain">
              {renderActiveView()}
            </div>
          </div>
        </div>
      )}

      {/* Language Settings Modal */}
      <Suspense fallback={null}>
        <LanguageSettings
          isOpen={showLanguageSettings}
          onClose={() => setShowLanguageSettings(false)}
          languages={languages}
          currentLocale={locale}
          onLanguageChange={changeLanguage}
          primaryColor={primaryColor}
          t={t}
        />
      </Suspense>

      <Suspense fallback={null}>
        <ReportSheet
          isOpen={showReportSheet}
          onClose={() => setShowReportSheet(false)}
          primaryColor={primaryColor}
          fontFamily={fontFamily}
          t={t}
          context={reportContext}
          onSent={(category) => posthog.capture('fan_report_sent', fanEventProps({ club_id: finalClubId, category, view: activeView }, isDemoSeat))}
        />
      </Suspense>

      {/* Live polls — one sheet per open poll; only openPollId is visible. */}
      {openPolls.length > 0 && (
        <Suspense fallback={null}>
          {openPolls.map((p) => (
            <PollSheet
              key={p.id}
              isOpen={openPollId === p.id}
              onClose={closePoll}
              poll={p}
              clubId={finalClubId}
              stadiumId={tagVenue?.id ?? null}
              eventId={event?.id ?? null}
              seatId={tag?.seat_id ?? null}
              tagId={tag?.id ?? null}
              deviceId={getDeviceId()}
              onJoinRequired={screenJoin.expire}
              initialResults={pollResults}
              primaryColor={primaryColor}
              sponsorLogos={sponsorLogos}
              sponsorLogoSize={sponsorLogoSize}
              t={t}
            />
          ))}
        </Suspense>
      )}
    </>
  );
}

// Action Button Component
interface ActionButtonProps {
  icon: React.ReactNode;
  label: string;
  onClick: React.MouseEventHandler<HTMLButtonElement> | (() => void);
  isActive?: boolean;
  className?: string;
}

function ActionButton({ icon, label, onClick, isActive, className }: ActionButtonProps) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "w-full bg-white rounded-2xl pl-4 pr-5 py-3.5 flex items-center gap-3.5 transition-all text-left",
        "shadow-[0_2px_0_rgba(0,0,0,0.06),0_12px_26px_-10px_rgba(0,0,0,0.25)] hover:shadow-[0_3px_0_rgba(0,0,0,0.08),0_16px_30px_-10px_rgba(0,0,0,0.3)] hover:-translate-y-0.5 active:scale-[0.98]",
        isActive && "ring-2 ring-black/10",
        className
      )}
    >
      {/* Icon chip — tinted with a soft wash of the club's primary color so
          the white pill picks up the brand palette instead of reading neutral. */}
      <span
        className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
        style={{
          backgroundColor: 'color-mix(in srgb, var(--club-color, #0b0d13) 12%, transparent)',
          color: 'var(--club-icon, var(--club-color, #0b0d13))',
        }}
      >
        {icon}
      </span>
      <span
        className="flex-1 text-left text-neutral-900 uppercase text-[15px] font-extrabold leading-tight tracking-[0.05em]"
      >
        {label}
      </span>
      <ChevronRight className="w-5 h-5 text-neutral-400" />
    </button>
  );
}
